/* eslint-disable @typescript-eslint/no-unused-vars */
import {downloadRowsAsExcel, excelFileToNamedSheets} from 'common/HelperFunctions'
import CustomSkeleton, {CARD_PRESET} from 'common/components/CustomSkeleton'
import FilterSelect from 'common/components/FilterSelect'
import DataUploadReport from 'common/components/dataUpload/DataUploadReport'
import {dispatchApi, isChunkedUploadAvailable, withUploadedFile} from 'common/helpers/chunkedUpload'
import {
  type DataUploadConversion,
  buildDataUploadConvertForm,
  dataUploadAcceptAttribute,
  dataUploadCountsSummary,
  dataUploadErrorMessage,
  dataUploadFileExtension,
  dataUploadTemplateToFile,
  firstDataUploadFileError,
  formatDataUploadBytes,
  normalizeDataUploadConversion,
  validateDataUploadFile,
} from 'common/helpers/dataUpload'
import {FREEMIUM_TEACHER_LIMIT} from 'common/helpers/freemiumPlan'
import {trackOnboardingEvent} from 'common/helpers/onboardingEventTracking'
import {
  isDataUploadConversionAvailable,
  useDataUploadConfig,
} from 'common/hooks/useDataUploadConfig'
import {useFreemiumTeacherLimit} from 'common/hooks/useFreemiumTeacherLimit'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CircleX,
  Download,
  Info,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react'
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import Select from 'react-select'
import {toast} from 'react-toastify'
import {RootState} from 'setup'
import {convertDataUpload, deleteDataUploadDraft} from 'setup/redux/Actions/DataUpload/dataUpload'

import {toAbsoluteUrl} from '_metronic/helpers'

import {downloadLessonPlanningTemplate} from '../../../../../../setup/redux/Actions/Onboarding/downloadLessonPlanningTemplate'
import {downloadStudentsTemplate} from '../../../../../../setup/redux/Actions/Onboarding/downloadStudentsTemplate'
import {
  cachedPrefillFrequency,
  loadCurriculumDefaults,
  readStoredCurriculumPrefills,
} from '../curriculumPrefill'
import {useOnboardingPrefillDraft} from '../hooks/useOnboardingPrefillDraft'
import {useTeacherAllocationAi} from '../hooks/useTeacherAllocationAi'
import type {OnboardingPartialSubmit, UpdateDataOptions, WizardData} from '../index'
import {readStoredSchoolSystemDetails} from '../schoolSystemDetailsCache'
import {parsePositiveNumber} from '../utils/allocationIds'
import {
  buildCurriculumPrefixIndex,
  buildDisplayNamePairs,
  displayCurriculumName,
  stripCurriculumPrefixesInText,
} from '../utils/curriculumDisplay'
import {buildSharedNameLabeler, lookupSubjectId} from '../utils/levelCurriculum'
import {
  STUDENTS_TEMPLATE_LISTS_SHEET,
  buildStudentTemplateListsIndex,
  pickStudentClass,
  resolveCurriculumColumnClass,
} from '../utils/studentClassMatch'
import {
  STUDENTS_CURRICULUM_COLUMN_ENABLED,
  STUDENTS_TEMPLATE_SHEET,
  STUDENT_ROW_TRANSIENT_FIELDS,
  STUDENT_SUBJECT_DELIMITER,
  type StudentCsvRow,
  type StudentMissingField,
  parseStudentsRows,
  studentClassChoices,
  studentRowKey,
} from '../utils/studentsFile'
import {TeacherEntryRow, parseCsvRows} from '../utils/teacherEntry'
import {
  downloadTeacherSampleTemplate,
  downloadTeacherSampleTemplateCsv,
} from '../utils/teacherSampleTemplate'
import GenericTable, {CheckboxOption, MultiValueContainer} from './GenericTable'
import HorizontalStepper from './HorizontalStepper'
import LessonPlanEditSheet from './LessonPlanEditSheet'
import OnboardingExcelImportModal from './OnboardingExcelImportModal'
import Step4TeacherEntry from './Step4TeacherEntry'
import Step4TeacherReview from './Step4TeacherReview'
import TagRemoveIcon from './TagRemoveIcon'
import TeacherAllocationAiPanel from './TeacherAllocationAiPanel'

interface Props {
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: UpdateDataOptions) => void
  onComplete: () => void
  /** Called when the user presses Back on the first tab (exit to the previous wizard step). */
  onBack?: () => void
  onPartialSubmit?: OnboardingPartialSubmit
  isDark: boolean
  isGenerating?: boolean
  /** True while the wizard is still hydrating from the server on resume. Drives skeletons on the
   *  data-heavy classes/review screens so the user doesn't see a flash of empty state. */
  isHydrating?: boolean
  enabledTabs?: Array<'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'>
  initialTab?: 'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'
  /** Which screen of the Teachers tab to open: the Add Teachers entry screen or Review Teachers. */
  initialTeachersPhase?: 'entry' | 'review'
  stepperOverride?: {
    steps: Array<{
      id: string
      label: string
      topTabId?: 'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'
      classesFlowStep?: number
    }>
    activeStepId: string
    onStepClick?: (stepId: string) => void
  }
  /** Reports the active sub-step id as the user navigates (for URL syncing). With a
   *  stepperOverride, reports the matched stepper id (classes/sections/review/subjects);
   *  otherwise the active tab id (teachers/students/lessonPlanning). */
  onActiveSubStepChange?: (subStepId: string) => void
  /** Called only when Add Teachers' Review button opens the Review Teachers screen. */
  onTeacherReview?: () => void
  /** Called when Review Teachers' Confirm and Continue is pressed. */
  onTeacherReviewConfirm?: () => void
  /** Called after the Lesson Planning Excel import lands server-side, so the wizard can
   *  re-fetch onboarding data and show the imported entities in the grids. */
  onExcelImported?: () => void
}

type Step4TabId = 'teachers' | 'classes' | 'subjects' | 'lessonPlanning' | 'students'
type Step4TabItem = {id: Step4TabId; label: string}

const subSteps = [
  {id: 'teachers', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.TEACHERS'},
  {id: 'classes', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.CLASSES'},
  {id: 'subjects', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.SUBJECTS'},
  {id: 'lessonPlanning', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.LESSON_PLANNING'},
]

const studentSubStep = [{id: 'students', labelKey: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.STUDENTS'}]

interface PrefilledClassEntry {
  name: string
  subjects: string[]
}

interface ClassesReviewRow {
  id: string
  curriculum: string
  level: string
  gradeName: string
  sections: string[]
  subjects: string[]
}

interface SubjectLevelRow {
  curriculum: string
  level: string
  key: string
}

const uniqueStrings = (values: string[]) =>
  Array.from(new Set(values.map((v) => v.trim()).filter((v) => v.length > 0)))

/** Joins class name and curriculum in a student Class option shared by two curricula. */
const CLASS_CURRICULUM_SEP = '::curriculum::'

/**
 * Tolerant class-name key so "Class 1A", "class 1-a", "Class 1 - A" and "1-A" all
 * refer to the same class: lowercase, alphanumerics only, and a leading "class"/"grade"
 * word is ignored (falling back to the unstripped key when nothing else remains).
 * Uploaded student files rarely spell the class exactly like the wizard does, and every
 * subjects-dropdown check matches on the class string — an unmatched spelling made each
 * subject click a silent no-op.
 */
const normalizeClassMatchKey = (value: any): string => {
  const raw = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
  const stripped = raw.replace(/^(class|grade)/, '')
  return stripped || raw
}

const splitSubjectsOutsideParens = (raw: string) => {
  const value = (raw || '').trim()
  if (!value) return []

  const output: string[] = []
  let token = ''
  let depth = 0

  for (const ch of value) {
    if (ch === '(') depth += 1
    if (ch === ')' && depth > 0) depth -= 1

    if (ch === ',' && depth === 0) {
      const clean = token.trim()
      if (clean) output.push(clean)
      token = ''
      continue
    }

    token += ch
  }

  const tail = token.trim()
  if (tail) output.push(tail)

  return output
}

interface TeacherOption {
  name: string
  employeeId?: string
}

interface TeacherSearchSelectProps {
  teachers: TeacherOption[]
  value: string
  selectedEmployeeId?: string
  onChange: (val: string) => void
  onSelectOption?: (opt: TeacherOption) => void
  hasError?: boolean
  isDark?: boolean
}

type PendingDeleteAction =
  | {type: 'category-row'; category: 'teachers' | 'students'; index: number}
  /** Students step: take back the rows the Teachers file prefilled. */
  | {type: 'students-prefill'}
  /** Review Teachers: one row or a bulk "Delete Selected" — original indices into manualData.teachers. */
  | {type: 'teacher-rows'; indices: number[]}
  | {type: 'classes-review-row'; branchName: string; rowId: string; rowIdx: number}

/** A student that exists in the school: a Students save succeeded with it, or it has an id. */
const isSavedStudent = (row: any) => row?._saved === true || Boolean(parsePositiveNumber(row?.id))

/**
 * Adds the saved rows among `removed` to `deletedStudents`, sent with `action:'delete'` on
 * the next Students save (the backend never reads a missing student as removed). Unsaved rows
 * need nothing: they just leave the grid.
 */
const queueStudentDeletes = <T extends Record<string, any>>(data: T, removed: any[]): T => {
  const saved = removed.filter(isSavedStudent)
  if (saved.length === 0) return data
  const existing = ((data as any).deletedStudents as any[]) ?? []
  const seen = new Set(existing.map(studentRowKey))
  const merged = [...existing]
  saved.forEach((row) => {
    const key = studentRowKey(row)
    if (seen.has(key)) return
    seen.add(key)
    merged.push({
      registrationNumber: String(row.registrationNumber ?? '').trim(),
      full_name: String(row.full_name || `${row.first_name || ''} ${row.last_name || ''}`).trim(),
      ...(parsePositiveNumber(row.id) ? {id: parsePositiveNumber(row.id)} : {}),
      class: row.class,
      branch: row.branch,
      level: row.level,
      curriculum: row.curriculum,
    })
  })
  return {...data, deletedStudents: merged}
}

const TeacherSearchSelect: FC<TeacherSearchSelectProps> = ({
  teachers,
  value,
  selectedEmployeeId,
  onChange,
  onSelectOption,
  hasError,
  isDark,
}) => {
  const intl = useIntl()
  const [inputValue, setInputValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIdx, setHighlightedIdx] = useState(-1)
  const [dropdownPos, setDropdownPos] = useState({top: 0, left: 0, width: 180})
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const filledSlotRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)
  // Whether a teacher is committed to this slot — based on the persisted `value`,
  // NOT the in-progress `inputValue`, so typing a search term doesn't flip the UI
  // into the "filled slot" display and swap the input out from under the caret.
  const hasTeacherValue = (value || '').trim().length > 0
  // Show the editable input when there's no committed teacher, or while the
  // dropdown is open (i.e. the user is actively searching/editing).
  const showInput = !hasTeacherValue || isOpen
  const DROPDOWN_MAX_HEIGHT = 180
  const DROPDOWN_GAP = 4

  const filteredTeachers = useMemo(() => {
    if (!inputValue) return teachers
    return teachers.filter((t) => t.name.toLowerCase().includes(inputValue.toLowerCase()))
  }, [teachers, inputValue])

  const selectedTeacher = useMemo(() => {
    const v = (value || '').trim()
    if (!v) return null
    const eid = (selectedEmployeeId || '').toString().trim()
    if (eid) {
      return (
        teachers.find((t) => t.name === v && (t.employeeId || '').toString().trim() === eid) ?? null
      )
    }
    // Fallback: name-only match (may be ambiguous)
    return teachers.find((t) => t.name === v) ?? null
  }, [teachers, value, selectedEmployeeId])

  /** Show ID from matched teacher or persisted slot value so name + ID stay visible together. */
  const displayEmployeeId = (
    selectedTeacher?.employeeId ||
    (selectedEmployeeId || '').toString().trim() ||
    ''
  ).trim()

  useEffect(() => {
    setInputValue(value)
  }, [value])

  // When the dropdown opens on a previously-filled slot, the display div is
  // replaced by the input — move focus into it so the user can type immediately.
  useEffect(() => {
    if (isOpen && showInput) {
      inputRef.current?.focus()
    }
  }, [isOpen, showInput])

  const openDropdown = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    // Only flip in the bottom 30% of the viewport
    const flipThresholdY = window.innerHeight * 0.7
    const estimatedHeight = Math.min(
      DROPDOWN_MAX_HEIGHT,
      Math.max(filteredTeachers.length * 34 + 8, 44)
    )
    const spaceAbove = rect.top - 8
    const spaceBelow = window.innerHeight - rect.bottom - 8
    const preferAbove = rect.top > flipThresholdY
    const openAbove =
      preferAbove &&
      (spaceBelow < estimatedHeight || (spaceAbove >= estimatedHeight && spaceAbove > spaceBelow))

    setDropdownPos({
      top: openAbove
        ? Math.max(8, rect.top - DROPDOWN_GAP - estimatedHeight)
        : Math.min(window.innerHeight - 8, rect.bottom + DROPDOWN_GAP),
      left: rect.left,
      width: Math.max(rect.width, 180),
    })
    // When editing an already-selected teacher, open the list unfiltered
    // (otherwise the inputValue equals the selected name and we only see that one option).
    // Only clear on the opening transition — not on every keystroke while already
    // open, which would wipe the search term the user is typing.
    if (!isOpen && hasTeacherValue) {
      setInputValue('')
      setHighlightedIdx(-1)
    }
    setIsOpen(true)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
      setIsOpen(false)
      setHighlightedIdx(-1)
      setInputValue(value)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value])

  const handleSelect = (opt: TeacherOption) => {
    onChange(opt.name)
    onSelectOption?.(opt)
    setInputValue(opt.name)
    setIsOpen(false)
    setHighlightedIdx(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      openDropdown()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIdx((prev) => Math.min(prev + 1, filteredTeachers.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIdx((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIdx >= 0 && highlightedIdx < filteredTeachers.length) {
        handleSelect(filteredTeachers[highlightedIdx])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setHighlightedIdx(-1)
      setInputValue(value)
    }
  }

  return (
    <div ref={containerRef} style={{width: '100%', position: 'relative'}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: hasError ? '#FFC6CC' : isDark ? '#5BA6FE' : '#EFF6FF',
          border: `1px solid ${
            hasError ? '#F64E60' : isDark ? 'rgba(255,255,255,0.4)' : 'rgba(5, 110, 233, 0.35)'
          }`,
          borderRadius: 6,
          padding: '3px 8px',
          gap: 4,
        }}
      >
        {hasError && (
          <AlertTriangle size={12} style={{color: '#F64E60', flexShrink: 0}} strokeWidth={2.2} />
        )}
        {!showInput ? (
          <div
            ref={filledSlotRef}
            role='button'
            tabIndex={0}
            onClick={openDropdown}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              minWidth: 0,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: hasError ? '#F64E60' : '#0263D4',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              title={value}
            >
              {value}
            </span>
            {displayEmployeeId ? (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: hasError ? '#F64E60' : '#0263D4',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                title={displayEmployeeId}
              >
                ({displayEmployeeId})
              </span>
            ) : null}
          </div>
        ) : (
          <input
            ref={inputRef}
            type='text'
            className='teacher-search-input'
            data-has-error={hasError ? 'true' : undefined}
            value={inputValue}
            placeholder={intl.formatMessage({
              id: hasError
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.SELECT_ERROR'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.NAME_PLACEHOLDER',
            })}
            onChange={(e) => {
              setInputValue(e.target.value)
              openDropdown()
              setHighlightedIdx(-1)
            }}
            onFocus={openDropdown}
            onClick={openDropdown}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: hasError ? '#F64E60' : isDark ? '#ffffff' : '#0263D4',
              fontSize: '11px',
              fontWeight: 400,
              minWidth: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          />
        )}
        {/* Always show the search icon — including when a teacher is assigned.
            The assigned name box is itself clickable to reopen the search, so a
            separate edit (pencil) icon is not shown. */}
        <Search
          size={12}
          style={{
            color: hasError
              ? '#F64E60'
              : isDark
                ? 'rgba(255,255,255,0.7)'
                : 'rgba(2, 99, 212, 0.75)',
            flexShrink: 0,
          }}
        />
      </div>
      {isOpen &&
        createPortal(
          <ul
            ref={dropdownRef}
            className='step4-teacher-search-dropdown'
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              listStyle: 'none',
              margin: 0,
              padding: '4px',
              background: isDark ? '#80BBFF' : '#F0F7FF',
              border: isDark ? 'none' : '1px solid #BFDBFE',
              borderRadius: 8,
              boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.35)' : '0 8px 24px rgba(0,0,0,0.18)',
              maxHeight: 180,
              overflowY: 'auto',
              zIndex: 99999,
            }}
          >
            {filteredTeachers.length === 0 && (
              <li
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#0263D4',
                  textAlign: 'center',
                  cursor: 'default',
                }}
              >
                No teacher found
              </li>
            )}
            {filteredTeachers.map((t, idx) => (
              <li
                key={`${t.name}|||${t.employeeId || ''}|||${idx}`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(t)
                }}
                onMouseEnter={() => setHighlightedIdx(idx)}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: highlightedIdx === idx ? 600 : 400,
                  background:
                    highlightedIdx === idx ? (isDark ? '#000066' : '#056EE9') : 'transparent',
                  color: highlightedIdx === idx ? '#ffffff' : '#0263D4',
                  borderRadius: 5,
                  transition: 'background 0.12s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>{t.name}</span>
                {t.employeeId && (
                  <span style={{fontSize: '11px', opacity: 0.9}}>{t.employeeId}</span>
                )}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  )
}

const Step4Manual: FC<Props> = ({
  data,
  updateData,
  onComplete,
  onBack,
  onPartialSubmit,
  isDark,
  isGenerating = false,
  isHydrating = false,
  enabledTabs,
  initialTab,
  initialTeachersPhase,
  stepperOverride,
  onActiveSubStepChange,
  onTeacherReview,
  onTeacherReviewConfirm,
  onExcelImported,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const urddId = useSelector<RootState>((state) => state.userReducer.urddId) as string | null

  // --- Lesson Planning allocation-method analytics -----------------------------
  // How did this tenant allocate teachers on the Lesson Planning tab: by hand,
  // via the AI assistant, or via Excel import — plus "opened the AI assistant
  // but never applied anything". Standalone `teacher_allocation` event, one
  // substep per method (NOT metadata on onboarding_substep_final — that family
  // is deduped once per tenant/substep server-side, so repeats carrying the
  // method would never be stored). Each kind fires at most once per mount.
  const trackedAllocationEventsRef = useRef<Set<string>>(new Set())
  // Whether the AI panel's current open session applied at least one allocation.
  // Starts FALSE: the panel opens by itself on arrival at lesson planning (defaultOpen),
  // and that auto-open never fires onOpenStateChange(true) — the panel only reports
  // transitions — so closing that first session untouched must still count as
  // "opened the assistant but never used it". User-initiated opens also reset this
  // to false (see onOpenStateChange below); applying an allocation sets it true.
  const aiUsedSinceOpenRef = useRef(false)
  const trackAllocationEvent = useCallback(
    (
      allocationEvent:
        | 'manual_assignment'
        | 'ai_assignment'
        | 'excel_assignment'
        | 'ai_opened_not_used'
    ) => {
      if (trackedAllocationEventsRef.current.has(allocationEvent)) return
      trackedAllocationEventsRef.current.add(allocationEvent)
      trackOnboardingEvent(dispatch, urddId, 'teacher_allocation', allocationEvent)
    },
    [dispatch, urddId]
  )

  // Defer parent updates to avoid "setState during render" warnings.
  const deferUpdateData = useCallback(
    (next: Partial<WizardData>, options?: UpdateDataOptions) => {
      Promise.resolve().then(() => updateData(next, options))
    },
    [updateData]
  )

  const tabs = useMemo<Step4TabItem[]>(() => {
    const base: Step4TabItem[] = [
      ...subSteps,
      ...(data.mode === 'subject' ? studentSubStep : []),
    ].map((s) => ({
      id: s.id as Step4TabId,
      label: intl.formatMessage({id: s.labelKey}),
    }))
    if (!enabledTabs || enabledTabs.length === 0) return base
    const byId = new Map<Step4TabId, Step4TabItem>(base.map((t) => [t.id, t]))
    // Keep the exact order coming from `enabledTabs`.
    return enabledTabs.map((id) => byId.get(id)).filter((t): t is Step4TabItem => Boolean(t))
  }, [enabledTabs, intl, data.mode])

  const resolvedInitialTab = (() => {
    if (initialTab && tabs.some((t) => t.id === initialTab)) return initialTab
    const persisted = data.manualData.manualActiveTab
    if (persisted && tabs.some((t) => t.id === persisted)) return persisted
    return tabs[0]?.id || 'teachers'
  })()
  const [activeTab, setActiveTab] = useState<string>(resolvedInitialTab)
  const [localData, setLocalData] = useState(data.manualData)

  // The Teachers tab has two internal screens: "entry" (type/paste names or upload files)
  // and "review" (the editable table). Review is not a visible sub-step — it's where the
  // entry screen's output lands, and where duplicate names / low-confidence rows get fixed.
  // A tenant resuming with teachers already saved starts on review; an empty list starts
  // on entry.
  const [teachersPhase, setTeachersPhase] = useState<'entry' | 'review'>(() => {
    // /assign-subjects/review-teachers opens Review directly — even before the server's
    // teachers have hydrated (the review screen shows a skeleton meanwhile); starting on
    // "entry" instead would report the wrong sub-step, rewrite the URL to /teachers, and
    // strand a resumed user on the wrong screen. /assign-subjects/teachers opens the entry
    // screen even when teachers exist. No URL hint: review when there is something to review.
    if (initialTeachersPhase === 'review') return 'review'
    if (initialTeachersPhase === 'entry') return 'entry'
    return ((data.manualData.teachers as any[]) || []).length > 0 ? 'review' : 'entry'
  })
  // Set once the user moves between the two screens themselves; the hydration fallback
  // below must never override a deliberate navigation.
  const teachersPhaseNavigatedRef = useRef(false)
  const goToTeachersPhase = (phase: 'entry' | 'review') => {
    teachersPhaseNavigatedRef.current = true
    setTeachersPhase(phase)
  }
  // Resume fallback: a /review-teachers URL whose hydration brings back no teachers has
  // nothing to review, so fall back to the entry screen once hydration settles.
  useEffect(() => {
    if (isHydrating || teachersPhaseNavigatedRef.current) return
    if (teachersPhase === 'review' && ((data.manualData.teachers as any[]) || []).length === 0) {
      setTeachersPhase('entry')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrating, data.manualData.teachers])

  /** Append the entry screen's rows to the list and move to the review table. */
  const handleTeacherEntryReview = (rows: TeacherEntryRow[]) => {
    let addedCount = 0
    setLocalData((prev) => {
      const existing = (prev.teachers as any[]) || []
      const nextTeachers = [...existing, ...rows]
      addedCount = nextTeachers.length - existing.length
      const next = {...prev, teachers: nextTeachers, teachersCount: nextTeachers.length}
      // Rows added on Add Teachers: an `assign_teachers` edit, even though the wizard is
      // already switching to the Review screen by the time this deferred write lands.
      deferUpdateData({manualData: next}, {subStep: 'teachers'})
      return next
    })
    goToTeachersPhase('review')
    onTeacherReview?.()
    if (rows.length > 0) {
      // Defer so the toast reports the post-dedupe count computed inside the updater.
      Promise.resolve().then(() => {
        toast.success(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.ADDED_TOAST'},
            {count: Math.max(0, addedCount)}
          )
        )
      })
    }
  }
  const onboardingTeacherCount = useMemo(
    () => ((localData.teachers as any[]) ?? []).length,
    [localData.teachers]
  )
  // report:false — the cap still blocks here, but reaching it during onboarding
  // must not raise the CreditsWall signal behind lifecycle email E14b. See the
  // hook's options docblock.
  const freemiumTeacherLimit = useFreemiumTeacherLimit(onboardingTeacherCount, {report: false})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [subjectInputByLevel, setSubjectInputByLevel] = useState<Record<string, string>>({})
  const [subjectErrorByLevel, setSubjectErrorByLevel] = useState<Record<string, string | null>>({})
  const [editingSubjectByLevel, setEditingSubjectByLevel] = useState<
    Record<string, {original: string; value: string} | null>
  >({})
  const skipSubjectInputBlurRef = useRef(false)
  const skipEditBlurCommitRef = useRef(false)
  const [lessonPlanAttempted, setLessonPlanAttempted] = useState(false)
  const [errorClearedSlots, setErrorClearedSlots] = useState<Set<string>>(new Set())
  const [showLessonNextHint, setShowLessonNextHint] = useState(false)

  useEffect(() => {
    setLocalData((prev) => {
      const prevSerialized = JSON.stringify(prev ?? {})
      const nextSerialized = JSON.stringify(data.manualData ?? {})
      if (prevSerialized === nextSerialized) return prev
      return data.manualData
    })
  }, [data.manualData])
  const manualDataRef = useRef(data.manualData)

  const matchParentTeacher = useCallback((teacher: any, parentTeachers: any[]) => {
    const empId = String(teacher?.employeeId ?? '').trim()
    if (empId) {
      const byEmp = parentTeachers.find((p: any) => String(p?.employeeId ?? '').trim() === empId)
      if (byEmp) return byEmp
    }
    const name =
      teacher?._nameInput !== undefined
        ? String(teacher._nameInput || '').trim()
        : `${teacher?.first_name || ''} ${teacher?.last_name || ''}`.trim()
    if (!name) return undefined
    return parentTeachers.find((p: any) => {
      const parentName =
        p?._nameInput !== undefined
          ? String(p._nameInput || '').trim()
          : `${p?.first_name || ''} ${p?.last_name || ''}`.trim()
      return parentName === name
    })
  }, [])

  useEffect(() => {
    const parent = data.manualData
    const parentTeachers = parent.teachers ?? []
    const parentClasses = parent.classes ?? []
    manualDataRef.current = {
      ...localData,
      subjectIdByNameAndClass:
        (parent as any).subjectIdByNameAndClass ?? (localData as any).subjectIdByNameAndClass,
      teachers: (localData.teachers ?? []).map((t: any) => {
        const id =
          parsePositiveNumber(t?.id) ??
          parsePositiveNumber(matchParentTeacher(t, parentTeachers)?.id)
        return id ? {...t, id} : t
      }),
      classes: (localData.classes ?? []).map((c: any) => {
        const name = String(c?.name ?? '').trim()
        const id =
          parsePositiveNumber(c?.id) ??
          parsePositiveNumber(
            parentClasses.find((p: any) => String(p?.name ?? '').trim() === name)?.id
          )
        return id ? {...c, id} : c
      }),
    }
  }, [localData, data.manualData, matchParentTeacher])
  const persistTimerRef = useRef<number | null>(null)
  const didMountPersistRef = useRef(false)
  const studentsCsvInputRef = useRef<HTMLInputElement>(null)
  const [isImportingStudentsFile, setIsImportingStudentsFile] = useState(false)
  // "Any file -> our template" (data-upload/convert): AI servers only. Elsewhere the upload
  // stays CSV/Excel, exactly as before.
  const studentsConversionAvailable = isDataUploadConversionAvailable()
  const studentsUploadConfig = useDataUploadConfig('students', studentsConversionAvailable)
  /** True while a PDF / Word / photo is being read server-side (can take a minute a page). */
  const [studentsReadingFile, setStudentsReadingFile] = useState(false)
  /** Report of the last conversion, shown above the grid until dismissed or replaced. */
  const [studentsConversion, setStudentsConversion] = useState<DataUploadConversion | null>(null)
  /** 0..1 while the file's pieces are being sent; null otherwise. */
  const [studentsUploadProgress, setStudentsUploadProgress] = useState<number | null>(null)

  // Lesson planning grid state: {className: {subjectName: {teacher, frequency, duration}}}
  const [lessonPlanningData, setLessonPlanningData] = useState<
    Record<string, Record<string, {teacher: string; frequency: string; duration: string}>>
  >((data.manualData.lessonPlanningData as any) || {})

  // Modal state for editing a cell (frequency/duration only - teacher is selected on slot)
  const [lessonEditModal, setLessonEditModal] = useState<{
    show: boolean
    className: string
    subjectName: string
    frequency: string
    duration: string
    hasExistingData: boolean
  }>({
    show: false,
    className: '',
    subjectName: '',
    frequency: '',
    duration: '',
    hasExistingData: false,
  })

  // Selection state for rows/columns
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [selectedCols, setSelectedCols] = useState<string[]>([])
  const [activeLessonBranch, setActiveLessonBranch] = useState<string>('')
  const [isLessonPlanningLoading, setIsLessonPlanningLoading] = useState(false)
  const [activeLessonLevel, setActiveLessonLevel] = useState<string>('')
  const [activeLessonCurriculum, setActiveLessonCurriculum] = useState<string>('')
  // Names only: state persisted by an older build may still hold `{name, id}` entries.
  const branchOptions = Array.from(
    new Set(
      ((localData.branches || data.manualData?.branches || []) as unknown[])
        .map((entry: any) => String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim())
        .filter(Boolean)
    )
  )

  const normalizeBranchName = useCallback(
    (branch?: string) => (branch || '').toString().trim() || 'Main',
    []
  )

  const getLessonClassKey = useCallback(
    (className: string, branch?: string, curriculum?: string) => {
      const cleanCurriculum = (curriculum || '').toString().trim()
      return `${normalizeBranchName(branch)}|||${cleanCurriculum}|||${className}`
    },
    [normalizeBranchName]
  )

  const [classesFlowStep, setClassesFlowStep] = useState<number>(
    () => data.manualData?.classesFlowStep ?? 2
  )
  // Flips true the moment the user navigates the internal classes flow. The flow-step seeding
  // effect below only runs while this is false, so it seeds from async hydration on resume but
  // never fights in-session navigation (saveProgress can persist a momentarily-stale flow step).
  const classesFlowNavigatedRef = useRef(false)

  // Report the active sub-step upward so the parent can keep the URL in sync. With a
  // stepperOverride (Curriculum step), the active tab + classesFlowStep map to a stepper
  // id (classes/sections/review/subjects); otherwise the active tab id is reported.
  //
  // The callback is read through a ref and is NOT an effect dependency: the parent passes
  // an inline arrow that also posts the tab's `onboarding_substep_final` analytics row, and
  // its identity changes on every parent render — including the one the callback itself
  // triggers (it sets the parent's sub-step slug). With it in the deps the effect re-ran
  // on that render and the row was posted twice ~1 ms apart, racing past server dedupe.
  const onActiveSubStepChangeRef = useRef(onActiveSubStepChange)
  onActiveSubStepChangeRef.current = onActiveSubStepChange
  useEffect(() => {
    const report = onActiveSubStepChangeRef.current
    if (!report) return
    let subStepId: string = activeTab
    if (activeTab === 'teachers' && teachersPhase === 'review') subStepId = 'review-teachers'
    if (stepperOverride) {
      const match = stepperOverride.steps.find(
        (s) =>
          (s.topTabId ?? s.id) === activeTab &&
          (s.classesFlowStep === undefined || s.classesFlowStep === classesFlowStep)
      )
      if (match) subStepId = match.id
    }
    report(subStepId)
  }, [activeTab, classesFlowStep, stepperOverride, teachersPhase])
  const initialReviewRowsByBranch =
    ((data.manualData as any)?.classesReviewRowsByBranch as Record<string, ClassesReviewRow[]>) ??
    {}
  const initialReviewRowIdCounter = Object.values(initialReviewRowsByBranch)
    .flat()
    .reduce((max, r) => {
      const match = /review-row-(\d+)/.exec(r.id || '')
      const n = match ? Number(match[1]) : 0
      return Number.isFinite(n) ? Math.max(max, n) : max
    }, 0)
  const [classesExpandedCurriculum, setClassesExpandedCurriculum] = useState<Set<string>>(new Set())
  const [sectionCountInput, setSectionCountInput] = useState(
    () => data.manualData?.classesSectionCount ?? '5'
  )
  const [sectionNomenclatureInput, setSectionNomenclatureInput] = useState(
    () => data.manualData?.classesSectionNomenclature ?? 'A,B,C,D,E'
  )
  const MAX_SECTIONS = 26
  const [sectionCountError, setSectionCountError] = useState<string | null>(null)
  const [classInputByLevel, setClassInputByLevel] = useState<Record<string, string>>({})
  const [prefilledClassesByCurriculumLevel, setPrefilledClassesByCurriculumLevel] = useState<
    Record<string, Record<string, PrefilledClassEntry[]>>
  >({})
  const [reviewRowsByBranch, setReviewRowsByBranch] =
    useState<Record<string, ClassesReviewRow[]>>(initialReviewRowsByBranch)
  const [expandedBranch, setExpandedBranch] = useState<Set<string>>(
    () => new Set(Object.keys(initialReviewRowsByBranch))
  )
  // Inline editing enhancement: Review table is always editable in-place.
  const classesPrefillInitializedRef = useRef(false)
  const classesDetailsFetchInProgressRef = useRef(false)
  const reviewRowIdCounterRef = useRef(initialReviewRowIdCounter)
  const didHydratePersistedReviewRef = useRef(false)
  // True once the user edits the Review table this session (section edits / row deletes). Gates the
  // hydration seeding effect below so it can never clobber in-session work.
  const reviewRowsEditedRef = useRef(false)

  const createReviewRow = useCallback((base: Omit<ClassesReviewRow, 'id'>): ClassesReviewRow => {
    reviewRowIdCounterRef.current += 1
    return {
      id: `review-row-${reviewRowIdCounterRef.current}`,
      ...base,
    }
  }, [])

  const selectedLevelsByCurriculum = useMemo(() => {
    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}
    const selectedCurriculums = data.manualData?.curriculums ?? Object.keys(levelsByCurriculum)
    const out: Record<string, string[]> = {}

    selectedCurriculums.forEach((curriculumName) => {
      const curriculum = levelsByCurriculum[curriculumName]
      if (!curriculum) return

      if (curriculum.levelsSameAcrossBranches === false) {
        const merged = uniqueStrings(Object.values(curriculum.levelsPerBranch ?? {}).flat())
        out[curriculumName] = merged
        return
      }

      out[curriculumName] = uniqueStrings((curriculum.levels ?? []).map((l: any) => l?.name || ''))
    })

    return out
  }, [data.manualData?.curriculums, data.manualData?.levelsByCurriculum])

  const getSubjectLevelKey = useCallback(
    (curriculumName: string, levelName: string) => `${curriculumName}|||${levelName}`,
    []
  )

  const subjectLevelRows = useMemo<SubjectLevelRow[]>(() => {
    return Object.entries(selectedLevelsByCurriculum).flatMap(([curriculumName, levels]) =>
      levels.map((levelName) => ({
        curriculum: curriculumName,
        level: levelName,
        key: getSubjectLevelKey(curriculumName, levelName),
      }))
    )
  }, [getSubjectLevelKey, selectedLevelsByCurriculum])

  const defaultSubjectSelectionsByLevel = useMemo(() => {
    const selections: Record<string, string[]> = {}

    subjectLevelRows.forEach(({curriculum, level, key}) => {
      const entries = prefilledClassesByCurriculumLevel[curriculum]?.[level] ?? []
      selections[key] = uniqueStrings(entries.flatMap((entry) => entry.subjects || []))
    })

    return selections
  }, [prefilledClassesByCurriculumLevel, subjectLevelRows])

  const getEffectiveSubjectSelectionsByLevel = useCallback(
    (subjectSelectionsByLevel?: Record<string, string[]>) => {
      const nextSelections: Record<string, string[]> = {}
      const providedSelections =
        subjectSelectionsByLevel ?? localData.subjectSelectionsByLevel ?? {}

      subjectLevelRows.forEach(({key}) => {
        if (Object.prototype.hasOwnProperty.call(providedSelections, key)) {
          nextSelections[key] = uniqueStrings(providedSelections[key] ?? [])
          return
        }

        nextSelections[key] = uniqueStrings(defaultSubjectSelectionsByLevel[key] ?? [])
      })

      return nextSelections
    },
    [defaultSubjectSelectionsByLevel, localData.subjectSelectionsByLevel, subjectLevelRows]
  )

  // Subjects are chosen per curriculum/level, but the bulk-import creates a `subject` row per
  // CLASS, and a student can only be linked to a subject that exists in their own class. So this
  // emits one row per (subject, class) for every class of that level — the same shape Step 3's
  // applyAcademicStructure writes. It used to emit one row per (subject, level) carrying the
  // FIRST class of that level, so the students sync (which sends subjects along, before Lesson
  // Planning has run) created e.g. "English" in "Playgroup-A" only, and every student in the
  // level's other classes failed with `subject "English" does not exist in class "…"`.
  const flattenSubjectsFromSelections = useCallback(
    (subjectSelectionsByLevel: Record<string, string[]>) => {
      const existingSubjects = [
        ...((data.manualData?.subjects as any[]) ?? []),
        ...((localData.subjects as any[]) ?? []),
      ]
      const existingByClass = new Map<string, any>()
      const existingByKey = new Map<string, any>()
      const existingByName = new Map<string, any>()
      existingSubjects.forEach((subject) => {
        const name = String(subject?.name ?? '').trim()
        const curriculum = String(subject?.curriculum ?? '').trim()
        const level = String(subject?.level ?? '').trim()
        const className = String(subject?.class ?? '').trim()
        const branch = String(subject?.branch ?? '').trim()
        if (!name) return
        if (className) {
          // Keyed by curriculum when the row has one: two curricula can share a class name,
          // and each class's subject row (and id) is its own.
          const classKeys = curriculum
            ? [`${name}|||${className}|||${branch}|||${curriculum}`]
            : [`${name}|||${className}|||${branch}`, `${name}|||${className}`]
          classKeys.forEach((classKey) => {
            if (!existingByClass.has(classKey)) existingByClass.set(classKey, subject)
          })
        }
        const key = `${name}|||${curriculum}|||${level}`
        if (!existingByKey.has(key)) existingByKey.set(key, subject)
        if (!existingByName.has(name)) existingByName.set(name, subject)
      })

      const subjectIdByNameAndClass =
        ((localData as any)?.subjectIdByNameAndClass as Record<string, number> | undefined) ??
        ((data.manualData as any)?.subjectIdByNameAndClass as Record<string, number> | undefined) ??
        {}
      const classRows = ((localData.classes as any[]) ?? (data.manualData?.classes as any[]) ?? [])
        .map((cls: any) => ({
          name: String(cls?.name ?? '').trim(),
          branch: String(cls?.branch ?? '').trim(),
          level: String(cls?.level ?? '').trim(),
          curriculum: String(cls?.curriculum ?? '').trim(),
        }))
        .filter((cls) => cls.name && cls.level)

      return subjectLevelRows.flatMap(({curriculum, level, key}) => {
        const names = uniqueStrings(subjectSelectionsByLevel[key] ?? [])
        if (names.length === 0) return []
        // A class row written before the curriculum was tracked carries none — match on level.
        const levelClasses = classRows.filter(
          (cls) => cls.level === level && (!cls.curriculum || cls.curriculum === curriculum)
        )
        const templateFor = (name: string) =>
          existingByKey.get(`${name}|||${curriculum}|||${level}`) ?? existingByName.get(name)

        if (levelClasses.length === 0) {
          // No classes for this level yet: keep a class-less row so the payload builder expands
          // it over the level's classes once they exist, instead of pinning it to one class.
          return names.map((name) => {
            const existing = templateFor(name)
            return {
              name,
              curriculum,
              level,
              teacher: '',
              class: '',
              frequency: String(existing?.frequency ?? '').trim(),
              duration: String(existing?.duration ?? '').trim(),
            }
          })
        }

        const seen = new Set<string>()
        return names.flatMap((name) =>
          levelClasses.flatMap((cls) => {
            const dedupeKey = `${name}|||${cls.name}|||${cls.branch}`
            if (seen.has(dedupeKey)) return []
            seen.add(dedupeKey)
            const perClass =
              existingByClass.get(
                `${name}|||${cls.name}|||${cls.branch}|||${cls.curriculum || curriculum}`
              ) ??
              existingByClass.get(`${name}|||${cls.name}|||${cls.branch}`) ??
              existingByClass.get(`${name}|||${cls.name}`)
            const template = perClass ?? templateFor(name)
            const id =
              parsePositiveNumber(perClass?.id) ??
              lookupSubjectId(subjectIdByNameAndClass, name, cls.name, cls.curriculum || curriculum)
            return [
              {
                name,
                curriculum,
                level,
                ...(cls.branch ? {branch: cls.branch} : {}),
                // A teacher belongs to one class-subject; never copy it across the level.
                teacher: String(perClass?.teacher ?? '').trim(),
                class: cls.name,
                frequency: String(template?.frequency ?? '').trim(),
                duration: String(template?.duration ?? '').trim(),
                ...(id ? {id} : {}),
                ...(perClass?.action ? {action: perClass.action} : {}),
              },
            ]
          })
        )
      })
    },
    [data.manualData, localData, subjectLevelRows]
  )

  const getSubjectsForCurriculumLevel = useCallback(
    (curriculumName: string, levelName: string) => {
      const selections = getEffectiveSubjectSelectionsByLevel()
      return uniqueStrings(selections[getSubjectLevelKey(curriculumName, levelName)] ?? [])
    },
    [getEffectiveSubjectSelectionsByLevel, getSubjectLevelKey]
  )

  const parseSections = useCallback(() => {
    const requestedCount = Number.parseInt(sectionCountInput, 10)
    const count = Number.isFinite(requestedCount) && requestedCount > 0 ? requestedCount : 0
    const fromNomenclature = uniqueStrings(
      sectionNomenclatureInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    )

    if (count > 0 && fromNomenclature.length >= count) {
      return fromNomenclature.slice(0, count)
    }

    if (count > 0 && fromNomenclature.length > 0) {
      const padded = [...fromNomenclature]
      let idx = 1
      while (padded.length < count) {
        padded.push(String(idx))
        idx += 1
      }
      return padded
    }

    if (fromNomenclature.length > 0) return fromNomenclature
    if (count <= 0) return []
    return Array.from({length: count}, (_, i) => String(i + 1))
  }, [sectionCountInput, sectionNomenclatureInput])

  const deriveManualDataFromReview = useCallback(
    (review: Record<string, ClassesReviewRow[]>, prevSubjects: any[] = []) => {
      const classes: any[] = []
      const subjectsSet = new Set<string>()

      Object.entries(review).forEach(([branchName, rows]) => {
        rows.forEach((row) => {
          const sections = row.sections.length > 0 ? row.sections : ['A']
          sections.forEach((sectionName) => {
            const section = sectionName.trim()
            if (!row.gradeName.trim() || !section) return
            classes.push({
              branch: branchName,
              level: row.level,
              curriculum: row.curriculum,
              grade: row.gradeName,
              section,
              teacher: '',
              name: `${row.gradeName}-${section}`,
            })
          })

          row.subjects.forEach((subjectName) => {
            const clean = subjectName.trim()
            if (clean) subjectsSet.add(clean)
          })
        })
      })

      const nextSubjects =
        subjectsSet.size > 0 ? Array.from(subjectsSet).map((name) => ({name})) : prevSubjects

      return {
        classes,
        classesCount: classes.length,
        subjects: nextSubjects,
        subjectsCount: nextSubjects.length,
      }
    },
    []
  )

  const syncManualDataFromReview = useCallback(
    (review: Record<string, ClassesReviewRow[]>) => {
      setLocalData((prev) => {
        const derived = deriveManualDataFromReview(review, (prev.subjects as any[]) || [])

        // Prune the pending delete list of any class that is back in the derived set (re-added),
        // so a re-added class is sent as insert — not both insert and delete.
        const key = (c: any) =>
          [
            String(c?.name ?? '')
              .trim()
              .toLowerCase(),
            String(c?.branch ?? '')
              .trim()
              .toLowerCase(),
            String(c?.level ?? '')
              .trim()
              .toLowerCase(),
          ].join('|||')
        const presentKeys = new Set(((derived.classes as any[]) ?? []).map(key))
        const prevDeleted = ((prev as any).deletedClasses as any[]) ?? []
        const nextDeleted = prevDeleted.filter((c) => !presentKeys.has(key(c)))

        const next = {
          ...prev,
          ...derived,
          deletedClasses: nextDeleted,
        }
        deferUpdateData({manualData: next})
        return next
      })
    },
    [deriveManualDataFromReview, deferUpdateData]
  )

  const buildReviewRowsFromPrefill = useCallback(() => {
    const sections = parseSections()
    const branches = branchOptions.length > 0 ? branchOptions : ['Main Branch']
    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}

    // Per-branch authority from the current table (seeded from server data on resume). The prefill
    // source is curriculum/level-scoped (branch-agnostic), so blindly replicating it to every
    // branch resurrects grades that were deleted from only some branches. Rule: a grade present in
    // SOME branch's current rows but missing from this branch's was deleted here — skip it. A grade
    // in no branch at all is newly added on the Classes screen — replicate it everywhere as before.
    // Existing rows are reused so per-row section edits survive the rebuild.
    const gradeKey = (curriculum: string, level: string, grade: string) =>
      `${curriculum}|||${level}|||${grade}`
    const knownGrades = new Set<string>()
    const rowByBranchGrade = new Map<string, ClassesReviewRow>()
    Object.entries(reviewRowsByBranch).forEach(([branchName, rows]) => {
      rows.forEach((row) => {
        const key = gradeKey(row.curriculum, row.level, row.gradeName)
        knownGrades.add(key)
        rowByBranchGrade.set(`${branchName}|||${key}`, row)
      })
    })

    const out: Record<string, ClassesReviewRow[]> = {}
    branches.forEach((branchName) => {
      const rows: ClassesReviewRow[] = []
      const branchHasRows = (reviewRowsByBranch[branchName] ?? []).length > 0

      Object.entries(prefilledClassesByCurriculumLevel).forEach(([curriculumName, levels]) => {
        const curriculumData = levelsByCurriculum[curriculumName]
        const allowedLevels = (() => {
          if (!curriculumData) {
            return Object.keys(levels)
          }
          if (curriculumData.levelsSameAcrossBranches === false) {
            return uniqueStrings(curriculumData.levelsPerBranch?.[branchName] ?? [])
          }
          return uniqueStrings((curriculumData.levels ?? []).map((level: any) => level?.name || ''))
        })()

        allowedLevels.forEach((levelName) => {
          const entries = levels[levelName] ?? []
          entries.forEach((entry) => {
            if (!entry.name.trim()) return
            const key = gradeKey(curriculumName, levelName, entry.name)
            const existingRow = rowByBranchGrade.get(`${branchName}|||${key}`)
            if (existingRow) {
              // Keep the branch's own row (so per-branch deletions stay deleted), but refresh
              // both subjects and sections — a later change on the Sections screen must reach
              // grades that already have a row, not only newly-added ones.
              rows.push({
                ...existingRow,
                sections: [...sections],
                subjects: uniqueStrings(entry.subjects || []),
              })
              return
            }
            // Known in another branch but absent here → deleted from this branch; don't resurrect.
            if (branchHasRows && knownGrades.has(key)) return
            rows.push(
              createReviewRow({
                curriculum: curriculumName,
                level: levelName,
                gradeName: entry.name,
                sections: [...sections],
                subjects: uniqueStrings(entry.subjects || []),
              })
            )
          })
        })
      })

      out[branchName] = rows
    })

    return out
  }, [
    branchOptions,
    createReviewRow,
    data.manualData?.levelsByCurriculum,
    parseSections,
    prefilledClassesByCurriculumLevel,
    reviewRowsByBranch,
  ])

  useEffect(() => {
    if (classesPrefillInitializedRef.current) return
    // Wait until the curriculum/level config has loaded (async on resume) before prefilling,
    // otherwise we'd mark prefill "done" against empty data and never re-run.
    if (Object.keys(selectedLevelsByCurriculum).length === 0) return

    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}
    const selectedCurriculums = data.manualData?.curriculums ?? Object.keys(levelsByCurriculum)

    // Build the per-curriculum/level prefilled-classes map from a given school-system-details set,
    // preferring any per-curriculum levelDetails the user/cache already carries.
    const buildPrefillMap = (systemDetails: ReturnType<typeof readStoredSchoolSystemDetails>) => {
      const nextMap: Record<string, Record<string, PrefilledClassEntry[]>> = {}
      Object.entries(selectedLevelsByCurriculum).forEach(([curriculumName, selectedLevels]) => {
        const userLevelDetails = levelsByCurriculum[curriculumName]?.levelDetails ?? {}
        const systemLevelDetails = systemDetails[curriculumName] ?? {}
        const levelMap: Record<string, PrefilledClassEntry[]> = {}
        selectedLevels.forEach((levelName) => {
          const userClasses = userLevelDetails[levelName]?.classes
          const systemClasses = systemLevelDetails[levelName]?.classes
          // Prefer user/server-edited classes when they actually exist; otherwise fall back to the
          // fetched school-system-details defaults. An EMPTY levelDetails entry (e.g. a resume
          // where the server returned the level but no classes yet) must not mask the defaults —
          // so we resolve per level instead of spread-merging, which would let {} clobber them.
          const rawClasses =
            Array.isArray(userClasses) && userClasses.length > 0
              ? userClasses
              : Array.isArray(systemClasses)
                ? systemClasses
                : []
          // The chosen class list may carry NAMES without subjects — e.g. on resume the server
          // returns reviewed classes but no subjects until they're saved. Index the fetched
          // school-system-details classes by name so we can backfill each class's default subjects.
          const systemSubjectsByClassName = new Map<string, string[]>()
          ;(Array.isArray(systemClasses) ? systemClasses : []).forEach((cls: any) => {
            const nm = (cls?.name || cls?.class || '').toString().trim().toLowerCase()
            if (!nm) return
            const subs = Array.isArray(cls?.subjects)
              ? cls.subjects
              : splitSubjectsOutsideParens((cls?.subjects || '').toString())
            systemSubjectsByClassName.set(nm, uniqueStrings(subs))
          })
          const classesForLevel = rawClasses
            .map((cls: any) => {
              const className = (cls?.name || cls?.class || '').toString().trim()
              const subjectList = Array.isArray(cls?.subjects)
                ? cls.subjects
                : splitSubjectsOutsideParens((cls?.subjects || '').toString())
              let subjects = uniqueStrings(subjectList)
              // Backfill default subjects from the fetched details when the class has none.
              if (subjects.length === 0) {
                const fromSystem = systemSubjectsByClassName.get(className.toLowerCase())
                if (fromSystem && fromSystem.length > 0) subjects = fromSystem
              }
              return {
                name: className,
                subjects,
              }
            })
            .filter((cls: PrefilledClassEntry) => cls.name.length > 0)
          levelMap[levelName] = classesForLevel
        })
        if (Object.keys(levelMap).length > 0) {
          nextMap[curriculumName] = levelMap
        }
      })
      return nextMap
    }

    const applyPrefill = (systemDetails: ReturnType<typeof readStoredSchoolSystemDetails>) => {
      const nextMap = buildPrefillMap(systemDetails)
      setPrefilledClassesByCurriculumLevel(nextMap)
      setClassesExpandedCurriculum(new Set(Object.keys(nextMap)))
      classesPrefillInitializedRef.current = true
    }

    const storedSystemDetails = readStoredSchoolSystemDetails()
    // "Past review": the bulk-import runs at/after the curriculum review, so once the server
    // returns reviewed classes they arrive in resume-data (classesReviewRowsByBranch). When that
    // exists the Classes step honours resume-data (the review table is never overwritten below).
    const hasResumeClasses = Object.keys(initialReviewRowsByBranch).length > 0
    // Have subjects already been saved? They come back in resume-data (subjects array) or are
    // held in-session (subjectSelectionsByLevel). If so the Subjects step needs no defaults.
    const savedSubjectSelections = (data.manualData?.subjectSelectionsByLevel ?? {}) as Record<
      string,
      string[]
    >
    const hasSavedSubjects =
      Object.values(savedSubjectSelections).some((arr) => Array.isArray(arr) && arr.length > 0) ||
      ((data.manualData?.subjects as any[]) ?? []).length > 0
    // Do the selected curriculums already have cached class details (e.g. fetched at the levels
    // step)? If so we can prefill straight from cache without another API call.
    const hasCachedClasses = selectedCurriculums.some(
      (c) => Object.keys(storedSystemDetails[c] ?? {}).length > 0
    )
    // Fetch defaults when the cache is empty and at least one step still needs them: classes not
    // yet reviewed (Classes step) OR subjects not yet saved (Subjects step). The response's
    // classes carry their subjects, so one fetch prefills both. It is non-destructive — reviewed
    // classes and saved subject selections always take precedence over these defaults.
    const needsDefaults = !hasResumeClasses || !hasSavedSubjects

    if (needsDefaults && !hasCachedClasses && selectedCurriculums.length > 0) {
      // Landed on Classes/Subjects without cached details (levels skipped, a fresh resume, or the
      // user left the subjects step without saving): fetch school-system-details for the selected
      // curriculum and prefill classes + subjects from the response. Guard against a duplicate
      // fetch if the effect re-runs before the async response marks prefill done.
      if (classesDetailsFetchInProgressRef.current) return
      classesDetailsFetchInProgressRef.current = true
      // timetable/prefill for catalog picks, the legacy details endpoint for older ones; the
      // loader merges whatever resolved into the cache and always completes.
      loadCurriculumDefaults(
        dispatch,
        {
          country: data.country,
          curriculums: selectedCurriculums,
          codes: data.manualData?.curriculumCodes,
        },
        () => {
          classesDetailsFetchInProgressRef.current = false
          applyPrefill(readStoredSchoolSystemDetails())
        }
      )
      return
    }

    applyPrefill(storedSystemDetails)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.manualData?.levelsByCurriculum,
    data.manualData?.curriculums,
    selectedLevelsByCurriculum,
  ])

  // When returning to Classes tab on the review step (3), rebuild review table from persisted class names and section config
  useEffect(() => {
    // Guard on actual ROWS, not branch keys: a resume/back-navigation can leave
    // reviewRowsByBranch holding branch keys with empty arrays (e.g. {"Main Branch": []}) —
    // a state that would otherwise pass a keys-length check and permanently block the rebuild,
    // leaving the Review grid empty. Only skip when some branch genuinely has rows, or the user
    // has edited the table this session (so we never resurrect intentional deletions).
    const hasAnyReviewRows = Object.values(reviewRowsByBranch).some(
      (rows) => (rows?.length ?? 0) > 0
    )
    if (
      !classesPrefillInitializedRef.current ||
      classesFlowStep !== 3 ||
      reviewRowsEditedRef.current ||
      hasAnyReviewRows ||
      Object.keys(prefilledClassesByCurriculumLevel).length === 0
    ) {
      return
    }
    const review = buildReviewRowsFromPrefill()
    setExpandedBranch(new Set(Object.keys(review)))
    setReviewRowsByBranch(review)
    syncManualDataFromReview(review)
  }, [
    classesFlowStep,
    reviewRowsByBranch,
    prefilledClassesByCurriculumLevel,
    buildReviewRowsFromPrefill,
    syncManualDataFromReview,
  ])

  // Hydrate localData from persisted Review table (so refresh keeps everything consistent).
  useEffect(() => {
    if (didHydratePersistedReviewRef.current) return
    if (classesFlowStep !== 3) return
    if (Object.keys(reviewRowsByBranch).length === 0) return

    didHydratePersistedReviewRef.current = true
    setExpandedBranch(new Set(Object.keys(reviewRowsByBranch)))
    syncManualDataFromReview(reviewRowsByBranch)
  }, [classesFlowStep, reviewRowsByBranch, syncManualDataFromReview])

  const didRepairClassesFromReviewRef = useRef(false)
  useEffect(() => {
    if (didRepairClassesFromReviewRef.current) return
    if (classesFlowStep !== 3) return

    const persistedReview =
      ((data.manualData as any)?.classesReviewRowsByBranch as Record<string, ClassesReviewRow[]>) ??
      {}
    if (Object.keys(persistedReview).length === 0) return

    const derived = deriveManualDataFromReview(
      persistedReview,
      (data.manualData?.subjects as any[]) || []
    )
    const currentClasses = ((data.manualData?.classes as any[]) || []) as any[]

    const currentByBranch = new Map<string, number>()
    currentClasses.forEach((c: any) => {
      const b = (c?.branch || '').toString()
      currentByBranch.set(b, (currentByBranch.get(b) || 0) + 1)
    })
    const derivedByBranch = new Map<string, number>()
    ;(derived.classes || []).forEach((c: any) => {
      const b = (c?.branch || '').toString()
      derivedByBranch.set(b, (derivedByBranch.get(b) || 0) + 1)
    })

    const needsRepair = Array.from(derivedByBranch.entries()).some(
      ([branch, count]) => count > 0 && (currentByBranch.get(branch) || 0) < count
    )
    if (!needsRepair) return

    didRepairClassesFromReviewRef.current = true
    updateData({
      manualData: {
        ...data.manualData,
        ...derived,
      },
    })
    // localData will mirror from wizard state; no need to setLocalData here.
  }, [classesFlowStep, data.manualData, deriveManualDataFromReview, updateData])

  // Persist Classes tab data to wizard state so it survives navigation (forward/back) and refresh
  useEffect(() => {
    if (!classesPrefillInitializedRef.current) return

    const levelsByCurriculum = data.manualData?.levelsByCurriculum ?? {}
    const nextLevelsByCurriculum = {...levelsByCurriculum}

    Object.entries(prefilledClassesByCurriculumLevel).forEach(([curriculumName, levelMap]) => {
      const existing = nextLevelsByCurriculum[curriculumName] ?? {}
      const nextLevelDetails: Record<string, {classes: PrefilledClassEntry[]}> = {
        ...(existing.levelDetails ?? {}),
      }
      Object.entries(levelMap).forEach(([levelName, entries]) => {
        nextLevelDetails[levelName] = {classes: entries}
      })
      nextLevelsByCurriculum[curriculumName] = {
        ...existing,
        levelDetails: nextLevelDetails,
      }
    })

    updateData({
      manualData: {
        ...data.manualData,
        levelsByCurriculum: nextLevelsByCurriculum,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledClassesByCurriculumLevel, updateData])

  // Persist Review table edits so refresh keeps grade/sections/subjects changes.
  useEffect(() => {
    if (classesFlowStep !== 3) return
    if (!classesPrefillInitializedRef.current) return

    const t = window.setTimeout(() => {
      const nextManualData = saveProgress('classes', 3)
      const derived = deriveManualDataFromReview(
        reviewRowsByBranch,
        ((nextManualData as any)?.subjects as any[]) || (data.manualData?.subjects as any[]) || []
      )
      updateData({
        manualData: {
          ...(nextManualData as any),
          ...derived,
          classesReviewRowsByBranch: reviewRowsByBranch,
          classesFlowStep: 3,
        } as any,
      })
    }, 300)

    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classesFlowStep, reviewRowsByBranch])

  // Persist section count and nomenclature so they survive navigation
  useEffect(() => {
    updateData({
      manualData: {
        ...data.manualData,
        classesSectionCount: sectionCountInput,
        classesSectionNomenclature: sectionNomenclatureInput,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only persist when section inputs change
  }, [sectionCountInput, sectionNomenclatureInput, updateData])

  // Seed the section inputs from server-hydrated data. The inputs are `useState`-initialized at
  // mount, so a resume that populates classesSectionCount/Nomenclature asynchronously (after this
  // component has mounted) would otherwise be missed. Compare-based (not a one-time guard): the
  // persist effect above keeps manualData mirrored to the inputs, so this only fires when the value
  // changes externally (hydration) and never clobbers a user edit (values match after an edit).
  useEffect(() => {
    const count = data.manualData?.classesSectionCount
    const nomenclature = data.manualData?.classesSectionNomenclature
    if (count && String(count) !== sectionCountInput) setSectionCountInput(String(count))
    if (nomenclature && String(nomenclature) !== sectionNomenclatureInput) {
      setSectionNomenclatureInput(String(nomenclature))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.manualData?.classesSectionCount, data.manualData?.classesSectionNomenclature])

  // Seed the internal classes-flow position (1=Sections, 2=Classes, 3=Review) from server-hydrated
  // data. Like the section inputs above, `classesFlowStep` is `useState`-initialized at mount, so a
  // resume that restores it asynchronously — e.g. a user who reached Review and left without saving —
  // would otherwise land back on the default Classes screen instead of where they left off. Guarded
  // by classesFlowNavigatedRef so it ONLY applies before the user starts navigating: once they move
  // within the flow, saveProgress may persist a momentarily-stale flow step, and re-seeding from that
  // would revert the screen (making "Next" appear to do nothing).
  useEffect(() => {
    if (classesFlowNavigatedRef.current) return
    const persisted = data.manualData?.classesFlowStep
    if (typeof persisted === 'number' && persisted !== classesFlowStep) {
      setClassesFlowStep(persisted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.manualData?.classesFlowStep])

  // Seed the Review table from server-hydrated data. reviewRowsByBranch is useState-initialized at
  // mount from the (possibly stale) localStorage copy, so a resume whose hydration replaces
  // classesReviewRowsByBranch — e.g. a grade deleted last session that the backend no longer
  // returns — would otherwise keep showing the stale rows. Only applies while the user hasn't
  // navigated the classes flow or edited the review table this session.
  useEffect(() => {
    if (classesFlowNavigatedRef.current || reviewRowsEditedRef.current) return
    const persisted = (data.manualData as any)?.classesReviewRowsByBranch as
      | Record<string, ClassesReviewRow[]>
      | undefined
    if (!persisted || Object.keys(persisted).length === 0) return
    if (JSON.stringify(reviewRowsByBranch ?? {}) === JSON.stringify(persisted)) return
    setReviewRowsByBranch(persisted)
    setExpandedBranch(new Set(Object.keys(persisted)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(data.manualData as any)?.classesReviewRowsByBranch])

  useEffect(() => {
    const tabExists = tabs.some((tab) => tab.id === activeTab)
    if (!tabExists) {
      setActiveTab(tabs[0]?.id || 'teachers')
    }
  }, [tabs, activeTab])

  // Frequency/Duration info tooltip visibility (lesson planning tab)
  const [infoTooltipOpen, setInfoTooltipOpen] = useState(false)

  // Delete confirmation for lesson planning slot
  const [deleteSlotConfirm, setDeleteSlotConfirm] = useState<{
    className: string
    subjectName: string
  } | null>(null)
  // Delete confirmation for whole row/column
  const [deleteRowColConfirm, setDeleteRowColConfirm] = useState<{
    type: 'row' | 'col'
    name: string
    rowKey?: string
    rowSubjects?: string[]
  } | null>(null)
  const [pendingDeleteAction, setPendingDeleteAction] = useState<PendingDeleteAction | null>(null)
  const [donAskAgainDelete, setDonAskAgainDelete] = useState(false)
  const [donAskAgainDeleteRow, setDonAskAgainDeleteRow] = useState(false)
  const deleteSlotRef = useRef<{className: string; subjectName: string} | null>(null)

  const LESSON_DELETE_SKIP_CONFIRM_KEY = 'onboarding_lesson_delete_skip_confirm'
  const ROW_DELETE_SKIP_CONFIRM_KEY = 'onboarding_row_delete_skip_confirm'

  const [deletedSlots, setDeletedSlots] = useState<Set<string>>(
    () => new Set((data.manualData?.deletedLessonSlots as string[]) || [])
  )
  const slotKey = useCallback((cn: string, sn: string) => `${cn}|||${sn}`, [])
  const isSlotDeleted = useCallback(
    (cn: string, sn: string) => {
      return deletedSlots.has(slotKey(cn, sn))
    },
    [deletedSlots, slotKey]
  )

  const performDeleteSlot = useCallback(
    (slot: {className: string; subjectName: string}) => {
      const {className: cn, subjectName: sn} = slot
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        next.add(slotKey(cn, sn))
        return next
      })
      setLessonPlanningData((prev) => {
        const next = {...prev}
        const currentClassData = next[cn]
        if (!currentClassData) return prev
        const rest = Object.fromEntries(Object.entries(currentClassData).filter(([k]) => k !== sn))
        next[cn] = rest
        return next
      })
      deleteSlotRef.current = null
      setDeleteSlotConfirm(null)
    },
    [slotKey]
  )

  const toggleRow = (className: string) => {
    setSelectedRows((prev) =>
      prev.includes(className) ? prev.filter((c) => c !== className) : [...prev, className]
    )
  }

  const toggleCol = (subjectName: string) => {
    setSelectedCols((prev) =>
      prev.includes(subjectName) ? prev.filter((s) => s !== subjectName) : [...prev, subjectName]
    )
  }

  const toggleAllRows = (classes: any[]) => {
    const classKeys = classes
      .map((c: any) => getLessonClassKey(c.name || '', c.branch, c.curriculum))
      .filter(Boolean)
    if (selectedRows.length === classes.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(classKeys)
    }
  }

  useEffect(() => {
    const classes = (localData.classes as any[]) || []
    const branchesFromClasses = Array.from(
      new Set(classes.map((c: any) => normalizeBranchName(c?.branch)).filter(Boolean))
    )
    // Always include explicit branch options so newly-added branches (with no classes yet)
    // remain selectable and don't get auto-reset back to "Main".
    const availableBranches = Array.from(
      new Set([...branchOptions.map(normalizeBranchName), ...branchesFromClasses].filter(Boolean))
    )

    if (!activeLessonBranch && availableBranches.length > 0) {
      setActiveLessonBranch(availableBranches[0])
      return
    }

    if (activeLessonBranch && !availableBranches.includes(activeLessonBranch)) {
      setActiveLessonBranch(availableBranches[0] || '')
    }
  }, [activeLessonBranch, branchOptions, localData.classes, normalizeBranchName])

  // Clear loading state after branch change
  useEffect(() => {
    if (isLessonPlanningLoading) {
      const timer = setTimeout(() => {
        setIsLessonPlanningLoading(false)
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [isLessonPlanningLoading])
  // Delete whole row: mark all slots in that row as deleted
  const deleteRow = useCallback(
    (classKey: string, subjectNames: string[]) => {
      const keys = subjectNames.map((sn) => slotKey(classKey, sn))
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        keys.forEach((k) => next.add(k))
        return next
      })
    },
    [slotKey]
  )
  // Delete whole column
  const deleteColumn = useCallback(
    (subjectName: string, classNames: string[]) => {
      const keys = classNames.map((cn) => slotKey(cn, subjectName))
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        keys.forEach((k) => next.add(k))
        return next
      })
    },
    [slotKey]
  )

  const handleAddSubject = (curriculumName: string, levelName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const name = (subjectInputByLevel[key] || '').trim()
    if (!name) return

    const effectiveSelections = getEffectiveSubjectSelectionsByLevel()
    const rowSubjects = effectiveSelections[key] ?? []
    if (rowSubjects.some((subjectName) => subjectName.toLowerCase() === name.toLowerCase())) {
      setSubjectErrorByLevel((prev) => ({
        ...prev,
        [key]: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.DUPLICATE_ERROR'}),
      }))
      return
    }

    const nextSelections = {
      ...effectiveSelections,
      [key]: [...rowSubjects, name],
    }
    const nextSubjects = flattenSubjectsFromSelections(nextSelections)

    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
    setLocalData((prev) => {
      const nextLocal = {
        ...prev,
        subjectSelectionsByLevel: nextSelections,
        subjects: nextSubjects,
        subjectsCount: nextSubjects.length,
      }
      // Persist immediately so navigation/back never reverts to defaults.
      deferUpdateData({
        manualData: {
          ...nextLocal,
          manualActiveTab: 'subjects',
        },
      })
      return nextLocal
    })
    setSubjectInputByLevel((prev) => ({...prev, [key]: ''}))
  }

  const handleAddSubjectWithValue = (
    curriculumName: string,
    levelName: string,
    rawValue: string
  ) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const trimmed = (rawValue || '').trim()
    if (!trimmed) return

    if ((subjectInputByLevel[key] || '') !== rawValue) {
      setSubjectInputByLevel((prev) => ({...prev, [key]: rawValue}))
      setTimeout(() => handleAddSubject(curriculumName, levelName), 0)
      return
    }

    handleAddSubject(curriculumName, levelName)
  }

  const handleRemoveSubject = (curriculumName: string, levelName: string, subjectName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const effectiveSelections = getEffectiveSubjectSelectionsByLevel()
    const nextSelections = {
      ...effectiveSelections,
      [key]: (effectiveSelections[key] ?? []).filter((name) => name !== subjectName),
    }
    const nextSubjects = flattenSubjectsFromSelections(nextSelections)

    setLocalData((prev) => {
      const nextLocal = {
        ...prev,
        subjectSelectionsByLevel: nextSelections,
        subjects: nextSubjects,
        subjectsCount: nextSubjects.length,
      }
      // Persist immediately so deletes are retained (even if the row becomes empty).
      deferUpdateData({
        manualData: {
          ...nextLocal,
          manualActiveTab: 'subjects',
        },
      })
      return nextLocal
    })
    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
  }

  const startEditSubject = (curriculumName: string, levelName: string, subjectName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    setEditingSubjectByLevel((prev) => ({
      ...prev,
      [key]: {
        original: subjectName,
        value: subjectName,
      },
    }))
    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
  }

  const cancelEditSubject = (curriculumName: string, levelName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
  }

  const commitEditSubject = (curriculumName: string, levelName: string) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    const editing = editingSubjectByLevel[key]
    if (!editing) return

    const originalName = (editing.original || '').trim()
    const nextName = (editing.value || '').trim()
    if (!originalName) {
      setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
      return
    }

    if (!nextName) {
      handleRemoveSubject(curriculumName, levelName, originalName)
      setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
      return
    }

    const effectiveSelections = getEffectiveSubjectSelectionsByLevel()
    const rowSubjects = effectiveSelections[key] ?? []
    const duplicateExists = rowSubjects.some(
      (subjectName) =>
        subjectName.toLowerCase() === nextName.toLowerCase() &&
        subjectName.toLowerCase() !== originalName.toLowerCase()
    )
    if (duplicateExists) {
      setSubjectErrorByLevel((prev) => ({
        ...prev,
        [key]: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.DUPLICATE_ERROR'}),
      }))
      return
    }

    const nextSelections = {
      ...effectiveSelections,
      [key]: rowSubjects.map((subjectName) =>
        subjectName === originalName ? nextName : subjectName
      ),
    }
    const nextSubjects = flattenSubjectsFromSelections(nextSelections)

    setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
    setLocalData((prev) => {
      const nextLocal = {
        ...prev,
        subjectSelectionsByLevel: nextSelections,
        subjects: nextSubjects,
        subjectsCount: nextSubjects.length,
      }
      deferUpdateData({
        manualData: {
          ...nextLocal,
          manualActiveTab: 'subjects',
        },
      })
      return nextLocal
    })
    setEditingSubjectByLevel((prev) => ({...prev, [key]: null}))
  }

  const handleSubjectKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    curriculumName: string,
    levelName: string
  ) => {
    const key = getSubjectLevelKey(curriculumName, levelName)
    if (e.key === 'Enter') {
      e.preventDefault()
      // Avoid double-add: Enter adds in onKeyDown, but the input blur handler
      // can also run and re-add / cause flicker.
      skipSubjectInputBlurRef.current = true
      handleAddSubject(curriculumName, levelName)
    }
    if (e.key === 'Backspace' && !(subjectInputByLevel[key] || '').trim()) {
      const rowSubjects = getSubjectsForCurriculumLevel(curriculumName, levelName)
      if (rowSubjects.length > 0) {
        e.preventDefault()
        handleRemoveSubject(curriculumName, levelName, rowSubjects[rowSubjects.length - 1])
      }
    }
  }

  const handleDataChange = (category: string, index: number, field: string, value: any) => {
    const itemsKey = `${category}` as keyof typeof localData
    const newItems = [...(localData[itemsKey] as any[])]

    const normalizeStudentSubject = (raw: any, fallbackClass = '') => {
      if (raw && typeof raw === 'object') {
        const encoded = String(raw.value ?? '').trim()
        if (encoded.includes(STUDENT_SUBJECT_DELIMITER)) {
          const [namePart, classPart] = encoded.split(STUDENT_SUBJECT_DELIMITER)
          const name = String(namePart ?? '').trim()
          const cls = String(classPart ?? fallbackClass).trim()
          const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
          return {
            name,
            class: cls,
            teacher: raw.teacher ?? definedSubject?.teacher,
          }
        }

        const name = String(raw.name ?? raw.subjectName ?? raw.label ?? '').trim()
        const cls = String(raw.class ?? raw.className ?? fallbackClass).trim()
        const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
        return {
          name,
          class: cls,
          teacher: raw.teacher ?? definedSubject?.teacher,
        }
      }

      const rawStr = String(raw ?? '').trim()
      if (!rawStr) return {name: '', class: fallbackClass, teacher: undefined}

      if (rawStr.includes(STUDENT_SUBJECT_DELIMITER)) {
        const [namePart, classPart] = rawStr.split(STUDENT_SUBJECT_DELIMITER)
        const name = String(namePart ?? '').trim()
        const cls = String(classPart ?? fallbackClass).trim()
        const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
        return {name, class: cls, teacher: definedSubject?.teacher}
      }

      if (data.mode === 'subject' && rawStr.includes('-')) {
        const lastDash = rawStr.lastIndexOf('-')
        const name = rawStr.slice(0, lastDash).trim()
        const cls = rawStr.slice(lastDash + 1).trim() || fallbackClass
        const definedSubject = (localData.subjects as any[]).find((s) => s.name === name)
        return {name, class: cls, teacher: definedSubject?.teacher}
      }

      const definedSubject = (localData.subjects as any[]).find((s) => s.name === rawStr)
      return {
        name: rawStr,
        class: String(definedSubject?.class ?? fallbackClass).trim(),
        teacher: definedSubject?.teacher,
      }
    }

    if (category === 'students' && field === 'subjects') {
      const subjectStrings = Array.isArray(value)
        ? value
        : value.split(',').map((s: string) => s.trim())
      const currentClass = String(newItems[index]?.class ?? '').trim()
      let normalizedSubjects = subjectStrings
        .map((raw: any) => normalizeStudentSubject(raw, currentClass))
        .filter((entry: any) => entry.name)

      // A row class that doesn't resolve to any known class (e.g. an uploaded file
      // whose class names don't exist in this school) must NOT veto the selection —
      // treat it like the no-class case below and adopt the picked subject's class.
      const knownClassKeys = new Set(
        ((localData.classes as any[]) || [])
          .map((c: any) => normalizeClassMatchKey(c?.name))
          .filter(Boolean)
      )
      const currentClassKey = normalizeClassMatchKey(currentClass)
      const classIsKnown = Boolean(currentClassKey) && knownClassKeys.has(currentClassKey)

      if (currentClass && classIsKnown) {
        // Tolerant compare: a file-imported row's class spelling may differ from the
        // wizard's ("Class 1A" vs "Class 1-A") while still being the same class.
        normalizedSubjects = normalizedSubjects
          .filter(
            (entry: any) => !entry.class || normalizeClassMatchKey(entry.class) === currentClassKey
          )
          .map((entry: any) => ({...entry, class: currentClass}))
      } else if (normalizedSubjects.length > 0) {
        const firstClass = String(normalizedSubjects[0]?.class ?? '').trim()
        if (firstClass) {
          normalizedSubjects = normalizedSubjects
            .filter((entry: any) => String(entry.class ?? '').trim() === firstClass)
            .map((entry: any) => ({...entry, class: firstClass}))
          newItems[index] = {
            ...newItems[index],
            class: firstClass,
          }
        }
      }

      newItems[index] = {
        ...newItems[index],
        [field]: normalizedSubjects,
      }
    } else if (category === 'students' && field === 'class') {
      // A class name two curricula share comes as "name + curriculum" (studentClassOptions).
      const [pickedClass, pickedCurriculum] = String(value ?? '').split(CLASS_CURRICULUM_SEP)
      const selectedClass = String(pickedClass ?? '').trim()
      const existingSubjects = Array.isArray(newItems[index]?.subjects)
        ? newItems[index].subjects
        : []
      const normalizedSubjects = existingSubjects
        .map((raw: any) => normalizeStudentSubject(raw, selectedClass))
        .filter((entry: any) => entry.name)

      const nextSubjects = selectedClass
        ? normalizedSubjects
            .filter((entry: any) => !entry.class || entry.class === selectedClass)
            .map((entry: any) => ({...entry, class: selectedClass}))
        : normalizedSubjects

      // The curriculum belongs to the picked class: the one picked with a shared name, else
      // none (the payload resolves it from the class). A stale one would misplace the student.
      const {curriculum: _previousCurriculum, ...rest} = newItems[index] ?? {}
      newItems[index] = {
        ...rest,
        ...(pickedCurriculum?.trim() ? {curriculum: pickedCurriculum.trim()} : {}),
        class: selectedClass,
        subjects: nextSubjects,
      }
    } else if (category === 'classes' && (field === 'grade' || field === 'section')) {
      const updated = {...newItems[index], [field]: value}
      const g = (updated.grade || '').toString().trim()
      const s = (updated.section || '').toString().trim()
      updated.name = g && s ? `${g}-${s}` : `${g}${s}`
      newItems[index] = updated
    } else {
      newItems[index] = {...newItems[index], [field]: value}
    }
    setLocalData((prev) => {
      const next = {...prev, [itemsKey]: newItems}
      deferUpdateData({manualData: next})
      return next
    })
  }

  const [focusNewRowAt, setFocusNewRowAt] = useState<{category: string; index: number} | null>(null)
  useEffect(() => {
    if (!focusNewRowAt) return
    const {category, index} = focusNewRowAt
    const id = `step4-row-first-input-${category}-${index}`
    const timer = setTimeout(() => {
      document.getElementById(id)?.focus()
      setFocusNewRowAt(null)
    }, 0)
    return () => clearTimeout(timer)
  }, [focusNewRowAt])

  const handleAddRow = (category: string, requestFocusOnNew = false) => {
    // if (category === 'teachers' && !freemiumTeacherLimit.canAddTeacher) {
    //   toast.info(freemiumTeacherLimit.message)
    //   return
    // }

    const itemsKey = `${category}` as keyof typeof localData
    const countKey = `${category}Count` as keyof typeof localData
    const existingItems = (localData[itemsKey] as any[]) || []
    const count = (localData[countKey] as number) || 0
    const char = String.fromCharCode(65 + (count % 26))
    const num = count + 1

    let newItem: any = {}
    if (category === 'teachers') {
      newItem = {
        first_name: '',
        last_name: '',
        employeeId: '',
        level: '',
        email: '',
        cnic: '',
      }
    } else if (category === 'classes') {
      newItem = {
        branch: '',
        grade: '',
        section: '',
        teacher: '',
        name: '',
        level: '',
      }
    } else if (category === 'subjects') {
      newItem = {
        name: `Subject ${char}`,
        teacher: `Teacher ${char}`,
        class: 'Class 1A',
        frequency: '1/day',
        duration: '1',
      }
    } else if (category === 'students') {
      newItem = {
        full_name: '',
        class: '',
        registrationNumber: `REG${num}`,
        subjects: [],
      }
    } else if (category === 'lessonPlanning') {
      newItem = {}
    }

    const newItems = [...existingItems, newItem]
    const newIndex = existingItems.length
    setLocalData((prev) => {
      const next = {
        ...prev,
        [countKey]: newItems.length,
        [itemsKey]: newItems,
      }
      deferUpdateData({manualData: next})
      return next
    })
    if (requestFocusOnNew && (category === 'teachers' || category === 'students')) {
      setFocusNewRowAt({category, index: newIndex})
    }
  }

  // --- Lesson Planning Excel import (same APIs as the Excel Import module) -----------
  const excelImportInputRef = useRef<HTMLInputElement | null>(null)
  const [excelImportFile, setExcelImportFile] = useState<File | null>(null)
  const [showExcelImportModal, setShowExcelImportModal] = useState(false)
  const [downloadingExcelTemplate, setDownloadingExcelTemplate] = useState(false)

  /**
   * "Download template" on Lesson Planning: `GET onboarding/lesson-planning-template`. The
   * backend builds the workbook (teacher / class / subject dropdowns) from what is SAVED, so
   * unsynced class/subject edits are saved first, the same sync Next runs (unchanged sections
   * are skipped) — otherwise the file came back with teachers but no subjects. Plain function,
   * like the students template handler: saveProgress is declared further down.
   */
  const handleDownloadExcelTemplate = () => {
    if (downloadingExcelTemplate) return
    setDownloadingExcelTemplate(true)
    const release = () => setDownloadingExcelTemplate(false)

    const requestTemplate = () => {
      dispatch(
        downloadLessonPlanningTemplate(
          (res: any) => {
            release()
            // The object's Content-Disposition header makes the browser save it under the
            // server's file name.
            const url = res?.result?.url
            if (url) {
              const a = document.createElement('a')
              a.href = url
              a.download = ''
              a.rel = 'noopener noreferrer'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
          },
          () => {
            release()
            toast.error(intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
          }
        ) as never
      )
    }

    if (onPartialSubmit) {
      // Only classes and subjects can be missing server-side here; the checkpoint's other
      // sections (duration, timings, teachers, ...) are not compared, so their snapshots
      // drifting cannot turn a download into a bulk-import.
      onPartialSubmit(
        ['classes', 'subjects'],
        saveProgress(activeTab),
        requestTemplate,
        {sectionsOnly: ['classes', 'subjects']},
        release
      )
    } else {
      requestTemplate()
    }
  }

  // On the AI servers `csv-import/upload` also takes a PDF, Word file, photo or scan and
  // converts it into the Teacher Allocation template first. Elsewhere: spreadsheets only.
  const allocationConversionAvailable = isDataUploadConversionAvailable()
  const allocationUploadConfig = useDataUploadConfig('allocation', allocationConversionAvailable)

  const handleExcelImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const file = input.files?.[0] ?? null
    input.value = ''
    if (!file) return
    // Legacy .xls keeps working as a plain spreadsheet upload; everything else is checked
    // against the data-upload limits before it is sent (an oversized file fails with an HTML
    // 413 that cannot be read).
    if (allocationConversionAvailable && dataUploadFileExtension(file.name) !== 'xls') {
      const refusal = validateDataUploadFile(file, allocationUploadConfig)
      if (refusal) {
        toast.error(
          dataUploadErrorMessage(intl.formatMessage, refusal, {
            config: allocationUploadConfig,
            useCaseKey: 'allocation',
            fileName: file.name,
          })
        )
        return
      }
    }
    setExcelImportFile(file)
    setExcelImportFromPrefill(false)
    setAllocationPrefillReport(null)
    setShowExcelImportModal(true)
  }

  /** The import modal was opened with the Teachers file's allocations, not a picked file. */
  const [excelImportFromPrefill, setExcelImportFromPrefill] = useState(false)
  /** A prefill whose rows all waited on something (teachers not saved yet): says why. */
  const [allocationPrefillReport, setAllocationPrefillReport] =
    useState<DataUploadConversion | null>(null)

  // The Teachers file may hold the allocations too (`onboarding/prefill`). Fetched when this
  // step opens: the template only fills rows whose teacher, class and subject the school has
  // by then. It goes through the import modal exactly as if the user had picked it.
  const {reading: allocationPrefillReading, percent: allocationPrefillPercent} =
    useOnboardingPrefillDraft({
      useCaseKey: 'allocation',
      active: activeTab === 'lessonPlanning',
      templateParams: {flavour: 'onboarding'},
      onDraft: ({conversion}) => {
        if (!conversion.template || conversion.report.written === 0) {
          setAllocationPrefillReport(conversion)
          return
        }
        setAllocationPrefillReport(null)
        setExcelImportFile(dataUploadTemplateToFile(conversion.template))
        setExcelImportFromPrefill(true)
        setShowExcelImportModal(true)
      },
    })

  // After the Excel-import refresh lands (isHydrating true -> false), reseed the
  // lesson-planning grid from the re-hydrated wizard data. The grid keeps its own state
  // and never re-reads the parent after mount, so the imported allocations would
  // otherwise stay invisible until a full reload.
  const pendingExcelReseedRef = useRef(false)
  const prevHydratingRef = useRef(isHydrating)
  useEffect(() => {
    const wasHydrating = prevHydratingRef.current
    prevHydratingRef.current = isHydrating
    if (!pendingExcelReseedRef.current) return
    if (wasHydrating && !isHydrating) {
      pendingExcelReseedRef.current = false
      setLessonPlanningData(((data.manualData.lessonPlanningData as any) || {}) as any)
    }
  }, [isHydrating, data.manualData])

  const handleExcelImported = useCallback(() => {
    trackAllocationEvent('excel_assignment')
    pendingExcelReseedRef.current = true
    onExcelImported?.()
  }, [onExcelImported, trackAllocationEvent])

  // Styled two-column template (Teacher Name, Employee ID (Optional)) with an instruction
  // block; see utils/teacherSampleTemplate.ts. Plain CSV of the same layout if Excel fails.
  const handleDownloadTeachersSampleCsv = useCallback(async () => {
    try {
      await downloadTeacherSampleTemplate()
    } catch {
      downloadTeacherSampleTemplateCsv()
    }
  }, [])

  const [downloadingStudentsTemplate, setDownloadingStudentsTemplate] = useState(false)

  // Hard-coded two-row sample: the fallback where the server has no students template
  // (non-AI servers answer 404).
  const handleDownloadStudentsSampleCsv = useCallback(async () => {
    const rows = [
      ['Registration Number', 'Full Name', 'Class', 'Subjects'],
      ['REG001', 'Example Student', 'Class 1A', 'English;Mathematics'],
      ['REG002', 'Another Student', 'Class 1A', 'English;Science'],
    ]
    try {
      await downloadRowsAsExcel(rows, 'onboarding-students-sample.xlsx')
    } catch {
      // Fall back to a plain CSV download if the Excel build fails
      const csv = `\uFEFF${rows
        .map((r) => r.map((c) => (c.includes(';') ? `"${c}"` : c)).join(','))
        .join('\n')}\n`
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'onboarding-students-sample.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    }
  }, [])

  /**
   * "Download template" on Add Student: `GET onboarding/students-template`, built from what is
   * SAVED (classes, their subjects, existing students). Unsynced class/subject edits are saved
   * first, the same sync Next runs (unchanged sections are skipped), so the file never shows
   * the previous classes. Plain function: saveProgress is declared further down.
   */
  const handleDownloadStudentsTemplate = () => {
    if (downloadingStudentsTemplate) return
    setDownloadingStudentsTemplate(true)
    const release = () => setDownloadingStudentsTemplate(false)

    const requestTemplate = () => {
      dispatch(
        downloadStudentsTemplate(STUDENTS_CURRICULUM_COLUMN_ENABLED, release, (err: any) => {
          release()
          const status = Number(err?.statusCode ?? err?.status)
          const message = typeof err?.message === 'string' ? err.message.trim() : ''
          if (status === 409 || /^add classes\b/i.test(message)) {
            // No classes/subjects saved yet; the message says what to add.
            toast.error(message || intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
          } else if (status === 404 || !status) {
            // Non-AI server: no template endpoint, keep the local sample.
            void handleDownloadStudentsSampleCsv()
          } else {
            toast.error(intl.formatMessage({id: 'GENERAL.SOMETHING_WENT_WRONG'}))
          }
        }) as never
      )
    }

    if (onPartialSubmit) {
      onPartialSubmit(
        ['classes', 'subjects'],
        saveProgress(activeTab),
        requestTemplate,
        undefined,
        release
      )
    } else {
      requestTemplate()
    }
  }

  /**
   * Checks uploaded rows against the wizard's own data before anything reaches the grid
   * (a paste into Excel skips the template's dropdown check): the class must be a wizard
   * class (exact or tolerant match, scoped to the row's branch/level when the label carried
   * them) and every subject one of that class's subjects. Valid rows come back resolved to
   * the wizard's class name and subject spelling; any failure yields one line per row.
   * Plain function: getSubjectsForClass is declared further down.
   */
  const resolveImportedStudents = (
    rows: StudentCsvRow[]
  ): {rows: StudentCsvRow[]; errorLines: string[]} => {
    const classes = (localData.classes as any[]) || []
    const lower = (value: any) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    const resolved: StudentCsvRow[] = []
    const errorLines: string[] = []

    rows.forEach((row) => {
      if (!row.class.trim()) {
        // No class in the file (or the server couldn't pick one): kept, and the grid asks for
        // it. Its subjects are picked after the class.
        resolved.push({...row, class: '', subjects: []})
        return
      }
      const values = {row: row.rowNumber, registrationNumber: row.registrationNumber}
      const exactMatches = classes.filter((c: any) => lower(c?.name) === lower(row.class))
      const matchKey = normalizeClassMatchKey(row.class)
      const byName =
        exactMatches.length > 0
          ? exactMatches
          : classes.filter((c: any) => normalizeClassMatchKey(c?.name) === matchKey)
      // A named level must match exactly; a plain name that exists under more than one level
      // (or branch) is refused here rather than checked against whichever class came first —
      // its subjects differ per level, so "first" rejected valid rows and passed invalid ones.
      const match = pickStudentClass(
        byName,
        {branch: row.branch, level: row.level, curriculum: row.curriculum},
        data.manualData?.curriculumShortCodes
      )
      const labelQualifier = [[row.level, row.curriculum].filter(Boolean).join(', '), row.branch]
        .filter(Boolean)
        .join(' - ')
      // Curriculum-column layout: show the Curriculum + Class cells as typed, never the
      // stored (prefixed) name the pair was resolved to.
      const label = row.typedClass
        ? row.typedCurriculum
          ? `${row.typedClass} (${row.typedCurriculum})`
          : row.typedClass
        : labelQualifier
          ? `${row.class} (${labelQualifier})`
          : row.class

      if (row.unresolved === 'ambiguous') {
        errorLines.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.AMBIGUOUS_CLASS'},
            {...values, className: label}
          )
        )
        return
      }
      if (match.kind === 'none') {
        errorLines.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ONBOARDING_ONLY_CLASS'},
            {...values, className: label}
          )
        )
        return
      }
      if (match.kind === 'ambiguous') {
        errorLines.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.AMBIGUOUS_CLASS'},
            {...values, className: label}
          )
        )
        return
      }
      const classRecord = match.classRecord

      const className = String(classRecord.name ?? '').trim()
      if (row.subjects.length === 0) {
        // Class-wise schools never required subjects here: such rows are dropped, as before.
        if (data.mode === 'subject') {
          errorLines.push(
            intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.MISSING_SUBJECTS'},
              values
            )
          )
        }
        return
      }

      const allowedSubjects = new Map(
        uniqueStrings(getSubjectsForClass(classRecord)).map((name) => [name.toLowerCase(), name])
      )
      const subjectNames = row.subjects.map((encoded) =>
        String(encoded.split(STUDENT_SUBJECT_DELIMITER)[0] ?? '').trim()
      )
      const unknownSubjects = subjectNames.filter(
        (name) => !allowedSubjects.has(name.toLowerCase())
      )
      if (unknownSubjects.length > 0) {
        errorLines.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ONBOARDING_ONLY_SUBJECT'},
            {
              ...values,
              className: row.typedClass ? label : className,
              count: unknownSubjects.length,
              subjects: unknownSubjects.map((name) => `"${name}"`).join(', '),
            }
          )
        )
        return
      }

      // The match is unique, so pin its branch, level and curriculum on the row: the payload
      // builder sends them, and the backend needs them when the class name exists more than once.
      resolved.push({
        ...row,
        class: className,
        branch: String(classRecord.branch ?? '').trim() || undefined,
        level: String(classRecord.level ?? '').trim() || undefined,
        curriculum: String(classRecord.curriculum ?? '').trim() || undefined,
        subjects: subjectNames.map(
          (name) =>
            `${allowedSubjects.get(name.toLowerCase())}${STUDENT_SUBJECT_DELIMITER}${className}`
        ),
      })
    })

    return {rows: resolved, errorLines}
  }

  /**
   * Rows of an uploaded students file -> wizard grid. `lists` is the template's very hidden
   * `Lists` sheet (absent for a CSV or a hand-made workbook). Returns false when nothing was
   * imported (the reason has been toasted). Plain function: curriculumPrefixIndex and
   * resolveImportedStudents' dependencies are declared further down.
   *
   * Rows with a blank class or registration number are kept: the grid highlights the blanks
   * and Next waits for them. `classChoices` (lower-cased student name -> class names) are the
   * server's candidates for a class it would not guess; the class dropdown lists them first.
   * Returns the registration numbers / row keys added, so a prefill can be removed again.
   */
  const applyStudentRows = (
    rows: string[][],
    lists: string[][] | undefined,
    options: {silent?: boolean; classChoices?: ReadonlyMap<string, string[]>} = {}
  ): string[] | false => {
    const classes = (localData.classes as any[]) || []
    const wizardClassNames = new Set(
      classes
        .map((c: any) =>
          String(c?.name ?? '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
    const listsIndex = buildStudentTemplateListsIndex(lists)
    const parsed = parseStudentsRows(rows, {
      wizardClassNames,
      allowMissingColumns: true,
      resolveCurriculumClass: (cells) =>
        resolveCurriculumColumnClass(
          cells,
          listsIndex,
          classes,
          (name, curriculum) => displayCurriculumName(curriculumPrefixIndex, name, curriculum),
          data.manualData?.curriculumShortCodes
        ),
    })
    if (parsed.error) {
      toast.error(
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_INVALID',
        })
      )
      return false
    }
    // Rows the save would refuse (a name in Urdu, a total row, a heading) are left out; say so.
    const leftOut = parsed.skipped.length
      ? dataUploadCountsSummary(
          intl.formatMessage,
          {
            read: parsed.rows.length + parsed.skipped.length,
            kept: parsed.rows.length,
            leftOut: parsed.skipped.reduce<Record<string, number>>((acc, row) => {
              const code = row.code ?? ''
              acc[code] = (acc[code] ?? 0) + 1
              return acc
            }, {}),
            needValues: {},
          },
          'students'
        )
      : null
    if (leftOut) toast.warning(leftOut, {autoClose: 10000})
    if (parsed.rows.length === 0) {
      if (!leftOut) {
        toast.error(
          intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_NO_ROWS',
          })
        )
      }
      return false
    }
    // Any class or subject not added in onboarding blocks the whole file: import nothing.
    const resolvedImport = resolveImportedStudents(parsed.rows)
    const {errorLines} = resolvedImport
    let importedRows = resolvedImport.rows
    if (errorLines.length > 0) {
      const lines = [
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.ONBOARDING_ONLY'}),
        ...errorLines.slice(0, 5),
      ]
      if (errorLines.length > 5) {
        lines.push(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.MORE_ROWS'},
            {count: errorLines.length - 5}
          )
        )
      }
      toast.error(<div style={{whiteSpace: 'pre-line'}}>{lines.join('\n')}</div>, {
        autoClose: 12000,
      })
      return false
    }
    if (importedRows.length === 0) {
      toast.error(
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_NO_ROWS',
        })
      )
      return false
    }
    // A converted or prefilled template lists every student saved in the school, including
    // the ones the user removed but whose delete hasn't been saved yet: don't add them back.
    const pendingDeletes = new Set(
      (((localData as any).deletedStudents as any[]) ?? []).map(studentRowKey)
    )
    if (pendingDeletes.size > 0) {
      importedRows = importedRows.filter((row) => !pendingDeletes.has(studentRowKey(row)))
      if (importedRows.length === 0) return []
    }

    setLocalData((prev) => {
      // Merge by registration number: the template pre-fills saved students, so appending
      // would duplicate them on a re-upload. A listed student's class, subjects, branch and
      // level become what the row says (as on the backend); id and other fields are kept.
      // Students left out of the file are not removed.
      const nextStudents = [...((prev.students as any[]) || [])]
      const indexByReg = new Map<string, number>()
      nextStudents.forEach((student: any, index) => {
        const key = String(student?.registrationNumber ?? '')
          .trim()
          .toLowerCase()
        if (key && !indexByReg.has(key)) indexByReg.set(key, index)
      })
      importedRows.forEach((imported) => {
        // Upload-only fields (row number, the cells as typed) never reach wizard state.
        const row: Record<string, any> = {...imported}
        STUDENT_ROW_TRANSIENT_FIELDS.forEach((field) => delete row[field])
        const choices = options.classChoices?.get(imported.full_name.trim().toLowerCase())
        if (choices && choices.length > 0) row._classChoices = choices
        const key = imported.registrationNumber.trim().toLowerCase()
        // No registration number yet: always a new row (the grid asks for it).
        if (!key) {
          nextStudents.push(row)
          return
        }
        const index = indexByReg.get(key)
        if (index === undefined) {
          indexByReg.set(key, nextStudents.length)
          nextStudents.push(row)
          return
        }
        // first/last name would win over full_name in the payload; the file's name wins.
        nextStudents[index] = {
          ...nextStudents[index],
          ...row,
          first_name: undefined,
          last_name: undefined,
        }
      })
      const next = {
        ...prev,
        students: nextStudents,
        studentsCount: nextStudents.length,
      }
      updateData({manualData: next})
      return next
    })
    if (!options.silent) {
      toast.success(
        intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_IMPORTED'},
          {count: importedRows.length}
        )
      )
    }
    // Only rows that were not already in the grid: removing a prefill must never take a
    // student the school had before.
    const existingKeys = new Set(((localData.students as any[]) || []).map(studentRowKey))
    return importedRows.map(studentRowKey).filter((key) => !existingKeys.has(key))
  }

  /** First sheet and `Lists` sheet of a students workbook. */
  const readStudentsWorkbook = (file: File) =>
    excelFileToNamedSheets(file, [STUDENTS_TEMPLATE_LISTS_SHEET]).then((workbook) => ({
      rows: workbook.rows,
      lists: workbook.sheets[STUDENTS_TEMPLATE_LISTS_SHEET] as string[][] | undefined,
      isOurTemplate:
        workbook.firstSheetName.trim().toLowerCase() === STUDENTS_TEMPLATE_SHEET.toLowerCase(),
    }))

  /**
   * A file that is not our template (a PDF, Word file, photo or scan, or a spreadsheet in the
   * school's own layout): `POST data-upload/convert` reads it and returns OUR Students
   * template, filled in, which then goes through the same parser as a hand-filled one. The
   * workbook holds the saved students plus the new ones, so the all-or-nothing student sync
   * never reads "everyone else has left". Nothing is saved here: the user sees the report
   * first and the existing Next does the save.
   *
   * The template only offers classes and subjects that are SAVED, so unsynced class/subject
   * edits are saved first, like the template download. Plain function: saveProgress is
   * declared further down.
   */
  const convertStudentsFile = (file: File, done: () => void) => {
    setStudentsReadingFile(true)
    const finish = () => {
      setStudentsReadingFile(false)
      done()
    }
    const fail = (error: unknown) => {
      toast.error(
        dataUploadErrorMessage(intl.formatMessage, error, {
          config: studentsUploadConfig,
          useCaseKey: 'students',
          fileName: file.name,
        }),
        {autoClose: 10000}
      )
      finish()
    }

    const convert = (uploadId: string | null) =>
      dispatchApi(dispatch, (ok, onFailure) =>
        convertDataUpload(
          buildDataUploadConvertForm([file], {
            useCaseKey: 'students',
            flavour: 'onboarding',
            curriculumColumn: STUDENTS_CURRICULUM_COLUMN_ENABLED,
            uploadId,
          }),
          ok,
          onFailure
        )
      )

    const requestConversion = () => {
      // On the AI servers the file goes in 512 KB pieces first (nginx refuses bodies > 1 MB).
      const converted = isChunkedUploadAvailable()
        ? withUploadedFile(
            dispatch,
            file,
            {useCaseKey: 'students', onProgress: setStudentsUploadProgress},
            (uploadId) => {
              setStudentsUploadProgress(null)
              return convert(uploadId)
            }
          )
        : convert(null)
      converted
        .then((res: any) => {
          setStudentsUploadProgress(null)
          const conversion = normalizeDataUploadConversion(res?.result ?? res)
          if (!conversion?.template) {
            fail((conversion && firstDataUploadFileError(conversion)?.code) || 'NO_RELEVANT_DATA')
            return
          }
          return readStudentsWorkbook(dataUploadTemplateToFile(conversion.template))
            .then(({rows, lists}) => {
              applyStudentRows(rows, lists, {
                silent: true,
                classChoices: studentClassChoices(conversion),
              })
              // Shown even when the rows were refused: it lists what was left out and
              // carries the filled template, which the user can fix and upload again.
              setStudentsConversion(conversion)
              finish()
            })
            .catch(() => fail('PARSE_FAILED'))
        })
        .catch((error) => {
          setStudentsUploadProgress(null)
          fail(error)
        })
    }

    if (onPartialSubmit) {
      onPartialSubmit(
        ['classes', 'subjects'],
        saveProgress(activeTab),
        requestConversion,
        undefined,
        finish
      )
    } else {
      requestConversion()
    }
  }

  const handleStudentsCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const file = input.files?.[0]
    input.value = ''
    if (!file) return

    const extension = dataUploadFileExtension(file.name)
    const isSpreadsheet = extension === 'xlsx' || extension === 'csv'
    if (studentsConversionAvailable) {
      // Enforced before sending: an oversized file fails with an HTML 413 that can't be read.
      const refusal = validateDataUploadFile(file, studentsUploadConfig)
      if (refusal) {
        toast.error(
          dataUploadErrorMessage(intl.formatMessage, refusal, {
            config: studentsUploadConfig,
            useCaseKey: 'students',
            fileName: file.name,
          })
        )
        return
      }
    } else if (!isSpreadsheet) {
      toast.error(intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_INVALID'}))
      return
    }

    const onReadError = () => {
      toast.error(
        intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CSV_READ_ERROR',
        })
      )
    }

    setStudentsConversion(null)
    setStudentsPrefill(null)
    setIsImportingStudentsFile(true)
    const startedAt = Date.now()
    // Keep the table skeleton visible briefly so the import feedback is noticeable
    const finishImport = (apply?: () => void) => {
      const remaining = Math.max(0, 600 - (Date.now() - startedAt))
      window.setTimeout(() => {
        apply?.()
        setIsImportingStudentsFile(false)
      }, remaining)
    }

    /**
     * Our own template, or any sheet the parser recognises, keeps today's in-browser flow (no
     * AI). Only a sheet it cannot read is handed to the conversion, where that exists.
     */
    const importSheet = (rows: string[][], lists: string[][] | undefined, ours: boolean) => {
      const readable = !parseStudentsRows(rows).error
      if (ours || readable || !studentsConversionAvailable) {
        finishImport(() => applyStudentRows(rows, lists))
        return
      }
      convertStudentsFile(file, () => finishImport())
    }

    if (extension === 'xlsx') {
      readStudentsWorkbook(file)
        .then(({rows, lists, isOurTemplate}) => importSheet(rows, lists, isOurTemplate))
        .catch(() => finishImport(onReadError))
      return
    }

    if (extension === 'csv') {
      const reader = new FileReader()
      reader.onload = () => importSheet(parseCsvRows(String(reader.result ?? '')), undefined, false)
      reader.onerror = () => finishImport(onReadError)
      reader.readAsText(file, 'UTF-8')
      return
    }

    convertStudentsFile(file, () => finishImport())
  }

  /** Students the Teachers file prefilled, so Remove takes back exactly those rows. */
  const [studentsPrefill, setStudentsPrefill] = useState<{
    draftId: string
    keys: string[]
    count: number
  } | null>(null)

  // The Teachers file may hold the students too (`onboarding/prefill`): read its draft when this
  // step opens and apply it like a converted upload. Nothing is saved until Next.
  const {reading: studentsPrefillReading, percent: studentsPrefillPercent} =
    useOnboardingPrefillDraft({
      useCaseKey: 'students',
      active: activeTab === 'students',
      templateParams: {flavour: 'onboarding', curriculumColumn: STUDENTS_CURRICULUM_COLUMN_ENABLED},
      onDraft: ({draftId, conversion}) => {
        if (!conversion.template) {
          setStudentsConversion(conversion)
          return
        }
        readStudentsWorkbook(dataUploadTemplateToFile(conversion.template))
          .then(({rows, lists}) => {
            const added = applyStudentRows(rows, lists, {
              silent: true,
              classChoices: studentClassChoices(conversion),
            })
            setStudentsConversion(conversion)
            if (added && added.length > 0) {
              setStudentsPrefill({draftId, keys: added, count: conversion.report.written})
            }
          })
          .catch(() => undefined)
      },
    })

  /** "Reading… 40%" bar for a prefill job; nothing until the job reports a percent. */
  const renderPrefillProgress = (percent: number | null) =>
    percent === null ? null : (
      <div className='mt-2 mx-auto' style={{maxWidth: 320, width: '100%'}}>
        <div
          className='progress'
          style={{height: 6}}
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className='progress-bar' style={{width: `${percent}%`}} />
        </div>
        <div className='mt-1' style={{fontSize: '0.82rem'}}>
          {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_PERCENT'}, {percent})}
        </div>
      </div>
    )

  /** The grid rows the Teachers file prefilled (what Remove takes back). */
  const studentsPrefillRows = () => {
    const keys = new Set(studentsPrefill?.keys ?? [])
    return ((localData.students as any[]) || []).filter((student: any) =>
      keys.has(studentRowKey(student))
    )
  }

  /**
   * Takes back the prefilled rows. Rows already saved (Next was pressed after the prefill) are
   * queued for `action:'delete'` on the next Students save; unsaved rows just go.
   */
  const removeStudentsPrefill = () => {
    if (!studentsPrefill) return
    const keys = new Set(studentsPrefill.keys)
    dispatch(
      deleteDataUploadDraft(
        studentsPrefill.draftId,
        () => {},
        () => {}
      ) as never
    )
    setLocalData((prev) => {
      const all = (prev.students as any[]) || []
      const removed = all.filter((student: any) => keys.has(studentRowKey(student)))
      const students = all.filter((student: any) => !keys.has(studentRowKey(student)))
      const next = queueStudentDeletes({...prev, students, studentsCount: students.length}, removed)
      updateData({manualData: next})
      return next
    })
    setStudentsPrefill(null)
    setStudentsConversion(null)
    toast.success(
      intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVED'})
    )
  }

  const isTeacherRowValid = (
    item: any,
    index: number,
    items: any[],
    nameToIndices: Record<string, number[]>
  ) => {
    const getFullName = (t: any) =>
      (t._nameInput !== undefined
        ? t._nameInput
        : `${t.first_name || ''} ${t.last_name || ''}`.trim()
      ).trim()
    const name = getFullName(item).toLowerCase()
    if (!name) return false
    const dupIndices = nameToIndices[name]
    if (dupIndices && dupIndices.length > 1) {
      const ids = dupIndices.map((i) => (items[i].employeeId || '').toString().trim())
      if (ids.some((id) => !id)) return false
      if (new Set(ids).size !== ids.length) return false
    }
    return true
  }

  /**
   * Whether a student row's class resolves to one of this school's classes (tolerant match,
   * same rule the class cell's "Class not found" highlight and the subjects dropdown use). A
   * file-imported class that matches nothing keeps its row from validating — its subjects
   * could never be shown or synced — so the Students Next stays disabled until it is fixed.
   */
  const isKnownStudentClass = (className: any): boolean => {
    const key = normalizeClassMatchKey(String(className ?? '').trim())
    if (!key) return false
    return ((localData.classes as any[]) || []).some(
      (c: any) => normalizeClassMatchKey(c?.name) === key
    )
  }

  /** Removing a saved student deletes it from the school: always confirmed, never skipped. */
  const alwaysConfirmDelete = (action: PendingDeleteAction | null) =>
    action?.type === 'students-prefill' ||
    (action?.type === 'category-row' &&
      action.category === 'students' &&
      isSavedStudent(((localData.students as any[]) || [])[action.index]))

  /** A required cell the uploaded file left blank (`_missing`, set by parseStudentsRows). */
  const isMissingFromFile = (row: any, field: StudentMissingField) =>
    Array.isArray(row?._missing) && row._missing.includes(field)

  const isStudentRowValid = (item: any) => {
    const fullName = (
      item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim()
    ).trim()
    return (
      fullName.length > 0 &&
      (item.class || '').toString().trim() !== '' &&
      isKnownStudentClass(item.class) &&
      (item.registrationNumber || '').toString().trim() !== '' &&
      Array.isArray(item.subjects) &&
      item.subjects.length > 0
    )
  }

  const handleRowEnterKey = (
    category: string,
    index: number,
    e: React.KeyboardEvent,
    isValid: boolean
  ) => {
    if (e.key !== 'Enter') return
    if (!isValid) return
    e.preventDefault()
    handleAddRow(category, true)
  }

  const saveProgress = useCallback(
    (newTab?: string, classesFlowStepOverride?: number) => {
      const nextSubjectSelectionsByLevel = getEffectiveSubjectSelectionsByLevel(
        localData.subjectSelectionsByLevel
      )
      const nextSubjects = flattenSubjectsFromSelections(nextSubjectSelectionsByLevel)
      const teachers = (localData.teachers as any[]) || []
      const classes = (localData.classes as any[]) || []
      const subjects = nextSubjects
      const students = (localData.students as any[]) || []
      const flowStepToPersist =
        typeof classesFlowStepOverride === 'number' ? classesFlowStepOverride : classesFlowStep

      // Merge the latest "Classes step" edits (prefill table) into manual state.
      // Otherwise navigation/refresh can overwrite changes because `localData` may lag behind.
      const levelsByCurriculumBase =
        (localData as any)?.levelsByCurriculum ?? (data.manualData as any)?.levelsByCurriculum ?? {}
      const nextLevelsByCurriculum = {...levelsByCurriculumBase}
      Object.entries(prefilledClassesByCurriculumLevel).forEach(([curriculumName, levelMap]) => {
        const existing = nextLevelsByCurriculum[curriculumName] ?? {}
        const nextLevelDetails: Record<string, {classes: PrefilledClassEntry[]}> = {
          ...(existing.levelDetails ?? {}),
        }
        Object.entries(levelMap).forEach(([levelName, entries]) => {
          nextLevelDetails[levelName] = {classes: entries}
        })
        nextLevelsByCurriculum[curriculumName] = {
          ...existing,
          levelDetails: nextLevelDetails,
        }
      })

      const nextManualData = {
        ...localData,
        teachersCount: teachers.length,
        classesCount: classes.length,
        subjectsCount: subjects.length,
        studentsCount: students.length,
        manualActiveTab: newTab || activeTab,
        subjectSelectionsByLevel: nextSubjectSelectionsByLevel,
        subjects,
        lessonPlanningData: lessonPlanningData,
        deletedLessonSlots: Array.from(deletedSlots),
        classesFlowStep: flowStepToPersist,
        levelsByCurriculum: nextLevelsByCurriculum,
      }
      deferUpdateData({
        manualData: {
          ...nextManualData,
        },
      })
      return nextManualData
    },
    [
      activeTab,
      classesFlowStep,
      data.manualData,
      deletedSlots,
      flattenSubjectsFromSelections,
      getEffectiveSubjectSelectionsByLevel,
      lessonPlanningData,
      localData,
      prefilledClassesByCurriculumLevel,
      deferUpdateData,
    ]
  )

  // Auto-persist edits so forward/back always shows latest data.
  // Debounced to avoid excessive re-renders / API sync triggers.
  const schedulePersist = useCallback(() => {
    if (persistTimerRef.current) {
      window.clearTimeout(persistTimerRef.current)
    }
    persistTimerRef.current = window.setTimeout(() => {
      // Persist based on current active tab (keeps manualActiveTab consistent too)
      saveProgress(activeTab)
    }, 350)
  }, [activeTab, saveProgress])

  useEffect(() => {
    if (!didMountPersistRef.current) {
      didMountPersistRef.current = true
      return
    }
    // Skip debounced auto-persist in Subjects (it persists immediately).
    if (activeTab === 'subjects') return
    // Skip debounced auto-persist in Step3 Curriculum Classes to avoid update loops; we persist on navigation instead.
    if (stepperOverride && activeTab === 'classes') return
    schedulePersist()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: schedule persist on relevant edits
  }, [
    activeTab,
    classesFlowStep,
    localData.teachers,
    localData.students,
    localData.subjectSelectionsByLevel,
    localData.subjects,
    localData.classes,
    localData.manualActiveTab,
    reviewRowsByBranch,
    prefilledClassesByCurriculumLevel,
    lessonPlanningData,
    deletedSlots,
  ])

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
    }
  }, [])

  const classOptions = Array.from(
    new Set(((localData.classes as any[]) || []).map((c: any) => c.name).filter(Boolean))
  )

  // Display-only: level and class names carry a curriculum prefix ("FBISE Class 1-A") so the
  // same name under two boards never collides on the server. The UI shows them without it;
  // state, lesson-planning keys and every payload keep the full name. Never write a
  // `displayName` result back into data.
  const curriculumPrefixIndex = useMemo(
    () =>
      buildCurriculumPrefixIndex(
        (localData.classes as any[]) || [],
        (localData.curriculums as string[] | undefined) ?? []
      ),
    [localData.classes, localData.curriculums]
  )
  // A level/class name two curricula share gets the curriculum's short code beside it
  // ("Grade 1-A (FBISE)"); a unique name stays bare. Display only, like the prefix strip.
  const sharedNameLabel = useMemo(
    () =>
      buildSharedNameLabeler(
        (localData.classes as any[]) || [],
        localData.curriculumShortCodes ?? data.manualData?.curriculumShortCodes,
        Object.entries(localData.levelsByCurriculum ?? {}).flatMap(([curriculum, cfg]) =>
          (cfg?.levels ?? []).map((level) => ({name: level?.name, curriculum}))
        )
      ),
    [
      localData.classes,
      localData.curriculumShortCodes,
      localData.levelsByCurriculum,
      data.manualData?.curriculumShortCodes,
    ]
  )
  const displayName = (name: unknown, curriculum?: unknown) =>
    sharedNameLabel(
      displayCurriculumName(curriculumPrefixIndex, name, curriculum),
      name,
      curriculum
    )
  /** Every stored class/level name whose display form differs → that display form. */
  const displayNamePairs = useMemo(
    () => buildDisplayNamePairs(curriculumPrefixIndex, (localData.classes as any[]) || []),
    [curriculumPrefixIndex, localData.classes]
  )
  /** Free text (AI chat, warnings) with prefixed class/level names shown without the prefix. */
  const displayFreeText = useCallback(
    (text: string) => stripCurriculumPrefixesInText(text, displayNamePairs),
    [displayNamePairs]
  )
  /** Curriculum of a class (by its stored name), for option labels that only have the name. */
  const curriculumOfClass = (name: unknown) =>
    ((localData.classes as any[]) || []).find(
      (c: any) => String(c?.name ?? '').trim() === String(name ?? '').trim()
    )?.curriculum
  /** Curriculum of a level (by its stored name), for level tabs and messages. */
  const curriculumOfLevel = (level: unknown) =>
    ((localData.classes as any[]) || []).find(
      (c: any) => String(c?.level ?? '').trim() === String(level ?? '').trim()
    )?.curriculum
  /**
   * Class options for a student row: one per class name, or one per curriculum when two
   * curricula share the name (value "name + curriculum", split again in handleDataChange).
   */
  const studentClassOptions = (() => {
    const curriculaByName = new Map<string, Set<string>>()
    ;((localData.classes as any[]) || []).forEach((c: any) => {
      const name = String(c?.name ?? '').trim()
      if (!name) return
      const set = curriculaByName.get(name) ?? new Set<string>()
      const curriculum = String(c?.curriculum ?? '').trim()
      if (curriculum) set.add(curriculum)
      curriculaByName.set(name, set)
    })
    return Array.from(curriculaByName.entries()).flatMap(([name, curricula]) =>
      curricula.size > 1
        ? Array.from(curricula).map((curriculum) => ({
            value: `${name}${CLASS_CURRICULUM_SEP}${curriculum}`,
            label: displayName(name, curriculum),
          }))
        : [{value: name, label: displayName(name, Array.from(curricula)[0])}]
    )
  })()
  /** The option a student row shows: its class, with its curriculum when the name is shared. */
  const studentClassValue = (row: any) => {
    const name = String(row?.class ?? '').trim()
    const curriculum = String(row?.curriculum ?? '').trim()
    const encoded = `${name}${CLASS_CURRICULUM_SEP}${curriculum}`
    return curriculum && studentClassOptions.some((opt) => opt.value === encoded) ? encoded : name
  }
  const subjectOptions = Array.from(
    new Set(((localData.subjects as any[]) || []).map((s: any) => s.name).filter(Boolean))
  )

  const classSubjectsByLevelAndName = useMemo(() => {
    const map: Record<string, string[]> = {}

    Object.values(prefilledClassesByCurriculumLevel).forEach((levels) => {
      Object.entries(levels).forEach(([levelName, classes]) => {
        classes.forEach((entry) => {
          const className = (entry.name || '').toString().trim().toLowerCase()
          if (!className) return

          const subjects = uniqueStrings(entry.subjects || [])
          const levelKey = `${(levelName || '').toString().trim().toLowerCase()}|||${className}`

          map[levelKey] = uniqueStrings([...(map[levelKey] || []), ...subjects])
          map[className] = uniqueStrings([...(map[className] || []), ...subjects])
        })
      })
    })

    return map
  }, [prefilledClassesByCurriculumLevel])

  const getClassBaseName = useCallback((classItem: any) => {
    const gradeName = (classItem?.grade || '').toString().trim()
    if (gradeName) return gradeName

    const className = (classItem?.name || '').toString().trim()
    const sectionName = (classItem?.section || '').toString().trim()
    if (className && sectionName) {
      const suffix = `-${sectionName}`
      if (className.endsWith(suffix)) {
        return className.slice(0, -suffix.length).trim()
      }
    }

    return className
  }, [])

  const getSubjectsForClass = useCallback(
    (classItem: any): string[] => {
      const classKey = getLessonClassKey(
        classItem?.name || '',
        classItem?.branch,
        classItem?.curriculum
      )
      const dynamicSubjects = uniqueStrings(Object.keys(lessonPlanningData[classKey] || {})).filter(
        (sn) => !isSlotDeleted(classKey, sn)
      )

      if (Array.isArray(classItem?.subjects) && classItem.subjects.length > 0) {
        return uniqueStrings([
          ...classItem.subjects.map((s: any) => (typeof s === 'string' ? s : s?.name || '')),
          ...dynamicSubjects,
        ])
      }

      const curriculumName = (classItem?.curriculum || '').toString().trim()
      const levelName = (classItem?.level || '').toString().trim()
      const levelSubjects = curriculumName
        ? getSubjectsForCurriculumLevel(curriculumName, levelName)
        : []
      if (levelSubjects.length > 0) return uniqueStrings([...levelSubjects, ...dynamicSubjects])

      const baseName = getClassBaseName(classItem).toLowerCase()
      const normalizedLevelName = levelName.toLowerCase()

      if (!baseName) return uniqueStrings([...subjectOptions, ...dynamicSubjects])

      const scopedKey = `${normalizedLevelName}|||${baseName}`
      const scopedSubjects = classSubjectsByLevelAndName[scopedKey] || []
      if (scopedSubjects.length > 0) return uniqueStrings([...scopedSubjects, ...dynamicSubjects])

      const baseSubjects = classSubjectsByLevelAndName[baseName] || []
      if (baseSubjects.length > 0) return uniqueStrings([...baseSubjects, ...dynamicSubjects])

      return uniqueStrings([...subjectOptions, ...dynamicSubjects])
    },
    [
      classSubjectsByLevelAndName,
      getClassBaseName,
      getLessonClassKey,
      getSubjectsForCurriculumLevel,
      isSlotDeleted,
      lessonPlanningData,
      subjectOptions,
    ]
  )

  // Keep downstream Lesson Planning data consistent with Subjects selections.
  // When subjects for a curriculum/level are changed, remove any lesson-planning cells
  // (and deleted-slot markers) that refer to subjects that are no longer allowed.
  useEffect(() => {
    if (activeTab !== 'lessonPlanning') {
      // Still keep state consistent even if user isn't on lessonPlanning,
      // so coming back later shows correct data.
    }

    const classRecords = (localData.classes as any[]) || []
    if (classRecords.length === 0) return

    const allowedSubjectsByClassKey = new Map<string, Set<string>>()
    classRecords.forEach((classItem: any) => {
      const classKey = getLessonClassKey(
        classItem?.name || '',
        classItem?.branch,
        classItem?.curriculum
      )
      const curriculumName = (classItem?.curriculum || '').toString().trim()
      const levelName = (classItem?.level || '').toString().trim()

      // Preferred: subjects defined for this class row, else subjects defined for curriculum/level,
      // else fall back to current global subject options.
      const allowed = (() => {
        if (Array.isArray(classItem?.subjects) && classItem.subjects.length > 0) {
          return uniqueStrings(
            classItem.subjects.map((s: any) => (typeof s === 'string' ? s : s?.name || ''))
          )
        }
        const levelSubjects = curriculumName
          ? getSubjectsForCurriculumLevel(curriculumName, levelName)
          : []
        if (levelSubjects.length > 0) return uniqueStrings(levelSubjects)
        return uniqueStrings(subjectOptions)
      })()

      // Important: allow "user-added" subjects created from the Lesson Planning grid (+ cells).
      // These are stored in `lessonPlanningData[classKey]` and should not be wiped by the
      // consistency cleanup that reacts to Subjects tab changes.
      const dynamicSubjects = uniqueStrings(Object.keys(lessonPlanningData[classKey] || {}))

      allowedSubjectsByClassKey.set(
        classKey,
        new Set(uniqueStrings([...allowed, ...dynamicSubjects]))
      )
    })

    let didChangeLP = false
    const nextLessonPlanningData: typeof lessonPlanningData = {...lessonPlanningData}
    Object.entries(lessonPlanningData || {}).forEach(([classKey, subjectMap]) => {
      const allowed = allowedSubjectsByClassKey.get(classKey)
      if (!allowed) return
      const nextSubjectMap: any = {...(subjectMap || {})}
      Object.keys(nextSubjectMap).forEach((sn) => {
        if (!allowed.has(sn)) {
          delete nextSubjectMap[sn]
          didChangeLP = true
        }
      })
      nextLessonPlanningData[classKey] = nextSubjectMap
    })

    let didChangeDeleted = false
    const nextDeleted = new Set(deletedSlots)
    Array.from(nextDeleted).forEach((encoded) => {
      const parts = encoded.split('|||')
      if (parts.length < 2) return
      const classKey = parts[0]
      const subjectName = parts.slice(1).join('|||')
      const allowed = allowedSubjectsByClassKey.get(classKey)
      if (allowed && !allowed.has(subjectName)) {
        nextDeleted.delete(encoded)
        didChangeDeleted = true
      }
    })

    if (didChangeLP) {
      setLessonPlanningData(nextLessonPlanningData)
    }
    if (didChangeDeleted) {
      setDeletedSlots(nextDeleted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync based on subject selections/classes
  }, [
    localData.subjectSelectionsByLevel,
    localData.subjects,
    localData.classes,
    prefilledClassesByCurriculumLevel,
  ])

  // Keep Lesson Planning consistent with Teachers edits:
  // if a teacher is removed/renamed, clear assignments that no longer match.
  useEffect(() => {
    const teachers = (localData.teachers as any[]) || []
    const getFullName = (t: any) =>
      (t?._nameInput !== undefined
        ? t._nameInput
        : `${t?.first_name || ''} ${t?.last_name || ''}`.trim()
      ).trim()
    const validNames = new Set(teachers.map((t: any) => getFullName(t)).filter(Boolean))
    const validIds = new Set(
      teachers.map((t: any) => (t.employeeId || '').toString().trim()).filter(Boolean)
    )

    let changed = false
    const next: typeof lessonPlanningData = {...lessonPlanningData}
    Object.entries(lessonPlanningData || {}).forEach(([classKey, subjectMap]) => {
      const nextSubjectMap: any = {...(subjectMap || {})}
      Object.entries(nextSubjectMap).forEach(([sn, cell]: any) => {
        const name = (cell?.teacher || '').toString().trim()
        const eid = (cell?.teacherEmployeeId || '').toString().trim()
        if (!name) return
        const okName = validNames.has(name)
        const okId = eid ? validIds.has(eid) : true
        if (!okName || !okId) {
          nextSubjectMap[sn] = {...cell, teacher: '', teacherEmployeeId: ''}
          changed = true
        }
      })
      next[classKey] = nextSubjectMap
    })

    if (changed) setLessonPlanningData(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync teacher edits to lesson planning
  }, [localData.teachers])

  const studentsSubjectOptions = useMemo(() => {
    const optionsByClass: Record<string, string[]> = {}
    const classRecords = (localData.classes as any[]) || []

    classRecords.forEach((classItem: any) => {
      const className = String(classItem?.name ?? '').trim()
      if (!className) return
      const subjectsForClass = uniqueStrings(getSubjectsForClass(classItem))
      optionsByClass[className] = subjectsForClass
    })

    return optionsByClass
  }, [getSubjectsForClass, localData.classes])

  const getStudentSubjectSelectOptions = useCallback(
    (selectedClassName: string) => {
      const normalizedClass = String(selectedClassName ?? '').trim()

      if (normalizedClass) {
        // Exact match first, then a tolerant one (case/spacing/dash differences) so a
        // file-imported class spelling still resolves to its own class's subjects.
        const matchKey = normalizeClassMatchKey(normalizedClass)
        const matched = studentsSubjectOptions[normalizedClass]
          ? ([normalizedClass, studentsSubjectOptions[normalizedClass]] as const)
          : Object.entries(studentsSubjectOptions).find(
              ([className]) => normalizeClassMatchKey(className) === matchKey
            )
        if (matched) {
          const [className, subjects] = matched
          return subjects.map((subjectName) => ({
            value: `${subjectName}${STUDENT_SUBJECT_DELIMITER}${className}`,
            label: subjectName,
            subjectName,
            className,
          }))
        }
      }

      return Object.entries(studentsSubjectOptions).flatMap(([className, subjects]) =>
        subjects.map((subjectName) => ({
          value: `${subjectName}${STUDENT_SUBJECT_DELIMITER}${className}`,
          label: `${subjectName}-${displayName(className, curriculumOfClass(className))}`,
          subjectName,
          className,
        }))
      )
    },
    [studentsSubjectOptions]
  )

  const getValidatedSubjectsForClass = useCallback(
    (classItem: any): string[] => {
      const applicableFromCurriculum = getSubjectsForClass(classItem)
      const classKey = getLessonClassKey(
        classItem?.name || '',
        classItem?.branch,
        classItem?.curriculum
      )
      const dynamicActive = uniqueStrings(Object.keys(lessonPlanningData[classKey] || {})).filter(
        (sn) => !isSlotDeleted(classKey, sn)
      )

      const userSubjectsInClass = subjectOptions.filter((sn) =>
        applicableFromCurriculum.includes(sn)
      )
      return uniqueStrings([...userSubjectsInClass, ...dynamicActive])
    },
    [getSubjectsForClass, getLessonClassKey, isSlotDeleted, lessonPlanningData, subjectOptions]
  )

  const aiDefaultFrequencyStr = useMemo(() => {
    const defaultFreq = data.manualData?.defaultSubjectFrequency
    const defaultPer = data.manualData?.defaultFrequencyPer || 'week'
    return defaultFreq && defaultFreq >= 1 ? `${defaultFreq}/${defaultPer}` : '1/week'
  }, [data.manualData?.defaultSubjectFrequency, data.manualData?.defaultFrequencyPer])

  const aiActiveFilters = useMemo(
    () => ({
      branch: activeLessonBranch || branchOptions[0] || 'Main',
      level: activeLessonLevel,
      curriculum: activeLessonCurriculum,
    }),
    [activeLessonBranch, activeLessonLevel, activeLessonCurriculum, branchOptions]
  )

  // The "all branches" scope toggle only makes sense when the school actually
  // has more than one branch (from explicit options or from classes).
  const hasMultipleBranches = useMemo(() => {
    const classes = (localData.classes as any[]) || []
    const names = new Set(
      [
        ...branchOptions.map(normalizeBranchName),
        ...classes.map((c: any) => normalizeBranchName(c?.branch)),
      ].filter(Boolean)
    )
    return names.size > 1
  }, [branchOptions, localData.classes, normalizeBranchName])

  // Bumped to force the AI allocation chatbot window closed when leaving the
  // lesson-planning step via Next.
  const [aiWidgetCloseSignal, setAiWidgetCloseSignal] = useState(0)
  const allocationAi = useTeacherAllocationAi({
    manualDataRef,
    lessonPlanningData,
    setLessonPlanningData,
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    isSlotDeleted,
    activeFilters: aiActiveFilters,
    defaultFrequency: aiDefaultFrequencyStr,
    defaultDuration: '1',
    onApplied: () => {
      aiUsedSinceOpenRef.current = true
      trackAllocationEvent('ai_assignment')
    },
  })

  // With a single branch the persisted session may still carry an
  // "all_branches" scope — snap it back, the toggle is disabled in that case.
  useEffect(() => {
    if (!hasMultipleBranches && allocationAi.scope === 'all_branches') {
      allocationAi.setScope('active_filters')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setScope identity is stable
  }, [hasMultipleBranches, allocationAi.scope])

  // When a new AI proposal lands, bring its first highlighted (yellow) cell
  // into view — the targeted slots can be far down or off to the side of the
  // grid, and the user would otherwise not see what is being proposed.
  useEffect(() => {
    if (allocationAi.previewSlotKeys.size === 0) return
    const raf = requestAnimationFrame(() => {
      document
        .querySelector('.step4-ai-preview-cell')
        ?.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'})
    })
    return () => cancelAnimationFrame(raf)
  }, [allocationAi.previewSlotKeys])

  const prevActiveTabRef = useRef(activeTab)
  useEffect(() => {
    const prevTab = prevActiveTabRef.current
    prevActiveTabRef.current = activeTab

    // Only act on a genuine move *away* from lesson planning. This effect also runs on
    // mount, where activeTab is 'teachers' by default - resetting there would wipe the
    // session we just restored from sessionStorage.
    if (prevTab !== 'lessonPlanning' || activeTab === 'lessonPlanning') return

    // Drop the un-applied preview (its context goes stale once you leave), but keep the
    // conversation, baseline and undo stack - those are meant to survive coming back.
    allocationAi.cancelPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clear pending AI preview when leaving tab
  }, [activeTab])

  const hasSubjectsForLevelInBranch = useCallback(
    (branch: string, levelName: string): boolean => {
      const classes = (localData.classes as any[]) || []
      const classesInLevel = classes.filter(
        (c: any) =>
          normalizeBranchName(c?.branch) === branch &&
          (c.level || '').toString().trim() === levelName
      )

      if (classesInLevel.length === 0) return true

      for (const c of classesInLevel) {
        const applicableSubjects = getSubjectsForClass(c)
        const selectedSubjectsForClass = subjectOptions.filter((sn) =>
          applicableSubjects.includes(sn)
        )
        if (selectedSubjectsForClass.length > 0) {
          return true
        }
      }

      return false
    },
    [localData.classes, normalizeBranchName, getSubjectsForClass, subjectOptions]
  )

  const getFirstLevelWithoutSubjects = useCallback((): string | null => {
    const classes = (localData.classes as any[]) || []
    const branches = Array.from(
      new Set(classes.map((c: any) => normalizeBranchName(c?.branch)).filter(Boolean))
    )

    for (const branch of branches) {
      const classesInBranch = classes.filter((c: any) => normalizeBranchName(c?.branch) === branch)
      const levelsInBranch = Array.from(
        new Set(classesInBranch.map((c: any) => (c.level || '').toString().trim()).filter(Boolean))
      )
      for (const level of levelsInBranch) {
        if (!hasSubjectsForLevelInBranch(branch, level)) {
          return level
        }
      }
    }
    return null
  }, [localData.classes, normalizeBranchName, hasSubjectsForLevelInBranch])

  const isBranchLessonPlanningComplete = useCallback(
    (branch: string) => {
      const classes = (localData.classes as any[]) || []
      const classesInBranch = classes.filter((c: any) => normalizeBranchName(c?.branch) === branch)

      if (classesInBranch.length === 0) return true
      for (const c of classesInBranch) {
        const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
        if (!cn) continue
        const classSubjects = getValidatedSubjectsForClass(c)
        for (const sn of classSubjects) {
          if (!sn) continue
          if (isSlotDeleted(cn, sn)) continue
          const teacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
          if (!teacher.trim()) return false
        }
      }
      return true
    },
    [
      lessonPlanningData,
      localData.classes,
      normalizeBranchName,
      getLessonClassKey,
      isSlotDeleted,
      getValidatedSubjectsForClass,
    ]
  )

  const isLevelLessonPlanningComplete = useCallback(
    (branch: string, levelName: string) => {
      const classes = (localData.classes as any[]) || []
      const classesInLevel = classes.filter(
        (c: any) =>
          normalizeBranchName(c?.branch) === branch &&
          (c.level || '').toString().trim() === levelName
      )

      if (classesInLevel.length === 0) return true

      for (const c of classesInLevel) {
        const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
        if (!cn) continue
        // For level badge status, validate every subject visible for this class in LP grid.
        const classSubjects = getSubjectsForClass(c)
        for (const sn of classSubjects) {
          if (!sn) continue
          if (isSlotDeleted(cn, sn)) continue
          const teacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
          if (!teacher.trim()) return false
        }
      }

      return true
    },
    [
      getLessonClassKey,
      getSubjectsForClass,
      isSlotDeleted,
      lessonPlanningData,
      localData.classes,
      normalizeBranchName,
    ]
  )

  /**
   * How much of the Teacher Allocation grid is filled: class-subject slots (deleted ones left
   * out, as in validation) and how many of them have a teacher.
   */
  const getLessonPlanningFill = () => {
    let total = 0
    let filled = 0
    for (const c of (localData.classes as any[]) || []) {
      const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
      if (!cn) continue
      for (const sn of getValidatedSubjectsForClass(c)) {
        if (!sn || isSlotDeleted(cn, sn)) continue
        total += 1
        if ((lessonPlanningData[cn]?.[sn]?.teacher || '').trim()) filled += 1
      }
    }
    return {total, filled}
  }

  const isTabValid = (tabId: string) => {
    if (tabId === 'lessonPlanning') {
      const classes = (localData.classes as any[]) || []

      // First check: ensure subjects are selected for each level in each branch
      const branches = Array.from(
        new Set(classes.map((c: any) => normalizeBranchName(c?.branch)).filter(Boolean))
      )
      for (const branch of branches) {
        const classesInBranch = classes.filter(
          (c: any) => normalizeBranchName(c?.branch) === branch
        )
        const levelsInBranch = Array.from(
          new Set(
            classesInBranch.map((c: any) => (c.level || '').toString().trim()).filter(Boolean)
          )
        )
        for (const level of levelsInBranch) {
          if (!hasSubjectsForLevelInBranch(branch, level)) {
            return false // No subjects selected for this level
          }
        }
      }

      // Second check: ensure teachers are assigned to all subjects
      for (const c of classes) {
        const cn = getLessonClassKey(c.name, c.branch, c.curriculum)
        if (!cn) continue
        const classSubjects = getValidatedSubjectsForClass(c)
        for (const sn of classSubjects) {
          if (!sn) continue
          if (isSlotDeleted(cn, sn)) continue
          const teacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
          if (!teacher.trim()) return false
        }
      }
      return true
    }
    if (tabId === 'subjects') {
      const selections = getEffectiveSubjectSelectionsByLevel(localData.subjectSelectionsByLevel)
      return (
        subjectLevelRows.length > 0 &&
        subjectLevelRows.every(({key}) => (selections[key] ?? []).length > 0)
      )
    }
    const items = (localData[tabId as keyof typeof localData] as any[]) || []
    if (items.length === 0) return false
    if (tabId === 'teachers') {
      const getFullName = (t: any) =>
        (t._nameInput !== undefined
          ? t._nameInput
          : `${t.first_name || ''} ${t.last_name || ''}`.trim()
        ).trim()
      const hasName = (t: any) => getFullName(t).length > 0
      if (!items.every(hasName)) return false
      const nameToIndices: Record<string, number[]> = {}
      items.forEach((t: any, i: number) => {
        const name = getFullName(t).toLowerCase()
        if (name) {
          if (!nameToIndices[name]) nameToIndices[name] = []
          nameToIndices[name].push(i)
        }
      })
      const duplicateNames = Object.values(nameToIndices).filter((indices) => indices.length > 1)
      for (const indices of duplicateNames) {
        const ids = indices.map((i) => (items[i].employeeId || '').toString().trim())
        const missingId = ids.some((id) => !id)
        if (missingId) return false
        const uniqueIds = new Set(ids)
        if (uniqueIds.size !== ids.length) return false
      }
      return true
    }
    if (tabId === 'students') {
      // One rule for the row highlight, the Enter-key handler and the Next button: a row
      // with a class this school doesn't have (file import) blocks Next until it's fixed.
      return items.every(isStudentRowValid)
    }
    const excludeKeys: Record<string, string[]> = {
      teachers: ['employeeId'],
      classes: ['teacher', 'name', 'employeeId'],
      subjects: ['teacher', 'class', 'frequency', 'duration', 'employeeId'],
    }
    const keysToExclude = excludeKeys[tabId] || ['employeeId']
    return items.every((item: any) =>
      Object.entries(item)
        .filter(([k]) => !keysToExclude.includes(k))
        .every(
          ([, v]) =>
            v !== undefined &&
            v !== null &&
            (Array.isArray(v) ? v.length > 0 : v.toString().trim() !== '')
        )
    )
  }

  /**
   * Leave the current tab for the next one. `afterSave` runs once the tab's data is
   * confirmed saved (or at once when the tab has nothing to sync), right before the
   * switch — completion tracking hangs off it so a failed save, which keeps the user
   * on the tab, never reports the tab as done.
   */
  const handleNextTab = (afterSave?: () => void) => {
    const currentIdx = tabs.findIndex((t) => t.id === activeTab)
    if (currentIdx < tabs.length - 1) {
      const fieldsToSync: string[] = []
      if (activeTab === 'teachers') fieldsToSync.push('teachers')
      else if (activeTab === 'classes') fieldsToSync.push('classes')
      else if (activeTab === 'students') fieldsToSync.push('students')
      // Don't hit API when leaving subjects tab – only when lesson planning is complete
      else if (activeTab === 'lessonPlanning') fieldsToSync.push('lessonPlanning')

      const nextTabId = tabs[currentIdx + 1].id
      const nextClassesFlowStep = nextTabId === 'classes' ? 2 : classesFlowStep
      const nextManualData = saveProgress(nextTabId, nextClassesFlowStep)
      if (nextTabId === 'classes') setClassesFlowStep(2)
      if (fieldsToSync.length > 0 && onPartialSubmit) {
        setIsSubmitting(true)
        onPartialSubmit(
          fieldsToSync,
          nextManualData,
          () => {
            setIsSubmitting(false)
            afterSave?.()
            setActiveTab(nextTabId)
          },
          undefined,
          // Failed save: the sync already toasted; release the button so the user can retry.
          () => setIsSubmitting(false)
        )
      } else {
        afterSave?.()
        setActiveTab(nextTabId)
      }
    } else {
      // Important: persist current tab data (e.g. Subjects selections)
      // before leaving this step.
      saveProgress(activeTab)
      afterSave?.()
      onComplete()
    }
  }

  /** Same `afterSave` contract as handleNextTab, for the step's last tab. */
  const handleFinishStep = (afterSave?: () => void) => {
    if (activeTab !== 'lessonPlanning' && !isTabValid(activeTab)) return

    const nextManualData = saveProgress(activeTab)
    const releaseOnError = () => setIsSubmitting(false)
    if (activeTab === 'lessonPlanning' && onPartialSubmit) {
      setIsSubmitting(true)
      onPartialSubmit(
        ['subjects', 'lessonPlanning'],
        nextManualData,
        () => {
          setIsSubmitting(false)
          afterSave?.()
          onComplete()
        },
        undefined,
        releaseOnError
      )
      return
    }

    if (activeTab === 'students' && onPartialSubmit) {
      setIsSubmitting(true)
      onPartialSubmit(
        ['students'],
        nextManualData,
        () => {
          setIsSubmitting(false)
          afterSave?.()
          onComplete()
        },
        undefined,
        releaseOnError
      )
      return
    }

    if (activeTab === 'subjects' && onPartialSubmit) {
      setIsSubmitting(true)
      onPartialSubmit(
        ['subjects'],
        nextManualData,
        () => {
          setIsSubmitting(false)
          afterSave?.()
          onComplete()
        },
        undefined,
        releaseOnError
      )
      return
    }

    afterSave?.()
    onComplete()
  }

  const handleBackTab = () => {
    const currentIdx = tabs.findIndex((t) => t.id === activeTab)
    if (currentIdx > 0) {
      const prevTabId = tabs[currentIdx - 1].id
      // Re-entering Classes from a later tab lands on its last sub-step (Review).
      const prevClassesFlowStep = prevTabId === 'classes' ? 3 : classesFlowStep
      saveProgress(prevTabId, prevClassesFlowStep)
      if (prevTabId === 'classes') {
        setActiveTab('classes')
        goToClassesFlowStep(3)
        return
      }
      setActiveTab(prevTabId)
    } else {
      // Persist current tab data before leaving this step.
      saveProgress(activeTab)
      onBack?.()
    }
  }

  // Classes flow runs 2 (Classes) -> 1 (Sections) -> 3 (Review); back walks it in reverse.
  const handleClassesBack = () => {
    if (classesFlowStep === 3) {
      goToClassesFlowStep(1)
      return
    }
    if (classesFlowStep === 1) {
      goToClassesFlowStep(2)
      return
    }
    handleBackTab()
  }

  const showBackButton = tabs.findIndex((t) => t.id === activeTab) > 0 || !!onBack

  const isLastTab = activeTab === tabs[tabs.length - 1]?.id

  // Teachers sub-step: "entry" (type/paste names or upload an Excel file) then "review" — a
  // full-page state inside the same sub-step, never a third top sub-step. Both screens are
  // their own components; this only wires them to manualData and the wizard navigation.
  const renderTeachersUI = () => {
    const items = (localData.teachers as any[]) || []

    if (teachersPhase === 'entry') {
      return (
        <div className='step4-manual-content'>
          <Step4TeacherEntry
            isDark={isDark}
            existingCount={items.length}
            showBackButton={showBackButton}
            onBack={handleBackTab}
            onReview={handleTeacherEntryReview}
            onDownloadTemplate={handleDownloadTeachersSampleCsv}
          />
        </div>
      )
    }

    const requestDeleteTeacherRows = (indices: number[]) => {
      if (indices.length === 0) return
      const action: PendingDeleteAction = {type: 'teacher-rows', indices}
      const allEmpty = indices.every((i) =>
        ['first_name', 'last_name', 'employeeId', 'level', 'email', 'cnic', '_nameInput'].every(
          (field) => !String(items[i]?.[field] ?? '').trim()
        )
      )
      if (allEmpty || localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true') {
        performDeleteAction(action)
      } else {
        setPendingDeleteAction(action)
      }
    }

    return (
      <div className='step4-manual-content'>
        <Step4TeacherReview
          isDark={isDark}
          isHydrating={isHydrating}
          isSubmitting={isSubmitting}
          teachers={items}
          onTeachersChange={(nextTeachers) => {
            setLocalData((prev) => {
              const next = {...prev, teachers: nextTeachers, teachersCount: nextTeachers.length}
              deferUpdateData({manualData: next}, {subStep: 'review-teachers'})
              return next
            })
          }}
          onDeleteRows={requestDeleteTeacherRows}
          onBack={() => goToTeachersPhase('entry')}
          onContinue={() => {
            // The review's completion rows ride on the teachers bulk import's success,
            // so a failed save (user stays here, toast) reports nothing.
            if (activeTab === tabs[tabs.length - 1].id) handleFinishStep(onTeacherReviewConfirm)
            else handleNextTab(onTeacherReviewConfirm)
          }}
        />
      </div>
    )
  }

  const missingClassLevelsOnEntryScreen = useMemo(() => {
    const missing: Array<{curriculum: string; level: string}> = []

    Object.entries(selectedLevelsByCurriculum).forEach(([curriculumName, levels]) => {
      levels.forEach((levelName) => {
        const entries = prefilledClassesByCurriculumLevel[curriculumName]?.[levelName] ?? []
        const hasClass = entries.some((entry) => (entry.name || '').toString().trim().length > 0)
        if (!hasClass) {
          missing.push({curriculum: curriculumName, level: levelName})
        }
      })
    })

    return missing
  }, [prefilledClassesByCurriculumLevel, selectedLevelsByCurriculum])

  const setPrefilledEntries = (
    curriculumName: string,
    levelName: string,
    updater: (entries: PrefilledClassEntry[]) => PrefilledClassEntry[]
  ) => {
    setPrefilledClassesByCurriculumLevel((prev) => {
      const curr = prev[curriculumName] ?? {}
      const entries = curr[levelName] ?? []
      return {
        ...prev,
        [curriculumName]: {
          ...curr,
          [levelName]: updater(entries),
        },
      }
    })
  }

  const handleAddPrefilledClass = (curriculumName: string, levelName: string) => {
    const key = `${curriculumName}|||${levelName}`
    const inputValue = (classInputByLevel[key] || '').trim()
    if (!inputValue) return

    setPrefilledEntries(curriculumName, levelName, (entries) => {
      if (entries.some((entry) => entry.name.toLowerCase() === inputValue.toLowerCase())) {
        return entries
      }
      return [...entries, {name: inputValue, subjects: []}]
    })

    setClassInputByLevel((prev) => ({...prev, [key]: ''}))
  }

  const handleAddPrefilledClassWithValue = (
    curriculumName: string,
    levelName: string,
    rawValue: string
  ) => {
    const key = `${curriculumName}|||${levelName}`
    const trimmed = (rawValue || '').trim()
    if (!trimmed) return

    if ((classInputByLevel[key] || '') !== rawValue) {
      setClassInputByLevel((prev) => ({...prev, [key]: rawValue}))
      setTimeout(() => handleAddPrefilledClass(curriculumName, levelName), 0)
      return
    }

    handleAddPrefilledClass(curriculumName, levelName)
  }

  const goToClassesFlowStep = (targetFlowStep: number) => {
    if (targetFlowStep === classesFlowStep) return
    // User is now driving the flow; stop the hydration seeding effect from re-seeding a stale value.
    classesFlowNavigatedRef.current = true

    // Persist any typed class names even if user did not press Enter.
    // This keeps the "Classes" sub-step consistent when navigating away.
    if (classesFlowStep === 2 && targetFlowStep !== 2) {
      Object.entries(classInputByLevel).forEach(([key, value]) => {
        const [curriculumName, levelName] = key.split('|||')
        if (curriculumName && levelName && (value || '').trim()) {
          handleAddPrefilledClassWithValue(curriculumName, levelName, value)
        }
      })
    }

    if (targetFlowStep === 1) {
      setClassesFlowStep(1)
      updateData({
        manualData: {...data.manualData, classesFlowStep: 1},
      })
      saveProgress('classes')
      return
    }

    if (targetFlowStep === 3) {
      // Only reconcile the Review table from the prefill source once it has actually loaded.
      // On a fresh remount (e.g. pressing Back into this step from a later tab) prefill hasn't
      // initialized yet, so rebuilding here yields empty per-branch rows that clobber the
      // hydrated review table — the grid disappears. Leave the existing rows in place; the
      // recovery effect rebuilds once prefill finishes loading.
      if (classesPrefillInitializedRef.current) {
        const review = buildReviewRowsFromPrefill()
        setExpandedBranch(new Set(Object.keys(review)))
        setReviewRowsByBranch(review)
        syncManualDataFromReview(review)
      }
      setClassesFlowStep(3)
      updateData({
        manualData: {...data.manualData, classesFlowStep: 3},
      })
      saveProgress('classes')
      return
    }

    if (targetFlowStep === 2) {
      setClassesFlowStep(2)
      updateData({
        manualData: {...data.manualData, classesFlowStep: 2},
      })
      saveProgress('classes')
    }
  }

  const handleClassesNext = () => {
    if (classesFlowStep === 2) {
      goToClassesFlowStep(1)
      return
    }

    if (classesFlowStep === 1) {
      goToClassesFlowStep(3)
      return
    }

    if (!isTabValid('classes')) return
    // Ensure Review edits are persisted before leaving Classes tab so the
    // subsequent flow (Subjects / later Lesson Planning) sees the latest rows/sections.
    if (classesFlowStep === 3) {
      saveProgress('classes', 3)
      deferUpdateData({
        manualData: {
          ...data.manualData,
          classesReviewRowsByBranch: reviewRowsByBranch,
          classesFlowStep: 3,
        } as any,
      })
    }
    handleNextTab()
  }

  const updateReviewRows = useCallback(
    (updater: (rows: Record<string, ClassesReviewRow[]>) => Record<string, ClassesReviewRow[]>) => {
      // User is editing the review table; stop the hydration seeding effect from re-seeding.
      reviewRowsEditedRef.current = true
      setReviewRowsByBranch((prev) => {
        const next = updater(prev)
        syncManualDataFromReview(next)
        return next
      })
    },
    [syncManualDataFromReview]
  )

  const isClassesStepOneValid = (() => {
    const n = Number.parseInt(sectionCountInput, 10)
    return Number.isFinite(n) && n > 0 && n <= MAX_SECTIONS && !sectionCountError
  })()

  const isClassesReviewComplete = useMemo(() => {
    if (classesFlowStep !== 3) return true
    const branchRows = Object.values(reviewRowsByBranch)
    if (branchRows.length === 0) return false

    return branchRows.every((rows) => {
      if (!rows || rows.length === 0) return false
      return rows.every((row) => {
        return (
          row.curriculum.trim().length > 0 &&
          row.level.trim().length > 0 &&
          row.gradeName.trim().length > 0 &&
          Array.isArray(row.sections) &&
          row.sections.length > 0 &&
          row.sections.every((section) => section.trim().length > 0)
        )
      })
    })
  }, [classesFlowStep, reviewRowsByBranch])

  const isClassesCurrentStepValid =
    classesFlowStep === 2
      ? missingClassLevelsOnEntryScreen.length === 0
      : classesFlowStep === 1
        ? isClassesStepOneValid
        : isTabValid('classes') && isClassesReviewComplete

  const performDeleteAction = useCallback(
    (action: PendingDeleteAction) => {
      const blurActiveElement = () => {
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
      }

      if (action.type === 'category-row') {
        const itemsKey = `${action.category}` as keyof typeof localData
        const countKey = `${action.category}Count` as keyof typeof localData
        // Blur BEFORE splicing. A row input is usually still focused when the
        // trash icon is clicked (its onMouseDown preventDefault keeps focus),
        // and blurring fires that input's onBlur commit, which writes back to
        // its captured rowIndex. Blurring after the splice made that commit
        // land on the post-splice array — resurrecting the deleted row (or
        // overwriting the next one), so the row only really went away on the
        // second delete. Blur first, and splice via the functional updater so
        // it runs on top of the blur-committed state.
        blurActiveElement()
        setLocalData((prev) => {
          const existingItems = [...(prev[itemsKey] as any[])]
          const [removed] = existingItems.splice(action.index, 1)
          const base = {
            ...prev,
            [countKey]: existingItems.length,
            [itemsKey]: existingItems,
          }
          // A saved student must be deleted on the server too (next Students save).
          const next = action.category === 'students' ? queueStudentDeletes(base, [removed]) : base
          deferUpdateData({manualData: next})
          return next
        })
        return
      }
      if (action.type === 'teacher-rows') {
        // Same blur-before-splice rule as category-row (see above).
        blurActiveElement()
        const drop = new Set(action.indices)
        setLocalData((prev) => {
          const teachers = ((prev.teachers as any[]) || []).filter((_, i) => !drop.has(i))
          const next = {...prev, teachers, teachersCount: teachers.length}
          // Rows are only deleted from the Review Teachers table.
          deferUpdateData({manualData: next}, {subStep: 'review-teachers'})
          return next
        })
        return
      }
      // Handled by handleConfirmDeleteAction (it needs the live prefill state).
      if (action.type === 'students-prefill') return
      // Record the removed classes (one per section) so the next bulk-import can send them with
      // action:'delete'. Without this the row just disappears from the payload and the backend keeps
      // it, so it reappears on resume. Written onto localData first so the review-sync below (whose
      // deferUpdateData spreads localData) carries deletedClasses to the parent in the same pass.
      const target = (reviewRowsByBranch[action.branchName] ?? [])[action.rowIdx]
      if (target) {
        const sectionNames = (target.sections ?? []).map((s) => (s || '').trim()).filter(Boolean)
        const sections = sectionNames.length > 0 ? sectionNames : ['A']
        const newlyDeleted = sections.map((section) => ({
          grade: target.gradeName,
          section,
          name: `${target.gradeName}-${section}`,
          branch: action.branchName,
          level: target.level,
          curriculum: target.curriculum,
        }))
        setLocalData((prev) => {
          const existing = ((prev as any).deletedClasses as any[]) ?? []
          const seen = new Set(existing.map((c: any) => `${c?.name}|||${c?.branch}|||${c?.level}`))
          const merged = [...existing]
          newlyDeleted.forEach((c) => {
            const key = `${c.name}|||${c.branch}|||${c.level}`
            if (!seen.has(key)) {
              seen.add(key)
              merged.push(c)
            }
          })
          return {...prev, deletedClasses: merged} as any
        })
      }
      updateReviewRows((prev) => {
        const next = {...prev}
        const list = [...(next[action.branchName] ?? [])]
        list.splice(action.rowIdx, 1)
        next[action.branchName] = list
        return next
      })
      blurActiveElement()
    },
    [reviewRowsByBranch, updateReviewRows, deferUpdateData]
  )

  const handleConfirmDeleteAction = () => {
    if (!pendingDeleteAction) return
    if (pendingDeleteAction.type === 'students-prefill') {
      removeStudentsPrefill()
      setPendingDeleteAction(null)
      return
    }
    if (donAskAgainDeleteRow && !alwaysConfirmDelete(pendingDeleteAction)) {
      localStorage.setItem(ROW_DELETE_SKIP_CONFIRM_KEY, 'true')
    }
    performDeleteAction(pendingDeleteAction)
    setPendingDeleteAction(null)
    setDonAskAgainDeleteRow(false)
  }

  const renderClassesUI = () => {
    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const bodyBg = isDark ? '#11141C' : '#ffffff'
    const classLevelGroups = Object.entries(prefilledClassesByCurriculumLevel)

    const showCurriculumColumn = (() => {
      const gradeNameBranches: Record<string, Set<string>> = {}
      Object.entries(reviewRowsByBranch).forEach(([branchName, branchRows]) => {
        branchRows.forEach((r) => {
          const key = r.gradeName.trim().toLowerCase()
          if (!key) return
          if (!gradeNameBranches[key]) gradeNameBranches[key] = new Set()
          gradeNameBranches[key].add(branchName)
        })
      })
      return Object.values(gradeNameBranches).some((branches) => branches.size > 1)
    })()

    return (
      <div className='step4-manual-content'>
        {classesFlowStep === 1 && (
          <div className='step4-classes-screen step4-classes-screen-1'>
            <div className='text-center px-4'>
              <h5 className='mb-2' style={{color: textColor, fontWeight: 500, fontSize: '2rem'}}>
                How many sections does each class have?
              </h5>
              <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                Enter the average number. You can review, add, or remove sections for each class on
                the next screen.
              </p>
            </div>

            <div
              className='step4-classes-fields'
              style={{display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'nowrap'}}
            >
              <div className='step4-classes-field' style={{minWidth: 220, flex: '0 0 220px'}}>
                <label>Section Count</label>
                <input
                  type='text'
                  inputMode='numeric'
                  value={sectionCountInput}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '')
                    setSectionCountInput(raw)
                    if (!raw) return
                    const newCount = Number.parseInt(raw, 10)
                    if (!Number.isFinite(newCount) || newCount < 1) return
                    if (newCount > MAX_SECTIONS) {
                      setSectionCountError(
                        `Maximum ${MAX_SECTIONS} sections are allowed. Please reduce the count.`
                      )
                      return
                    } else {
                      setSectionCountError(null)
                    }
                    const current = uniqueStrings(sectionNomenclatureInput.split(','))
                    if (newCount > current.length) {
                      const next = [...current]
                      for (let i = current.length; i < newCount; i++) {
                        next.push(String.fromCharCode(65 + i))
                      }
                      setSectionNomenclatureInput(next.join(','))
                    } else if (newCount < current.length) {
                      setSectionNomenclatureInput(current.slice(0, newCount).join(','))
                    }
                  }}
                  className='form-control form-control-solid'
                />
                {sectionCountError && (
                  <div
                    style={{
                      color: '#dc3545',
                      fontSize: '0.85rem',
                      marginTop: 6,
                      display: 'block',
                    }}
                  >
                    {sectionCountError}
                  </div>
                )}
              </div>
              <div className='step4-classes-field step4-classes-field-wide' style={{flex: 1}}>
                <label>Section Nomenclature</label>
                <input
                  type='text'
                  value={sectionNomenclatureInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9,]/g, '')
                    setSectionNomenclatureInput(val)
                    const parsed = uniqueStrings(val.split(','))
                    if (parsed.length === 0) return

                    if (parsed.length > MAX_SECTIONS) {
                      setSectionCountError(
                        `Maximum ${MAX_SECTIONS} sections are allowed. Please reduce the count.`
                      )
                      setSectionCountInput(String(parsed.length))
                      return
                    }

                    setSectionCountError(null)
                    setSectionCountInput(String(parsed.length))
                  }}
                  className='form-control form-control-solid'
                />
              </div>
            </div>
          </div>
        )}

        {classesFlowStep === 2 && (
          <div className='step4-classes-screen step4-classes-screen-2'>
            <div className='text-center px-4 mb-4'>
              <h5 className='mb-1' style={{color: textColor, fontWeight: 500, fontSize: '2rem'}}>
                Add each class name in the rows below.
              </h5>
              <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                Data is pre-filled. You can change it if needed.
              </p>
            </div>

            <div className='step4-classes-level-groups'>
              {isHydrating && classLevelGroups.length === 0 ? (
                <CustomSkeleton variant={CARD_PRESET.LIST_ITEM} count={4} />
              ) : classLevelGroups.length === 0 ? (
                <div className='text-center px-4 py-8'>
                  <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                    Select at least one curriculum and level in Basic Setup to add classes here.
                  </p>
                </div>
              ) : (
                classLevelGroups.map(([curriculumName, levels]) => {
                  const expanded =
                    classesExpandedCurriculum.size === 0 ||
                    classesExpandedCurriculum.has(curriculumName)

                  return (
                    <div key={curriculumName} className='step4-classes-level-group'>
                      <button
                        type='button'
                        className={`step4-classes-level-group-header${expanded ? ' is-expanded' : ''}`}
                        onClick={() => {
                          setClassesExpandedCurriculum((prev) => {
                            const base =
                              prev.size === 0
                                ? new Set(classLevelGroups.map(([k]) => k))
                                : new Set(prev)
                            if (expanded) base.delete(curriculumName)
                            else base.add(curriculumName)
                            return base
                          })
                        }}
                      >
                        {expanded ? (
                          <ChevronUp size={18} color='#056EE9' />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                        <span>{curriculumName}</span>
                      </button>

                      {expanded && (
                        <div className='step4-classes-level-group-body'>
                          {Object.entries(levels).map(([levelName, entries]) => {
                            const inputKey = `${curriculumName}|||${levelName}`

                            return (
                              <div key={levelName} className='step4-classes-level-row'>
                                <div className='step4-classes-level-input'>
                                  {entries.map((entry, idx) => (
                                    <span key={`${entry.name}-${idx}`} className='step2-branch-tag'>
                                      {entry.name}
                                      <button
                                        type='button'
                                        className='step2-branch-tag-remove'
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setPrefilledEntries(curriculumName, levelName, (items) =>
                                            items.filter((_, itemIdx) => itemIdx !== idx)
                                          )
                                        }}
                                      >
                                        <TagRemoveIcon />
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    type='text'
                                    className='step2-branch-input-field step4-classes-chip-input'
                                    value={classInputByLevel[inputKey] || ''}
                                    placeholder='Enter class name and press Enter'
                                    onChange={(e) =>
                                      setClassInputByLevel((prev) => ({
                                        ...prev,
                                        [inputKey]: e.target.value,
                                      }))
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleAddPrefilledClass(curriculumName, levelName)
                                      } else if (
                                        e.key === 'Backspace' &&
                                        !(classInputByLevel[inputKey] || '') &&
                                        entries.length > 0
                                      ) {
                                        // Mirror the levels behavior: backspace on an empty input
                                        // removes the last class chip.
                                        e.preventDefault()
                                        setPrefilledEntries(curriculumName, levelName, (items) =>
                                          items.slice(0, -1)
                                        )
                                      }
                                    }}
                                    onBlur={(e) =>
                                      handleAddPrefilledClassWithValue(
                                        curriculumName,
                                        levelName,
                                        e.currentTarget.value
                                      )
                                    }
                                  />
                                </div>
                                <div className='step4-classes-level-pill'>{levelName}</div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            {missingClassLevelsOnEntryScreen.length > 0 && (
              <p className='step4-subjects-validation-note mt-3'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.CLASSES.LEVEL_VALIDATION',
                })}
              </p>
            )}
          </div>
        )}

        {classesFlowStep === 3 && (
          <div className='step4-classes-screen step4-classes-screen-3'>
            <div className='text-center px-4 mb-4'>
              <h5 className='mb-1' style={{color: textColor, fontWeight: 500, fontSize: '2rem'}}>
                Review
              </h5>
              <p className='mb-0' style={{color: mutedColor, fontSize: '1rem'}}>
                Review the details below.
              </p>
            </div>

            <div className='step4-classes-branch-groups step4-classes-branch-groups-scroll'>
              {isHydrating && Object.keys(reviewRowsByBranch).length === 0 && (
                <CustomSkeleton variant={CARD_PRESET.TABLE} />
              )}
              {Object.entries(reviewRowsByBranch).map(([branchName, rows]) => {
                const isOpen = expandedBranch.size === 0 || expandedBranch.has(branchName)
                return (
                  <div key={branchName} className='step4-classes-branch-group'>
                    <button
                      type='button'
                      className={`step4-classes-level-group-header${isOpen ? ' is-expanded' : ''}`}
                      onClick={() => {
                        setExpandedBranch((prev) => {
                          const base =
                            prev.size === 0
                              ? new Set(Object.keys(reviewRowsByBranch))
                              : new Set(prev)
                          if (isOpen) base.delete(branchName)
                          else base.add(branchName)
                          return base
                        })
                      }}
                    >
                      {isOpen ? <ChevronUp size={18} color='#056EE9' /> : <ChevronDown size={18} />}
                      <span>{branchName}</span>
                    </button>

                    {isOpen && (
                      <div className='step4-classes-review-table-wrap'>
                        <GenericTable
                          isDark={isDark}
                          data={rows as any[]}
                          onChange={() => {}}
                          hashLabel='#'
                          selectPlaceholder=''
                          containerClassName=''
                          tableClassName='table table-borderless align-middle mb-0 step4-teachers-table'
                          tbodyStyle={{backgroundColor: bodyBg}}
                          rowKey={(r: any) => r.id}
                          rowClassName={() => `step4-classes-review-row is-editing`}
                          columns={[
                            ...(showCurriculumColumn
                              ? [
                                  {
                                    key: 'curriculum',
                                    label: 'Curriculum Options',
                                    type: 'text' as const,
                                    render: ({row}: any) => (
                                      <input
                                        type='text'
                                        className='form-control form-control-solid'
                                        style={{border: 'none', boxShadow: 'none'}}
                                        value={row.curriculum}
                                        disabled
                                        readOnly
                                        tabIndex={-1}
                                      />
                                    ),
                                  },
                                ]
                              : []),
                            {
                              key: 'level',
                              label: 'Level',
                              type: 'text' as const,
                              render: ({row}: any) => (
                                <input
                                  type='text'
                                  className='form-control form-control-solid'
                                  style={{border: 'none', boxShadow: 'none'}}
                                  value={row.level}
                                  disabled
                                  readOnly
                                  tabIndex={-1}
                                />
                              ),
                            },
                            {
                              key: 'gradeName',
                              label: 'Class Name',
                              type: 'text' as const,
                              render: ({row, rowIndex}) => (
                                <input
                                  type='text'
                                  className='form-control form-control-solid'
                                  style={{border: 'none', boxShadow: 'none'}}
                                  value={row.gradeName}
                                  disabled
                                  readOnly
                                  tabIndex={-1}
                                />
                              ),
                            },
                            {
                              key: 'sections',
                              label: 'Sections',
                              type: 'text' as const,
                              render: ({row}: any) => (
                                <input
                                  type='text'
                                  className='form-control form-control-solid'
                                  style={{border: 'none', boxShadow: 'none'}}
                                  value={(row.sections ?? []).join(',')}
                                  disabled
                                  readOnly
                                  tabIndex={-1}
                                />
                              ),
                            },
                            {
                              key: 'actions',
                              label: 'Actions',
                              type: 'text' as const,
                              render: ({row, rowIndex}) => (
                                <div className='d-flex justify-content-center gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm btn-light-danger'
                                    onClick={() => {
                                      const action = {
                                        type: 'classes-review-row' as const,
                                        branchName,
                                        rowId: row.id,
                                        rowIdx: rowIndex,
                                      }
                                      if (
                                        localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true'
                                      ) {
                                        performDeleteAction(action)
                                      } else {
                                        setPendingDeleteAction(action)
                                      }
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className='d-flex justify-content-end gap-3 w-100 mt-4 step4-classes-next-footer'>
          {(classesFlowStep !== 2 || showBackButton) && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={isSubmitting}
              onClick={handleClassesBack}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
            </button>
          )}
          <button
            type='button'
            className='onboarding-btn-next'
            disabled={!isClassesCurrentStepValid || isSubmitting}
            onClick={handleClassesNext}
          >
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm align-middle me-2' />
                {intl.formatMessage({id: 'COMMON.LOADING'})}
              </>
            ) : (
              <>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                <ArrowRight size={16} className='ms-2' />
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  const renderSubjectsUI = () => {
    const subjectSelections = getEffectiveSubjectSelectionsByLevel(
      localData.subjectSelectionsByLevel
    )
    const missingLevels = subjectLevelRows.filter(
      ({key}) => (subjectSelections[key] ?? []).length === 0
    )
    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const tableBorderColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(24, 28, 50, 0.12)'

    return (
      <div className='step4-manual-content'>
        <div className='step4-subjects-layout'>
          <div className='text-center px-4 mb-4'>
            <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.65rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.HEADING'})}
            </h5>
            <p className='mb-1' style={{color: mutedColor, fontSize: '0.96rem', lineHeight: 1.45}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.TABLE_SUBTITLE'})}
            </p>
            <p className='mb-0' style={{color: mutedColor, fontSize: '0.92rem', lineHeight: 1.45}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.TABLE_HELPER'})}
            </p>
          </div>

          <div className='step4-subjects-table-card' style={{borderColor: tableBorderColor}}>
            <GenericTable
              isDark={isDark}
              data={Object.entries(selectedLevelsByCurriculum).flatMap(([curriculumName, levels]) =>
                levels.map((levelName, index) => ({
                  curriculumName,
                  levels,
                  levelName,
                  index,
                  key: getSubjectLevelKey(curriculumName, levelName),
                }))
              )}
              onChange={() => {}}
              hashLabel=''
              selectPlaceholder=''
              containerClassName=''
              tableClassName='step4-subjects-table'
              theadClassName={undefined}
              headerRowClassName={undefined}
              hashThClassName=''
              hashTdClassName=''
              hashThStyle={{display: 'none'}}
              hashTdStyle={{display: 'none'}}
              columns={[
                {
                  key: 'curriculumName',
                  label: intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.COL_CURRICULUM',
                  }),
                  type: 'text',
                  render: ({row}) => {
                    if (row.index !== 0) {
                      return {node: null, hidden: true}
                    }
                    return {
                      node: <>{row.curriculumName}</>,
                      tdProps: {
                        rowSpan: row.levels.length,
                        className: 'step4-subjects-curriculum-cell',
                      },
                    }
                  },
                },
                {
                  key: 'levelName',
                  label: intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.COL_LEVEL',
                  }),
                  type: 'text',
                  tdClassName: 'step4-subjects-level-cell',
                  render: ({row}) => <>{row.levelName}</>,
                },
                {
                  key: 'subjects',
                  label: intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.COL_SUBJECTS',
                  }),
                  type: 'text',
                  render: ({row}) => {
                    const key = row.key
                    const subjects = subjectSelections[key] ?? []
                    const rowError = subjectErrorByLevel[key]
                    const editing = editingSubjectByLevel[key]
                    return (
                      <>
                        <div className='step4-subjects-chip-box'>
                          {subjects.map((subjectName: string) => (
                            <span key={`${key}-${subjectName}`} className='step4-subject-chip'>
                              {editing?.original === subjectName ? (
                                <>
                                  <input
                                    type='text'
                                    value={editing.value}
                                    onChange={(e) => {
                                      const value = e.target.value
                                      setEditingSubjectByLevel((prev) => ({
                                        ...prev,
                                        [key]: {
                                          ...(prev[key] || {original: subjectName, value: ''}),
                                          value,
                                        },
                                      }))
                                      if (subjectErrorByLevel[key]) {
                                        setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        commitEditSubject(row.curriculumName, row.levelName)
                                      }
                                      if (e.key === 'Escape') {
                                        e.preventDefault()
                                        cancelEditSubject(row.curriculumName, row.levelName)
                                      }
                                    }}
                                    onBlur={() => {
                                      if (skipEditBlurCommitRef.current) {
                                        skipEditBlurCommitRef.current = false
                                        return
                                      }
                                      commitEditSubject(row.curriculumName, row.levelName)
                                    }}
                                    style={{
                                      border: 'none',
                                      background: 'transparent',
                                      color: 'inherit',
                                      minWidth: 70,
                                      outline: 'none',
                                      fontSize: 'inherit',
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    type='button'
                                    className='step4-subject-chip-remove'
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    onClick={() => {
                                      commitEditSubject(row.curriculumName, row.levelName)
                                    }}
                                    aria-label='Save'
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type='button'
                                    className='step4-subject-chip-remove'
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      skipEditBlurCommitRef.current = true
                                    }}
                                    onClick={() => {
                                      cancelEditSubject(row.curriculumName, row.levelName)
                                    }}
                                    aria-label='Cancel'
                                  >
                                    <TagRemoveIcon />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type='button'
                                    style={{
                                      border: 'none',
                                      background: 'transparent',
                                      color: 'inherit',
                                      padding: 0,
                                      cursor: 'pointer',
                                      fontSize: 'inherit',
                                    }}
                                    onMouseDown={(e) => {
                                      skipSubjectInputBlurRef.current = true
                                      e.preventDefault()
                                    }}
                                    onClick={() => {
                                      startEditSubject(
                                        row.curriculumName,
                                        row.levelName,
                                        subjectName
                                      )
                                    }}
                                    aria-label='Edit'
                                    title='Edit subject'
                                  >
                                    {subjectName}
                                  </button>
                                  <button
                                    type='button'
                                    className='step4-subject-chip-remove'
                                    onMouseDown={(e) => {
                                      skipSubjectInputBlurRef.current = true
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    onClick={() =>
                                      handleRemoveSubject(
                                        row.curriculumName,
                                        row.levelName,
                                        subjectName
                                      )
                                    }
                                    aria-label='Remove'
                                  >
                                    <TagRemoveIcon />
                                  </button>
                                </>
                              )}
                            </span>
                          ))}
                          <input
                            id={`step4-subject-input-${key}`}
                            type='text'
                            value={subjectInputByLevel[key] || ''}
                            onChange={(e) => {
                              const value = e.target.value
                              setSubjectInputByLevel((prev) => ({...prev, [key]: value}))
                              if (subjectErrorByLevel[key]) {
                                setSubjectErrorByLevel((prev) => ({...prev, [key]: null}))
                              }
                            }}
                            onKeyDown={(e) =>
                              handleSubjectKeyDown(e, row.curriculumName, row.levelName)
                            }
                            onBlur={(e) => {
                              if (skipSubjectInputBlurRef.current) {
                                skipSubjectInputBlurRef.current = false
                                return
                              }
                              handleAddSubjectWithValue(
                                row.curriculumName,
                                row.levelName,
                                e.currentTarget.value
                              )
                            }}
                            className='step4-subjects-inline-input'
                            placeholder={
                              subjects.length === 0
                                ? intl.formatMessage({
                                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.INPUT_PLACEHOLDER',
                                  })
                                : ''
                            }
                          />
                        </div>
                        {rowError && <div className='step4-subjects-row-error'>{rowError}</div>}
                      </>
                    )
                  },
                },
              ]}
            />
          </div>

          {missingLevels.length > 0 && (
            <p className='step4-subjects-validation-note'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SUBJECTS.LEVEL_VALIDATION',
              })}
            </p>
          )}
        </div>

        <div className='d-flex justify-content-end gap-3 w-100 mt-4'>
          {showBackButton && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={isSubmitting}
              onClick={handleBackTab}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
            </button>
          )}
          {activeTab === tabs[tabs.length - 1].id ? (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('subjects') || isSubmitting}
              onClick={() => handleFinishStep()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          ) : (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('subjects') || isSubmitting}
              onClick={() => handleNextTab()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderStudentsUI = () => {
    const items = (localData.students as any[]) || []
    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const tableBorderColor = isDark ? '#333640' : '#e4e6ef'
    const bodyBg = isDark ? '#1E1E2D' : '#ffffff'
    const headerBlue = '#056EE9'

    const getFullName = (item: any) =>
      (item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || '').trim()

    return (
      <div className='step4-manual-content'>
        <div className='text-center px-4 mb-4'>
          <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.35rem'}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.HEADING'})}
          </h5>
          <p className='mb-1' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.INSTRUCTION'})}
          </p>
          <p className='mb-0' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.INSTRUCTION_ADD'})}
          </p>
        </div>

        <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 mb-3 px-3'>
          <input
            ref={studentsCsvInputRef}
            type='file'
            accept={
              studentsConversionAvailable
                ? dataUploadAcceptAttribute(studentsUploadConfig.formats)
                : '.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
            className='d-none'
            onChange={handleStudentsCsvFileChange}
          />
          <button
            type='button'
            className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2'
            onClick={handleDownloadStudentsTemplate}
            disabled={downloadingStudentsTemplate}
          >
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.DOWNLOAD_SAMPLE_CSV',
            })}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-primary d-inline-flex align-items-center gap-2'
            onClick={() => studentsCsvInputRef.current?.click()}
            disabled={isImportingStudentsFile}
          >
            <Upload size={16} aria-hidden />
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.UPLOAD_DATA_FILE',
            })}
          </button>
        </div>

        {studentsConversionAvailable && (
          <div className='text-center px-4 mb-3' style={{color: mutedColor, fontSize: '0.85rem'}}>
            <div>
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.CONVERT.ACCEPTED_FORMATS'},
                {size: formatDataUploadBytes(studentsUploadConfig.maxFileBytes)}
              )}
            </div>
            <div>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.PHOTO_TIPS'})}</div>
          </div>
        )}

        {studentsPrefill && !isImportingStudentsFile && (
          <div
            className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'
            role='status'
            style={{
              width: '95%',
              maxWidth: '95%',
              background: isDark ? 'rgba(5, 110, 233, 0.14)' : '#eef6ff',
              border: `1px solid ${isDark ? '#1e3a5f' : '#cfe3fb'}`,
              borderRadius: 10,
              padding: '0.75rem 1rem',
              color: textColor,
              fontSize: '0.92rem',
            }}
          >
            <span className='d-inline-flex align-items-center gap-2'>
              <Info size={16} aria-hidden style={{color: headerBlue, flexShrink: 0}} />
              {intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_FOUND'},
                {count: studentsPrefill.count}
              )}
            </span>
            <button
              type='button'
              className='btn btn-sm btn-light-danger d-inline-flex align-items-center gap-2'
              onClick={() => setPendingDeleteAction({type: 'students-prefill'})}
            >
              <Trash2 size={14} aria-hidden />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVE'})}
            </button>
          </div>
        )}

        {studentsConversion && !isImportingStudentsFile && (
          <div className='mb-3' style={{width: '95%', maxWidth: '95%'}}>
            <DataUploadReport
              conversion={studentsConversion}
              isDark={isDark}
              showKeptStudents
              onDismiss={() => setStudentsConversion(null)}
            />
          </div>
        )}

        <div
          className='step4-teachers-table-wrapper mb-4'
          style={{
            border: `1px solid ${tableBorderColor}`,
            borderRadius: 10,
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '95%',
            maxWidth: '95%',
          }}
        >
          {isImportingStudentsFile || studentsPrefillReading ? (
            <div className='p-4'>
              {/* The Teachers file is being read for this step: skeleton until its rows land. */}
              {studentsPrefillReading && !isImportingStudentsFile && (
                <div
                  className='text-center mb-4'
                  role='status'
                  aria-live='polite'
                  style={{color: mutedColor, fontSize: '0.9rem'}}
                >
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_READING',
                  })}
                  {renderPrefillProgress(studentsPrefillPercent)}
                </div>
              )}
              {studentsReadingFile && (
                <div className='text-center mb-4' role='status' aria-live='polite'>
                  <div className='d-inline-flex align-items-center gap-2'>
                    <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
                    <strong style={{color: textColor}}>
                      {studentsUploadProgress !== null
                        ? intl.formatMessage(
                            {id: 'DATA_UPLOAD.CONVERT.UPLOADING'},
                            {percent: Math.round(studentsUploadProgress * 100)}
                          )
                        : intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE'})}
                    </strong>
                  </div>
                  {studentsUploadProgress !== null ? (
                    <div
                      className='progress mx-auto mt-2'
                      style={{height: 6, maxWidth: 320}}
                      role='progressbar'
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(studentsUploadProgress * 100)}
                    >
                      <div
                        className='progress-bar'
                        style={{width: `${Math.round(studentsUploadProgress * 100)}%`}}
                      />
                    </div>
                  ) : (
                    <div className='mt-1' style={{color: mutedColor, fontSize: '0.88rem'}}>
                      {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}
                    </div>
                  )}
                </div>
              )}
              <CustomSkeleton variant={CARD_PRESET.TABLE} />
            </div>
          ) : (
            <GenericTable
              isDark={isDark}
              data={items}
              onChange={() => {}}
              hashLabel='#'
              selectPlaceholder=''
              containerClassName=''
              tableClassName='table align-middle mb-0 step4-teachers-table'
              tbodyStyle={{backgroundColor: bodyBg}}
              hashThStyle={{
                width: 60,
                background: headerBlue,
                color: '#fff',
                fontWeight: 600,
                border: 'none',
                padding: '0.75rem 1rem',
                textAlign: 'center',
              }}
              hashTdStyle={{
                color: mutedColor,
                fontWeight: 600,
                borderColor: tableBorderColor,
                padding: '0.75rem 1rem',
                textAlign: 'center',
              }}
              emptyState={
                <div className='d-flex flex-column align-items-center gap-2'>
                  <span style={{color: mutedColor, fontSize: '0.95rem'}}>
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.EMPTY'})}
                  </span>
                  <button
                    type='button'
                    className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                    onClick={() => handleAddRow('students')}
                  >
                    <span
                      className='step4-add-row-icon'
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: headerBlue,
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      +
                    </span>
                    {intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.ADD_ROW',
                    })}
                  </button>
                </div>
              }
              emptyTdClassName='step4-teachers-empty'
              emptyTdStyle={{
                backgroundColor: bodyBg,
                border: 'none',
                padding: '1.5rem 1rem 1rem',
                verticalAlign: 'middle',
              }}
              columns={[
                {
                  key: 'full_name',
                  label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.FULL_NAME'}),
                  type: 'text',
                  thStyle: {
                    background: headerBlue,
                    color: '#fff',
                    fontWeight: 600,
                    border: 'none',
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 170,
                  },
                  tdStyle: {
                    borderColor: tableBorderColor,
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 170,
                  },
                  render: ({row, rowIndex}) => (
                    <input
                      id={`step4-row-first-input-students-${rowIndex}`}
                      type='text'
                      className='form-control form-control-solid'
                      placeholder=''
                      value={('full_name' in row ? row.full_name : getFullName(row)) || ''}
                      onChange={(e) =>
                        handleDataChange('students', rowIndex, 'full_name', e.target.value)
                      }
                      onKeyDown={(e) =>
                        handleRowEnterKey('students', rowIndex, e, isStudentRowValid(row))
                      }
                      style={{
                        padding: '0.55rem 0.75rem',
                        fontSize: '0.95rem',
                        borderRadius: '0.425rem',
                      }}
                    />
                  ),
                },
                {
                  key: 'class',
                  label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.CLASS'}),
                  type: 'text',
                  thStyle: {
                    background: headerBlue,
                    color: '#fff',
                    fontWeight: 600,
                    border: 'none',
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 170,
                  },
                  tdStyle: {
                    borderColor: tableBorderColor,
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 170,
                  },
                  render: ({row, rowIndex}) => {
                    // A file-imported class that matches none of this school's classes
                    // (tolerant match, same as the subjects dropdown) — its subjects can't
                    // show or sync until the user picks a real class here.
                    const rowClass = String(row.class ?? '').trim()
                    const rowClassKey = normalizeClassMatchKey(rowClass)
                    const classNotFound =
                      rowClass.length > 0 &&
                      !classOptions.some((opt) => normalizeClassMatchKey(opt) === rowClassKey)
                    const classNotFoundLabel = intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_NOT_FOUND',
                    })
                    // The file left the class blank: highlighted until one is picked, with the
                    // server's candidates (two curricula share the name) listed first.
                    const classMissing = !rowClass && isMissingFromFile(row, 'class')
                    const choiceKeys = new Set(
                      ((row._classChoices as string[] | undefined) ?? []).map((choice) =>
                        normalizeClassMatchKey(choice)
                      )
                    )
                    const isChoice = (opt: {value: string; label: string}) =>
                      choiceKeys.has(
                        normalizeClassMatchKey(opt.value.split(CLASS_CURRICULUM_SEP)[0])
                      ) || choiceKeys.has(normalizeClassMatchKey(opt.label))
                    const rowClassOptions =
                      choiceKeys.size > 0
                        ? [
                            ...studentClassOptions.filter(isChoice),
                            ...studentClassOptions.filter((opt) => !isChoice(opt)),
                          ]
                        : studentClassOptions
                    const needsClassLabel = intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_MISSING',
                    })
                    return (
                      <div
                        className='w-100'
                        title={
                          classNotFound
                            ? classNotFoundLabel
                            : classMissing
                              ? needsClassLabel
                              : undefined
                        }
                        style={
                          classNotFound || classMissing
                            ? {
                                border: '1px solid #F64E60',
                                borderRadius: '0.425rem',
                                background: '#FFC6CC',
                              }
                            : undefined
                        }
                      >
                        <FilterSelect
                          value={studentClassValue(row)}
                          options={[
                            {
                              value: '',
                              label: intl.formatMessage({
                                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.SELECT_PLACEHOLDER',
                              }),
                            },
                            ...rowClassOptions,
                          ]}
                          onChange={(v) => handleDataChange('students', rowIndex, 'class', v)}
                          className='w-100'
                          triggerClassName='filter-select-solid w-100'
                          ariaLabel={intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.CLASS',
                          })}
                        />
                        {(classNotFound || classMissing) && (
                          <div
                            className='text-danger fw-semibold'
                            style={{fontSize: '0.8rem', padding: '0.15rem 0.5rem 0.3rem'}}
                          >
                            {classNotFound ? classNotFoundLabel : needsClassLabel}
                          </div>
                        )}
                      </div>
                    )
                  },
                },
                {
                  key: 'registrationNumber',
                  label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.REG_NO'}),
                  type: 'text',
                  thStyle: {
                    background: headerBlue,
                    color: '#fff',
                    fontWeight: 600,
                    border: 'none',
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 170,
                  },
                  tdStyle: {
                    borderColor: tableBorderColor,
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 170,
                  },
                  render: ({row, rowIndex}) => {
                    // The file left it blank: highlighted until filled, like a blank class.
                    const regMissing =
                      !String(row.registrationNumber ?? '').trim() &&
                      isMissingFromFile(row, 'registrationNumber')
                    const needsRegLabel = intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.REG_MISSING',
                    })
                    return (
                      <>
                        <input
                          type='text'
                          className='form-control form-control-solid'
                          placeholder=''
                          value={row.registrationNumber || ''}
                          aria-invalid={regMissing || undefined}
                          title={regMissing ? needsRegLabel : undefined}
                          onChange={(e) =>
                            handleDataChange(
                              'students',
                              rowIndex,
                              'registrationNumber',
                              e.target.value
                            )
                          }
                          onKeyDown={(e) =>
                            handleRowEnterKey('students', rowIndex, e, isStudentRowValid(row))
                          }
                          style={{
                            padding: '0.55rem 0.75rem',
                            fontSize: '0.95rem',
                            borderRadius: '0.425rem',
                            ...(regMissing
                              ? {border: '1px solid #F64E60', background: '#FFC6CC'}
                              : {}),
                          }}
                        />
                        {regMissing && (
                          <div
                            className='text-danger fw-semibold'
                            style={{fontSize: '0.8rem', padding: '0.15rem 0.5rem 0'}}
                          >
                            {needsRegLabel}
                          </div>
                        )}
                      </>
                    )
                  },
                },
                {
                  key: 'subjects',
                  label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.SUBJECTS'}),
                  type: 'text',
                  thStyle: {
                    background: headerBlue,
                    color: '#fff',
                    fontWeight: 600,
                    border: 'none',
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 220,
                  },
                  tdStyle: {
                    borderColor: tableBorderColor,
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    minWidth: 220,
                  },
                  render: ({row, rowIndex}) => {
                    const selectedClass = String(row.class ?? '').trim()
                    // Subjects open only once the row's class is one of THIS school's classes.
                    // A class that no longer exists (the user went back and changed the
                    // curriculum after assigning students, or a file named a class the school
                    // does not have) shows "Class not found" in the class cell; until a real
                    // class is picked there, the subject picker stays disabled instead of
                    // offering every subject of every class.
                    const classIsKnown =
                      selectedClass.length > 0 && isKnownStudentClass(selectedClass)
                    const rowOptions = getStudentSubjectSelectOptions(selectedClass)
                    const selectedValueSet = new Set(
                      ((row.subjects || []) as any[])
                        .map((s: any) => {
                          let name = String(typeof s === 'object' ? s.name : s).trim()
                          let cls = String(
                            typeof s === 'object' ? s.class || selectedClass : selectedClass
                          ).trim()
                          // File-imported subjects arrive as encoded "name|||class" strings
                          if (name.includes(STUDENT_SUBJECT_DELIMITER)) {
                            const [namePart, classPart] = name.split(STUDENT_SUBJECT_DELIMITER)
                            name = String(namePart ?? '').trim()
                            cls = String(classPart ?? '').trim() || cls
                          }
                          // Class part matches tolerantly so a file-imported spelling
                          // ("Class 1A") still ticks the option of "Class 1-A".
                          return name && cls
                            ? `${name.toLowerCase()}${STUDENT_SUBJECT_DELIMITER}${normalizeClassMatchKey(cls)}`
                            : ''
                        })
                        .filter(Boolean)
                    )
                    const selectedOptionValues = rowOptions.filter((opt) =>
                      selectedValueSet.has(
                        `${String(opt.subjectName).toLowerCase()}${STUDENT_SUBJECT_DELIMITER}${normalizeClassMatchKey(opt.className)}`
                      )
                    )
                    const isAllSelected =
                      rowOptions.length > 0 && selectedOptionValues.length === rowOptions.length
                    const selectAllOption = {
                      value: '__select_all__',
                      label: isAllSelected ? 'Deselect All' : 'Select All',
                      subjectName: '',
                      className: '',
                    }
                    return (
                      <Select
                        isMulti
                        isDisabled={!classIsKnown}
                        closeMenuOnSelect={false}
                        // react-select blurs the input after a pick on touch devices,
                        // which closes the menu after every single subject on a phone
                        blurInputOnSelect={false}
                        hideSelectedOptions={false}
                        components={{
                          ValueContainer: MultiValueContainer,
                          Option: CheckboxOption,
                        }}
                        menuPortalTarget={document.body}
                        menuPosition='fixed'
                        // Placement is decided in the menuPortal style below from the space
                        // left in the viewport (below the field, else above); react-select's
                        // own flip is switched off so the two never disagree.
                        menuPlacement='bottom'
                        menuShouldScrollIntoView={false}
                        // Upper bound; the portal shrinks it to the space on the chosen side
                        // so the list's own scrollbar can reach the last option.
                        maxMenuHeight={240}
                        options={[selectAllOption, ...rowOptions]}
                        value={selectedOptionValues}
                        onChange={(selectedOptions: any, actionMeta: any) => {
                          if (
                            actionMeta.action === 'select-option' &&
                            actionMeta.option?.value === '__select_all__'
                          ) {
                            if (isAllSelected) {
                              handleDataChange('students', rowIndex, 'subjects', [])
                            } else {
                              const allValues = rowOptions.map((opt: any) => opt.value)
                              handleDataChange('students', rowIndex, 'subjects', allValues)
                            }
                          } else if (
                            actionMeta.action === 'deselect-option' &&
                            actionMeta.option?.value === '__select_all__'
                          ) {
                            handleDataChange('students', rowIndex, 'subjects', [])
                          } else {
                            const selectedValues = selectedOptions
                              ? selectedOptions
                                  .filter((opt: any) => opt.value !== '__select_all__')
                                  .map((opt: any) => opt.value)
                              : []
                            handleDataChange('students', rowIndex, 'subjects', selectedValues)
                          }
                        }}
                        className='react-select-styled'
                        classNamePrefix='react-select'
                        placeholder={intl.formatMessage({
                          id: classIsKnown
                            ? 'ONBOARDING_WIZARD.STEP4_MANUAL.SELECT_PLACEHOLDER'
                            : 'ONBOARDING_WIZARD.STEP4_MANUAL.SELECT_CLASS_FIRST',
                        })}
                        onKeyDown={(e: React.KeyboardEvent) =>
                          handleRowEnterKey('students', rowIndex, e, isStudentRowValid(row))
                        }
                        styles={{
                          control: (base: any, state: any) => ({
                            ...base,
                            borderColor: state.isFocused ? '#B5B5C3' : 'transparent',
                            backgroundColor: state.isDisabled
                              ? isDark
                                ? '#1E1E2D'
                                : '#EFF2F5'
                              : 'var(--bs-gray-100)',
                            color: isDark ? '#ffffff' : base.color,
                            minHeight: '43px',
                          }),
                          singleValue: (base: any) => ({
                            ...base,
                            color: isDark ? '#ffffff' : base.color,
                          }),
                          valueContainer: (base: any) => ({
                            ...base,
                            flexWrap: 'nowrap',
                          }),
                          multiValue: (base: any) => ({
                            ...base,
                            backgroundColor: isDark ? '#323248' : base.backgroundColor,
                          }),
                          multiValueLabel: (base: any) => ({
                            ...base,
                            color: isDark ? '#ffffff' : base.color,
                          }),
                          option: (base: any, state: any) => ({
                            ...base,
                            // Selected options keep the normal background — the checkbox
                            // tick is the only selection indicator.
                            backgroundColor: state.isFocused
                              ? isDark
                                ? '#323248'
                                : '#F4F5F7'
                              : isDark
                                ? '#1E1E2D'
                                : 'transparent',
                            color: state.isFocused
                              ? isDark
                                ? '#ffffff'
                                : '#181C32'
                              : isDark
                                ? '#A7A8AA'
                                : '#181C32',
                            ':active': {
                              backgroundColor: isDark ? '#323248' : '#F4F5F7',
                            },
                            ...(state.data?.value === '__select_all__' ? {fontWeight: 600} : {}),
                          }),
                          menu: (base: any) => ({
                            ...base,
                            backgroundColor: isDark ? '#1E1E2D' : base.backgroundColor,
                            // Fill the portal wrapper, which is sized and placed against the
                            // subject field itself (see menuPortal).
                            width: '100%',
                            // Hang below the wrapper's top edge, or - when menuPortal decided
                            // there is more room above the field - grow upward from it.
                            top: 'var(--rs-menu-top, 100%)',
                            bottom: 'var(--rs-menu-bottom, auto)',
                          }),
                          menuList: (base: any) => ({
                            ...base,
                            // bounded by the space actually left below the control
                            // (set as a CSS variable on the portal wrapper)
                            maxHeight: 'var(--rs-menu-max-height, 240px)',
                            overflowY: 'auto',
                          }),
                          menuPortal: (base: any, state: any) => {
                            const vw = window.innerWidth
                            const margin = 8
                            // Directly under the subject field: as wide as the field, or
                            // 280px if the field is narrower, so labels stay readable. The
                            // field can sit partly off-screen in the horizontally scrolled
                            // table, so the box is nudged back inside the viewport.
                            const width = Math.min(Math.max(state.rect.width, 280), vw - margin * 2)
                            const left = Math.max(
                              margin,
                              Math.min(state.rect.left, vw - width - margin)
                            )
                            // Open toward the side of the viewport with room: below the field
                            // when the full list fits there (or below has more room than
                            // above), otherwise above it. The list height is bounded by that
                            // side's space so its own scrollbar can always reach the last option.
                            const vh = window.innerHeight
                            const gap = 12
                            const spaceBelow = vh - state.rect.bottom - gap
                            const spaceAbove = state.rect.top - gap
                            const dropUp = spaceBelow < 240 && spaceAbove > spaceBelow
                            const maxHeight = Math.max(
                              120,
                              Math.min(240, dropUp ? spaceAbove : spaceBelow)
                            )
                            return {
                              ...base,
                              zIndex: 9999,
                              left,
                              width,
                              top: dropUp ? state.rect.top : state.rect.bottom,
                              ['--rs-menu-top' as any]: dropUp ? 'auto' : '100%',
                              ['--rs-menu-bottom' as any]: dropUp ? '100%' : 'auto',
                              ['--rs-menu-max-height' as any]: `${maxHeight}px`,
                            }
                          },
                        }}
                      />
                    )
                  },
                },
                {
                  key: 'actions',
                  label: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.ACTIONS'}),
                  type: 'text',
                  thStyle: {
                    width: 100,
                    background: headerBlue,
                    color: '#fff',
                    fontWeight: 600,
                    border: 'none',
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                  },
                  tdStyle: {
                    borderColor: tableBorderColor,
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                  },
                  render: ({rowIndex}) => (
                    <button
                      type='button'
                      className='btn btn-icon btn-sm btn-light-danger'
                      onClick={() => {
                        const action = {
                          type: 'category-row' as const,
                          category: 'students' as const,
                          index: rowIndex,
                        }
                        if (
                          !alwaysConfirmDelete(action) &&
                          localStorage.getItem(ROW_DELETE_SKIP_CONFIRM_KEY) === 'true'
                        ) {
                          performDeleteAction(action)
                        } else {
                          setPendingDeleteAction(action)
                        }
                      }}
                      title='Delete'
                    >
                      <Trash2 size={16} />
                    </button>
                  ),
                },
              ]}
            />
          )}
          {items.length > 0 && (
            <div
              className='step4-add-row-below d-flex justify-content-center p-3'
              style={{
                borderTop: `1px solid ${tableBorderColor}`,
                backgroundColor: bodyBg,
              }}
            >
              <button
                type='button'
                className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                onClick={() => handleAddRow('students')}
              >
                <span
                  className='step4-add-row-icon'
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: headerBlue,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  +
                </span>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.ADD_ROW',
                })}
              </button>
            </div>
          )}
        </div>

        <div className='d-flex justify-content-end gap-3 w-100 mt-4'>
          {showBackButton && (
            <button
              type='button'
              className='onboarding-btn-back me-auto'
              disabled={isSubmitting}
              onClick={handleBackTab}
            >
              <ArrowLeft size={16} className='me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
            </button>
          )}
          {activeTab === tabs[tabs.length - 1].id ? (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('students') || isSubmitting}
              onClick={() => handleFinishStep()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          ) : (
            <button
              type='button'
              className='onboarding-btn-next'
              disabled={!isTabValid('students') || isSubmitting}
              onClick={() => handleNextTab()}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm align-middle me-2' />
                  {intl.formatMessage({id: 'COMMON.LOADING'})}
                </>
              ) : (
                <>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                  <ArrowRight size={16} className='ms-2' />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderLessonPlanningPlaceholder = () => {
    const allClasses = (localData.classes as any[]) || []
    const teachers = (localData.teachers as any[]) || []
    const branches = Array.from(
      new Set(
        [
          ...branchOptions.map(normalizeBranchName),
          ...allClasses.map((c: any) => normalizeBranchName(c?.branch)),
        ].filter(Boolean)
      )
    )
    const resolvedActiveBranch =
      activeLessonBranch && branches.includes(activeLessonBranch)
        ? activeLessonBranch
        : branches[0] || 'Main'
    const classesByBranch = allClasses.filter(
      (c: any) => normalizeBranchName(c?.branch) === resolvedActiveBranch
    )
    const levels = Array.from(
      new Set(classesByBranch.map((c: any) => (c.level || '').trim()).filter(Boolean))
    ) as string[]
    const resolvedActiveLevel =
      activeLessonLevel && levels.includes(activeLessonLevel) ? activeLessonLevel : ''
    const classesAtResolvedLevel = resolvedActiveLevel
      ? classesByBranch.filter((c: any) => (c.level || '').trim() === resolvedActiveLevel)
      : classesByBranch

    const curriculaForResolvedLevel = uniqueStrings(
      classesAtResolvedLevel.map((c: any) => String(c?.curriculum || '').trim()).filter(Boolean)
    )

    const resolvedActiveCurriculum =
      activeLessonCurriculum && curriculaForResolvedLevel.includes(activeLessonCurriculum)
        ? activeLessonCurriculum
        : curriculaForResolvedLevel[0] || ''

    const classes =
      resolvedActiveLevel && curriculaForResolvedLevel.length > 1 && resolvedActiveCurriculum
        ? classesAtResolvedLevel.filter(
            (c: any) => String(c?.curriculum || '').trim() === resolvedActiveCurriculum
          )
        : classesAtResolvedLevel

    const textColor = isDark ? '#ffffff' : '#181C32'
    const mutedColor = isDark ? '#A7A8AA' : '#7E8299'
    const tableBorderColor = isDark ? '#2b2b40' : '#e4e6ef'
    const bodyBg = isDark ? '#151521' : '#ffffff'
    // brighter header blue to match reference
    const headerBg = '#0B79F0'
    const headerBlue = '#0B79F0'
    const classNameColumnBg = isDark ? '#15171C' : '#f5f8fa'
    const accentBlue = '#056EE9'
    // light sky-blue cell background (matches the chat UI's light blue tones)
    const cellBgOffset = isDark ? '#1f2530' : '#E8F4FC'
    const defaultFreq = data.manualData?.defaultSubjectFrequency
    const defaultPer = data.manualData?.defaultFrequencyPer || 'week'
    const defaultFrequencyStr =
      defaultFreq && defaultFreq >= 1 ? `${defaultFreq}/${defaultPer}` : '1/week'
    const defaultDurationStr = '1' // 1 slot per session (hardcoded)
    // Per-subject default: the curriculum prefill's weekly frequency for this class's level
    // (a heuristic — inferred from the subject's role, never published — so only ever a
    // starting point the school is expected to edit), else Step 2's institution-wide default.
    const storedPrefills = readStoredCurriculumPrefills()
    const defaultFrequencyFor = (className: string, subjectName: string): string => {
      const classItem = classes.find((c: any) => String(c?.name ?? '') === className)
      const hit = classItem
        ? cachedPrefillFrequency(
            storedPrefills,
            String(classItem?.curriculum ?? ''),
            String(classItem?.level ?? ''),
            subjectName
          )
        : null
      return hit ?? defaultFrequencyStr
    }

    const getTeacherFullName = (t: any) =>
      (t?._nameInput !== undefined
        ? String(t._nameInput || '').trim()
        : `${t?.first_name || ''} ${t?.last_name || ''}`.trim()
      ).trim() ||
      (t?.employeeId || '').toString().trim() ||
      ''

    const subjectNames = Array.from(
      new Set(classes.flatMap((classItem: any) => getSubjectsForClass(classItem)).filter(Boolean))
    )

    // Subjects that appear only because a user added them in Lesson Planning for a specific class.
    // If a subject isn't part of any class's "allowed" set (class subjects / curriculum-level subjects),
    // we treat it as dynamic-only and avoid showing "+" add actions for other classes.
    const dynamicOnlySubjects = (() => {
      const allowedAcrossClasses = new Set<string>()
      classes.forEach((classItem: any) => {
        const curriculumName = (classItem?.curriculum || '').toString().trim()
        const levelName = (classItem?.level || '').toString().trim()
        const allowed = (() => {
          if (Array.isArray(classItem?.subjects) && classItem.subjects.length > 0) {
            return uniqueStrings(
              classItem.subjects.map((s: any) => (typeof s === 'string' ? s : s?.name || ''))
            )
          }
          const levelSubjects = curriculumName
            ? getSubjectsForCurriculumLevel(curriculumName, levelName)
            : []
          if (levelSubjects.length > 0) return uniqueStrings(levelSubjects)
          return uniqueStrings(subjectOptions)
        })()
        allowed.forEach((sn) => allowedAcrossClasses.add(sn))
      })

      return new Set(subjectNames.filter((sn) => !allowedAcrossClasses.has(sn)))
    })()
    const classKeys = classes.map((c: any) =>
      getLessonClassKey(c.name || '', c.branch, c.curriculum)
    )

    // Helper to check if cell has any custom data (for edit modal/delete)
    const hasCellData = (className: string, subjectName: string): boolean => {
      const cellData = lessonPlanningData[className]?.[subjectName]
      return !!(cellData && (cellData.teacher || cellData.frequency || cellData.duration))
    }

    // Handle cell click to open edit modal for frequency/duration
    const handleCellClick = (className: string, subjectName: string) => {
      const cellData = lessonPlanningData[className]?.[subjectName] || {
        teacher: '',
        frequency: '',
        duration: '',
      }
      setLessonEditModal({
        show: true,
        className,
        subjectName,
        frequency: cellData.frequency || defaultFrequencyFor(className, subjectName),
        duration: cellData.duration || defaultDurationStr,
        hasExistingData: hasCellData(className, subjectName),
      })
    }

    const showAllLevelsError =
      lessonPlanAttempted &&
      levels.some((level) => !isLevelLessonPlanningComplete(resolvedActiveBranch, level))

    const gridLocked = Boolean(allocationAi.hasActionPreview || allocationAi.isLoading)

    // While the wizard is (re)hydrating from the server — initial resume or the post-Excel-import
    // refresh — the grid data isn't in yet; show a table skeleton instead of stale/empty cells.
    if (isHydrating) {
      return (
        <div
          className='step4-manual-content step4-manual-content--lesson-planning'
          style={{maxWidth: '100%', width: '100%'}}
        >
          <div className='text-center px-4 mb-4'>
            <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.35rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.HEADING'})}
            </h5>
            <p className='mb-0' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.INSTRUCTION',
              })}
            </p>
          </div>
          <div className='p-4'>
            <CustomSkeleton variant={CARD_PRESET.TABLE} />
          </div>
        </div>
      )
    }

    return (
      <>
        <div
          className='step4-manual-content step4-manual-content--lesson-planning'
          style={{maxWidth: '100%', width: '100%'}}
        >
          <div className='text-center px-4 mb-4'>
            <h5 className='mb-2' style={{color: textColor, fontWeight: 600, fontSize: '1.35rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.HEADING'})}
            </h5>
            <p className='mb-0' style={{color: mutedColor, fontSize: '0.98rem', lineHeight: 1.6}}>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.INSTRUCTION',
              })}
            </p>
          </div>

          {/* Excel import: same pair of actions as the Teachers tab, but wired to the Excel
              Import module's APIs — real template download + upload -> map -> review modal. */}
          <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 mb-3 px-3'>
            <input
              ref={excelImportInputRef}
              type='file'
              accept={
                allocationConversionAvailable
                  ? `${dataUploadAcceptAttribute(allocationUploadConfig.formats)},.xls`
                  : '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              }
              className='d-none'
              onChange={handleExcelImportFileChange}
            />
            <button
              type='button'
              className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2'
              onClick={handleDownloadExcelTemplate}
              disabled={downloadingExcelTemplate}
            >
              {downloadingExcelTemplate ? (
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <Download size={16} aria-hidden />
              )}
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DOWNLOAD_TEMPLATE',
              })}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary d-inline-flex align-items-center gap-2'
              onClick={() => excelImportInputRef.current?.click()}
            >
              <Upload size={16} aria-hidden />
              {intl.formatMessage({
                id: allocationConversionAvailable
                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.UPLOAD_DATA_FILE'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.UPLOAD_EXCEL',
              })}
            </button>
          </div>
          {allocationConversionAvailable && (
            <div className='text-center px-4 mb-3' style={{color: mutedColor, fontSize: '0.85rem'}}>
              <div>
                {intl.formatMessage(
                  {id: 'DATA_UPLOAD.CONVERT.ACCEPTED_FORMATS'},
                  {size: formatDataUploadBytes(allocationUploadConfig.maxFileBytes)}
                )}
              </div>
              <div>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.PHOTO_TIPS'})}</div>
            </div>
          )}

          {allocationPrefillReading && (
            <div
              className='d-flex flex-column align-items-center justify-content-center gap-1 mb-3'
              role='status'
              aria-live='polite'
              style={{color: mutedColor, fontSize: '0.9rem'}}
            >
              <div className='d-inline-flex align-items-center gap-2'>
                <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_READING',
                })}
              </div>
              {renderPrefillProgress(allocationPrefillPercent)}
            </div>
          )}
          {allocationPrefillReport && (
            <div className='mb-3 px-3'>
              <DataUploadReport
                conversion={allocationPrefillReport}
                isDark={isDark}
                onDismiss={() => setAllocationPrefillReport(null)}
              />
            </div>
          )}

          {(() => {
            // How much of the grid has a teacher: moves as slots are filled by hand, by AI,
            // by an Excel import or by the Teachers file's prefill.
            const {total, filled} = getLessonPlanningFill()
            if (total === 0) return null
            const percent = Math.round((filled / total) * 100)
            return (
              <div
                className='mx-auto mb-3 px-3'
                style={{maxWidth: 480, width: '100%'}}
                role='status'
                aria-live='polite'
              >
                <div
                  className='d-flex justify-content-between align-items-center mb-1'
                  style={{fontSize: '0.88rem'}}
                >
                  <span style={{color: textColor, fontWeight: 600}}>
                    {intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.FILL_PROGRESS'},
                      {filled, total}
                    )}
                  </span>
                  <span style={{color: mutedColor}}>{percent}%</span>
                </div>
                <div
                  className='progress'
                  style={{height: 6}}
                  role='progressbar'
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                >
                  <div
                    className={`progress-bar${percent === 100 ? ' bg-success' : ''}`}
                    style={{width: `${percent}%`}}
                  />
                </div>
              </div>
            )
          })()}

          <div className='step4-lesson-planning-layout d-flex flex-column gap-3'>
            <TeacherAllocationAiPanel
              isDark={isDark}
              isLoading={allocationAi.isLoading}
              isApplying={allocationAi.isApplying}
              // Always in front of the user on arrival at lesson planning, not tucked away
              // behind the floating button.
              defaultOpen
              onOpenStateChange={(open) => {
                if (open) {
                  aiUsedSinceOpenRef.current = false
                } else if (
                  !aiUsedSinceOpenRef.current &&
                  // A tenant that already applied an AI allocation this mount is
                  // an AI user — a later open-and-close must not brand them
                  // "opened but not used". (Analytics also derives usage-wins
                  // server-side for the cross-session order.)
                  !trackedAllocationEventsRef.current.has('ai_assignment')
                ) {
                  trackAllocationEvent('ai_opened_not_used')
                }
              }}
              disabled={(localData.teachers as any[])?.length === 0}
              scope={allocationAi.scope}
              onScopeChange={allocationAi.setScope}
              hasMultipleBranches={hasMultipleBranches}
              onAsk={allocationAi.askAi}
              hasPreview={Boolean(allocationAi.preview)}
              conversationHistory={allocationAi.conversationHistory}
              preview={allocationAi.preview}
              lastContext={allocationAi.lastContext}
              onApply={allocationAi.applyPreview}
              onCancel={allocationAi.cancelPreview}
              onResolveDisambiguation={allocationAi.resolveDisambiguation}
              onClearAll={allocationAi.clearAllGridChanges}
              onRevert={allocationAi.revertPreviousGridChange}
              onStartNewChat={allocationAi.startNewChat}
              canClearAll={allocationAi.canClearAll}
              canRevert={allocationAi.canRevert}
              canStartNewChat={allocationAi.canStartNewChat}
              closeSignal={aiWidgetCloseSignal}
              formatText={displayFreeText}
            />

            <div className='step4-lesson-planning-main flex-grow-1 min-w-0'>
              {branches.length > 0 && (
                <div className='step4-lesson-branch-tabs-wrap mb-3'>
                  <div
                    className='mb-2'
                    style={{
                      color: mutedColor,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Branch
                  </div>
                  <div className='step4-lesson-branch-tabs'>
                    {branches.map((branch) => {
                      const isActiveBranch = branch === resolvedActiveBranch
                      const isBranchComplete = isBranchLessonPlanningComplete(branch)
                      const showBranchError = lessonPlanAttempted && !isBranchComplete
                      return (
                        <button
                          key={branch}
                          type='button'
                          className={`step4-lesson-branch-tab${isActiveBranch ? ' active' : ''}${showBranchError ? ' incomplete' : ''}`}
                          onClick={() => {
                            setIsLessonPlanningLoading(true)
                            setActiveLessonBranch(branch)
                            setActiveLessonLevel('')
                            setActiveLessonCurriculum('')
                            setSelectedRows([])
                            setSelectedCols([])
                          }}
                          title={
                            showBranchError
                              ? 'Incomplete - Please complete lesson planning for this branch'
                              : ''
                          }
                        >
                          <span className='d-inline-flex align-items-center gap-1'>
                            {showBranchError && <AlertTriangle size={12} strokeWidth={2.2} />}
                            <span>{branch}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {levels.length > 1 && (
                <div className='step4-lesson-branch-tabs-wrap mb-3'>
                  <div
                    className='mb-2'
                    style={{
                      color: mutedColor,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Level
                  </div>
                  <div className='step4-lesson-branch-tabs'>
                    <button
                      type='button'
                      className={`step4-lesson-branch-tab${resolvedActiveLevel ? '' : ' active'}${showAllLevelsError ? ' incomplete' : ''}`}
                      onClick={() => {
                        setIsLessonPlanningLoading(true)
                        setActiveLessonLevel('')
                        setActiveLessonCurriculum('')
                        setSelectedRows([])
                        setSelectedCols([])
                      }}
                      title={
                        showAllLevelsError
                          ? 'Incomplete - Please complete lesson planning for one or more levels'
                          : ''
                      }
                    >
                      <span className='d-inline-flex align-items-center gap-1'>
                        {showAllLevelsError && <AlertTriangle size={12} strokeWidth={2.2} />}
                        <span>All</span>
                      </span>
                    </button>
                    {levels.map((level) => {
                      const isActiveLevel = level === resolvedActiveLevel
                      const isLevelComplete = isLevelLessonPlanningComplete(
                        resolvedActiveBranch,
                        level
                      )
                      const showLevelError = lessonPlanAttempted && !isLevelComplete
                      return (
                        <button
                          key={level}
                          type='button'
                          className={`step4-lesson-branch-tab${isActiveLevel ? ' active' : ''}${showLevelError ? ' incomplete' : ''}`}
                          onClick={() => {
                            setIsLessonPlanningLoading(true)
                            setActiveLessonLevel(level)
                            setActiveLessonCurriculum('')
                            setSelectedRows([])
                            setSelectedCols([])
                          }}
                          title={
                            showLevelError
                              ? 'Incomplete - Please complete lesson planning for this level'
                              : ''
                          }
                        >
                          <span className='d-inline-flex align-items-center gap-1'>
                            {showLevelError && <AlertTriangle size={12} strokeWidth={2.2} />}
                            <span>{displayName(level, curriculumOfLevel(level))}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {resolvedActiveLevel && curriculaForResolvedLevel.length > 1 && (
                <div className='step4-lesson-branch-tabs-wrap mb-3'>
                  <div
                    className='mb-2'
                    style={{
                      color: mutedColor,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Curriculum
                  </div>
                  <div style={{maxWidth: 420}}>
                    <select
                      className='form-select form-select-solid'
                      value={resolvedActiveCurriculum}
                      onChange={(e) => {
                        setIsLessonPlanningLoading(true)
                        setActiveLessonCurriculum(e.target.value)
                        setSelectedRows([])
                        setSelectedCols([])
                      }}
                      style={{
                        backgroundColor: isDark ? '#262638' : undefined,
                        color: isDark ? '#ffffff' : undefined,
                        borderColor: isDark ? '#323248' : undefined,
                      }}
                    >
                      {curriculaForResolvedLevel.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div
                className='step4-lesson-planning-table-wrapper mb-4'
                style={{
                  border: `1px solid ${tableBorderColor}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: bodyBg,
                  boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 5px 15px rgba(0,0,0,0.05)',
                  margin: '0 auto',
                  width: '100%',
                  position: 'relative',
                }}
              >
                {/* Check if any subjects are selected for current level */}
                {resolvedActiveLevel &&
                !hasSubjectsForLevelInBranch(resolvedActiveBranch, resolvedActiveLevel) ? (
                  <div
                    style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background: bodyBg,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    <AlertTriangle size={40} color='#FFA500' strokeWidth={1.5} />
                    <div>
                      <h6 style={{color: textColor, fontWeight: 600, marginBottom: '0.5rem'}}>
                        {intl.formatMessage(
                          {
                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_FOR_LEVEL',
                          },
                          {
                            level: displayName(
                              resolvedActiveLevel,
                              resolvedActiveCurriculum || curriculumOfLevel(resolvedActiveLevel)
                            ),
                          }
                        )}
                      </h6>
                      <p
                        style={{
                          color: mutedColor,
                          fontSize: '0.9rem',
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_INSTRUCTION',
                        })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Loading Spinner Overlay — only for the grid's own data load;
                        while the AI is thinking the chat widget shows its own
                        typing indicator, so the grid stays fully visible. */}
                    {isLessonPlanningLoading && (
                      <div
                        className='step4-lesson-planning-loading-overlay'
                        role='status'
                        aria-live='polite'
                        aria-label={intl.formatMessage({id: 'COMMON.LOADING'})}
                      >
                        <img
                          src={toAbsoluteUrl('media/logos/LogoAnim1.gif')}
                          alt={intl.formatMessage({id: 'ONBOARDING_WIZARD.LOADING'})}
                          className='onboarding-loading-logo'
                        />
                      </div>
                    )}
                    <div className='step4-lesson-planning-table-scroll'>
                      <table
                        className='table align-middle mb-0 step4-lesson-planning-table'
                        style={{
                          width: '100%',
                          tableLayout: 'fixed',
                          // Grow with the number of subject columns (class col 150 + 200 per subject)
                          // so wide grids overflow and scroll horizontally instead of squishing.
                          minWidth: `${150 + subjectNames.length * 200}px`,
                          borderCollapse: 'separate',
                          borderSpacing: 0,
                        }}
                      >
                        <thead style={{position: 'sticky', top: 0, zIndex: 10}}>
                          <tr>
                            <th
                              style={{
                                backgroundColor: headerBg,
                                color: '#ffffff',
                                fontWeight: 600,
                                fontSize: '14px',
                                padding: '12px',
                                textAlign: 'center',
                                position: 'sticky',
                                left: 0,
                                top: 0,
                                zIndex: 30, // Higher than row headers and column headers
                                borderRight: `1px solid #A7A7A7`,
                                borderBottom: `1px solid #A7A7A7`,
                                minWidth: '150px',
                                width: '150px',
                                boxShadow: '1px 1px 0 #A7A7A7',
                                cursor: 'pointer',
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleAllRows(classes)
                              }}
                            >
                              {intl.formatMessage({
                                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.CLASS_NAME',
                              })}
                            </th>
                            {subjectNames.map((subjectName: string, idx: number) => {
                              const colHasData = classes.some((ci: any) => {
                                const classKey = getLessonClassKey(
                                  ci.name || '',
                                  ci.branch,
                                  ci.curriculum
                                )
                                const classSubjects = getValidatedSubjectsForClass(ci)
                                return (
                                  classSubjects.includes(subjectName) &&
                                  !isSlotDeleted(classKey, subjectName)
                                )
                              })
                              return (
                                <th
                                  key={idx}
                                  style={{
                                    backgroundColor: headerBlue,
                                    color: '#ffffff',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    padding: '12px',
                                    textAlign: 'center',
                                    borderRight: `1px solid #A7A7A7`,
                                    borderBottom: `1px solid #A7A7A7`,
                                    minWidth: '200px',
                                    width: '200px',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    top: 0,
                                    zIndex: 10,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (
                                      (e.target as HTMLElement).closest(
                                        '.step4-lp-header-delete-btn'
                                      )
                                    )
                                      return
                                    toggleCol(subjectName)
                                  }}
                                >
                                  <span>{subjectName}</span>
                                  <button
                                    type='button'
                                    className='step4-lp-header-delete-btn'
                                    disabled={!colHasData}
                                    title={
                                      colHasData
                                        ? intl.formatMessage({
                                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_COL',
                                          })
                                        : 'No data to delete in this column'
                                    }
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      if (!colHasData) return
                                      setDeleteRowColConfirm({type: 'col', name: subjectName})
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      padding: 2,
                                      border: 'none',
                                      background: 'none',
                                      borderRadius: 4,
                                      cursor: colHasData ? 'pointer' : 'not-allowed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#ffffff',
                                      opacity: colHasData ? 1 : 0.35,
                                    }}
                                  >
                                    <CircleX size={14} strokeWidth={2.5} />
                                  </button>
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {classes.map((classItem: any, rowIdx: number) => {
                            const classKey = getLessonClassKey(
                              classItem.name || '',
                              classItem.branch,
                              classItem.curriculum
                            )
                            const classSubjects = getValidatedSubjectsForClass(classItem)
                            const rowSelected = selectedRows.includes(classKey)
                            const rowHasData = subjectNames.some(
                              (sn) => classSubjects.includes(sn) && !isSlotDeleted(classKey, sn)
                            )
                            return (
                              <tr key={classKey || rowIdx}>
                                <td
                                  style={{
                                    backgroundColor: classNameColumnBg,
                                    color: textColor,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    padding: '12px',
                                    textAlign: 'center',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 5,
                                    borderRight: `1px solid #A7A7A7`,
                                    borderBottom: `1px solid #A7A7A7`,
                                    boxShadow: '1px 0 0 #A7A7A7',
                                    cursor: 'pointer',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (
                                      (e.target as HTMLElement).closest(
                                        '.step4-lp-header-delete-btn'
                                      )
                                    ) {
                                      return
                                    }
                                    toggleRow(classKey)
                                  }}
                                >
                                  <span>{displayName(classItem.name, classItem.curriculum)}</span>
                                  <button
                                    type='button'
                                    className='step4-lp-header-delete-btn'
                                    disabled={!rowHasData}
                                    title={
                                      rowHasData
                                        ? intl.formatMessage({
                                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_ROW',
                                          })
                                        : 'No data to delete in this row'
                                    }
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      if (!rowHasData) return
                                      setDeleteRowColConfirm({
                                        type: 'row',
                                        // Shown in the confirm text only; delete uses rowKey.
                                        name: displayName(classItem.name, classItem.curriculum),
                                        rowKey: classKey,
                                        rowSubjects: classSubjects,
                                      })
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      padding: 2,
                                      border: 'none',
                                      background: 'none',
                                      borderRadius: 4,
                                      cursor: rowHasData ? 'pointer' : 'not-allowed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: textColor,
                                      opacity: rowHasData ? 1 : 0.35,
                                    }}
                                  >
                                    <CircleX size={14} strokeWidth={2.5} />
                                  </button>
                                </td>

                                {subjectNames.map((subjectName: string, colIdx: number) => {
                                  const cn = classKey
                                  const sn = subjectName
                                  const isApplicable = classSubjects.includes(sn)
                                  const isDynamicOnly = dynamicOnlySubjects.has(sn)
                                  const isSelected = rowSelected || selectedCols.includes(sn)
                                  const slotDeleted = isSlotDeleted(cn, sn)
                                  const cellData = lessonPlanningData[cn]?.[sn]
                                  const teacherName = cellData?.teacher || ''
                                  const teacherEmployeeId =
                                    (cellData as any)?.teacherEmployeeId || ''
                                  const frequency = cellData?.frequency || defaultFrequencyStr
                                  const duration = cellData?.duration || defaultDurationStr
                                  const cellHasError =
                                    lessonPlanAttempted &&
                                    !slotDeleted &&
                                    !teacherName &&
                                    !errorClearedSlots.has(slotKey(cn, sn))
                                  const isPreviewCell = allocationAi.previewSlotKeys.has(
                                    slotKey(cn, sn)
                                  )
                                  const incomingTeacherName = isPreviewCell
                                    ? allocationAi.previewIncomingTeachers.get(slotKey(cn, sn))
                                    : undefined
                                  const isAppliedCell = allocationAi.appliedSlotKeys.has(
                                    slotKey(cn, sn)
                                  )
                                  const isDiscardedCell = allocationAi.discardedSlotKeys.has(
                                    slotKey(cn, sn)
                                  )

                                  return (
                                    <td
                                      key={colIdx}
                                      className={
                                        isPreviewCell
                                          ? `step4-ai-preview-cell${
                                              incomingTeacherName
                                                ? ' step4-ai-preview-cell--incoming'
                                                : ''
                                            }`
                                          : isDiscardedCell
                                            ? 'step4-ai-discarded-cell'
                                            : isAppliedCell
                                              ? 'step4-ai-applied-cell'
                                              : undefined
                                      }
                                      style={{
                                        backgroundColor: cellBgOffset,
                                        padding: 0,
                                        textAlign: 'center',
                                        borderRight: `1px solid #A7A7A7`,
                                        borderBottom: `1px solid #A7A7A7`,
                                        boxShadow: isSelected
                                          ? 'inset 0 0 0 1px rgba(5, 110, 233, 0.45)'
                                          : undefined,
                                        cursor: gridLocked ? 'default' : 'pointer',
                                        verticalAlign: 'middle',
                                        minHeight: '44px',
                                        height: '72px',
                                        position: 'relative',
                                      }}
                                      onClick={() => {
                                        if (gridLocked) return
                                        if (slotDeleted) {
                                          setErrorClearedSlots((prev) =>
                                            new Set(prev).add(slotKey(cn, sn))
                                          )
                                          setDeletedSlots((prev) => {
                                            const next = new Set(prev)
                                            next.delete(slotKey(cn, sn))
                                            return next
                                          })
                                          return
                                        }
                                        if (!isApplicable) {
                                          // If this subject only exists because it was added for some other class
                                          // via Lesson Planning, don't allow adding it everywhere by default.
                                          if (isDynamicOnly) return
                                          const existing = lessonPlanningData[cn]?.[sn]
                                          setLessonPlanningData((prev) => ({
                                            ...prev,
                                            [cn]: {
                                              ...prev[cn],
                                              [sn]: {
                                                teacher: existing?.teacher || '',
                                                frequency:
                                                  existing?.frequency ||
                                                  defaultFrequencyFor(cn, sn),
                                                duration: existing?.duration || defaultDurationStr,
                                              },
                                            },
                                          }))
                                          return
                                        }
                                        handleCellClick(cn, sn)
                                      }}
                                    >
                                      {!isApplicable ? (
                                        <div
                                          style={{
                                            height: '100%',
                                            width: '100%',
                                            backgroundColor: cellBgOffset,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: accentBlue,
                                            opacity: isDynamicOnly ? 0.0 : 1,
                                          }}
                                        >
                                          {!isDynamicOnly ? (
                                            <Plus size={18} strokeWidth={2} />
                                          ) : null}
                                        </div>
                                      ) : slotDeleted ? (
                                        <div
                                          className='step4-empty-slot'
                                          style={{
                                            height: '100%',
                                            width: '100%',
                                            backgroundColor: cellBgOffset,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: accentBlue,
                                          }}
                                        >
                                          <Plus size={24} strokeWidth={2} />
                                        </div>
                                      ) : (
                                        <div
                                          className='draggable-event'
                                          style={{
                                            backgroundColor: isDark ? '#CECECE' : '#DBEAFE',
                                            color: textColor,
                                            borderRadius: 0,
                                            padding: '4px 6px',
                                            textAlign: 'center',
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: '4px',
                                          }}
                                        >
                                          <button
                                            type='button'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              const slot = {
                                                className: cn,
                                                subjectName: sn,
                                              }
                                              if (
                                                localStorage.getItem(
                                                  LESSON_DELETE_SKIP_CONFIRM_KEY
                                                ) === 'true'
                                              ) {
                                                performDeleteSlot(slot)
                                              } else {
                                                deleteSlotRef.current = slot
                                                setDeleteSlotConfirm(slot)
                                              }
                                            }}
                                            className='step4-lp-slot-delete-btn'
                                            style={{
                                              position: 'absolute',
                                              top: 2,
                                              right: 2,
                                              padding: 2,
                                              border: 'none',
                                              background: 'none',
                                              borderRadius: 4,
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: accentBlue,
                                            }}
                                            aria-label='Delete'
                                          >
                                            <CircleX size={14} strokeWidth={2.5} />
                                          </button>

                                          <div
                                            onClick={(e) => e.stopPropagation()}
                                            className='step4-ai-lp-teacher-slot'
                                            style={{
                                              maxWidth: 'calc(100% - 28px)',
                                              minWidth: 0,
                                              marginLeft: 6,
                                              position: 'relative',
                                              pointerEvents: gridLocked ? 'none' : undefined,
                                              opacity: gridLocked ? 0.75 : 1,
                                            }}
                                          >
                                            <TeacherSearchSelect
                                              isDark={isDark}
                                              teachers={teachers
                                                .map((t: any) => ({
                                                  name: getTeacherFullName(t),
                                                  employeeId:
                                                    (t.employeeId || '').toString().trim() ||
                                                    undefined,
                                                }))
                                                .filter((t) => t.name)}
                                              value={teacherName}
                                              selectedEmployeeId={
                                                (cellData as any)?.teacherEmployeeId || ''
                                              }
                                              hasError={cellHasError}
                                              onSelectOption={(opt) => {
                                                trackAllocationEvent('manual_assignment')
                                                const existing = lessonPlanningData[cn]?.[sn]
                                                setLessonPlanningData((prev) => ({
                                                  ...prev,
                                                  [cn]: {
                                                    ...prev[cn],
                                                    [sn]: {
                                                      teacher: opt.name,
                                                      teacherEmployeeId: opt.employeeId || '',
                                                      frequency:
                                                        existing?.frequency ||
                                                        defaultFrequencyFor(cn, sn),
                                                      duration:
                                                        existing?.duration || defaultDurationStr,
                                                    },
                                                  },
                                                }))
                                              }}
                                              onChange={(val) => {
                                                if (val.trim()) {
                                                  trackAllocationEvent('manual_assignment')
                                                }
                                                const existing = lessonPlanningData[cn]?.[sn]
                                                setLessonPlanningData((prev) => ({
                                                  ...prev,
                                                  [cn]: {
                                                    ...prev[cn],
                                                    [sn]: {
                                                      teacher: val,
                                                      frequency:
                                                        existing?.frequency ||
                                                        defaultFrequencyFor(cn, sn),
                                                      duration:
                                                        existing?.duration || defaultDurationStr,
                                                    },
                                                  },
                                                }))
                                              }}
                                            />
                                            {incomingTeacherName && (
                                              <div className='step4-ai-replace-incoming'>
                                                <span title={incomingTeacherName}>
                                                  {incomingTeacherName}
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          <div
                                            style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: '1px',
                                              fontSize: '10px',
                                              fontWeight: 600,
                                              opacity: 0.9,
                                              color: accentBlue,
                                            }}
                                          >
                                            <div>
                                              {intl.formatMessage(
                                                {
                                                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.FREQUENCY_LABEL',
                                                },
                                                {value: frequency}
                                              )}
                                            </div>
                                            <div>
                                              {intl.formatMessage(
                                                {
                                                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DURATION_LABEL',
                                                },
                                                {value: duration}
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  )
                                })}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div
                className='mb-4'
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <button
                  type='button'
                  className='d-flex align-items-center justify-content-center p-2 rounded border-0'
                  style={{
                    backgroundColor: isDark ? 'rgba(43, 43, 64, 0.4)' : '#F5F8FA',
                    border: `1px solid ${tableBorderColor}`,
                    color: accentBlue,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                  }}
                  onClick={() => setInfoTooltipOpen((prev) => !prev)}
                  title={infoTooltipOpen ? 'Click to close' : 'Frequency & Duration'}
                >
                  <Info size={20} />
                </button>
                {infoTooltipOpen && (
                  <div
                    className='mt-2 p-4 rounded'
                    style={{
                      backgroundColor: isDark ? 'rgba(43, 43, 64, 0.4)' : '#F5F8FA',
                      border: `1px solid ${tableBorderColor}`,
                      borderRadius: 12,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    <div className='d-flex align-items-center gap-2'>
                      <span style={{color: accentBlue}}>
                        <Plus size={16} />
                      </span>
                      <p className='mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
                        <span style={{fontWeight: 700, color: textColor}}>Frequency</span> = how
                        many times the subject is taught in a day or week
                      </p>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <span style={{color: accentBlue}}>
                        <Plus size={16} />
                      </span>
                      <p className='mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
                        <span style={{fontWeight: 700, color: textColor}}>Duration</span> = number
                        of consecutive lectures each day
                      </p>
                    </div>
                    <div className='d-flex align-items-center gap-2 mt-1'>
                      <p
                        className='mb-0 ms-4'
                        style={{
                          color: mutedColor,
                          fontSize: '0.85rem',
                          fontStyle: 'italic',
                          opacity: 0.8,
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.EXAMPLE_TEXT',
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='d-flex justify-content-end gap-3 w-100 mt-4'>
            {showBackButton && (
              <button
                type='button'
                className='onboarding-btn-back me-auto'
                disabled={isGenerating || isSubmitting}
                onClick={handleBackTab}
              >
                <ArrowLeft size={16} className='me-2' />
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
              </button>
            )}
            {(() => {
              const lessonValid = isTabValid('lessonPlanning')
              const nextDisabled = isGenerating || isSubmitting || !lessonValid
              const tooltip =
                !isGenerating && !isSubmitting && !lessonValid
                  ? 'To enable, assign a teacher to every subject.'
                  : ''

              return (
                <span
                  style={{
                    display: 'inline-block',
                    cursor: nextDisabled ? 'not-allowed' : 'default',
                    position: 'relative',
                  }}
                  onMouseEnter={() => {
                    if (tooltip) setShowLessonNextHint(true)
                  }}
                  onMouseLeave={() => setShowLessonNextHint(false)}
                >
                  {showLessonNextHint && tooltip && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        bottom: 'calc(100% + 10px)',
                        background: 'rgba(0,0,0,0.85)',
                        color: '#fff',
                        padding: '8px 10px',
                        borderRadius: 8,
                        fontSize: 12,
                        maxWidth: 260,
                        lineHeight: 1.35,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                      }}
                      role='tooltip'
                    >
                      {tooltip}
                    </div>
                  )}
                  <button
                    type='button'
                    className='onboarding-btn-next'
                    disabled={nextDisabled}
                    style={nextDisabled ? {pointerEvents: 'none'} : undefined}
                    onClick={() => {
                      setLessonPlanAttempted(true)
                      const valid = isTabValid('lessonPlanning')
                      if (!valid) {
                        // Check if the issue is missing subjects for a level
                        const levelWithoutSubjects = getFirstLevelWithoutSubjects()
                        if (levelWithoutSubjects) {
                          toast.error(
                            intl.formatMessage(
                              {
                                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.NO_SUBJECTS_VALIDATION_ERROR',
                              },
                              {
                                level: displayName(
                                  levelWithoutSubjects,
                                  curriculumOfLevel(levelWithoutSubjects)
                                ),
                              }
                            )
                          )
                        } else {
                          toast.error(
                            intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.VALIDATION_ERROR',
                              defaultMessage:
                                'Please assign a teacher to every subject before proceeding.',
                            })
                          )
                        }
                        return
                      }
                      // Collapse the AI allocation chatbot before advancing.
                      setAiWidgetCloseSignal((n) => n + 1)
                      if (isLastTab) handleFinishStep()
                      else handleNextTab()
                    }}
                  >
                    {isGenerating || isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm align-middle me-2' />
                        {intl.formatMessage({id: 'COMMON.LOADING'})}
                      </>
                    ) : (
                      <>
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.BTN_NEXT'})}
                        <ArrowRight size={16} className='ms-2' />
                      </>
                    )}
                  </button>
                </span>
              )
            })()}
          </div>
        </div>

        {/* Lesson Edit Sheet (right-side panel) */}
        <LessonPlanEditSheet
          show={lessonEditModal.show}
          isDark={isDark}
          onHide={() =>
            setLessonEditModal({
              show: false,
              className: '',
              subjectName: '',
              frequency: '',
              duration: '',
              hasExistingData: false,
            })
          }
          className={lessonEditModal.className}
          classLabel={(() => {
            const parts = String(lessonEditModal.className ?? '').split('|||')
            return displayName(parts[parts.length - 1], parts.length >= 3 ? parts[1] : undefined)
          })()}
          subjectName={lessonEditModal.subjectName}
          frequency={lessonEditModal.frequency}
          duration={lessonEditModal.duration}
          onSave={({frequency: f, duration: d}) => {
            const {className: cn, subjectName: sn} = lessonEditModal
            const existingTeacher = lessonPlanningData[cn]?.[sn]?.teacher || ''
            setLessonPlanningData((prev) => ({
              ...prev,
              [cn]: {
                ...prev[cn],
                [sn]: {teacher: existingTeacher, frequency: f, duration: d},
              },
            }))
            setLessonEditModal({
              show: false,
              className: '',
              subjectName: '',
              frequency: '',
              duration: '',
              hasExistingData: false,
            })
          }}
        />

        {/* Delete slot confirmation modal */}
        <Modal
          show={!!deleteSlotConfirm}
          onHide={() => {
            setDeleteSlotConfirm(null)
            setDonAskAgainDelete(false)
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage({id: 'GENERAL.DELETE'})}</Modal.Title>
          </Modal.Header>
          <Modal.Body className='py-4'>
            {deleteSlotConfirm && (
              <p className='mb-0'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_DELETE_CONFIRM',
                })}{' '}
                ?
              </p>
            )}
          </Modal.Body>
          <Modal.Footer className='d-flex justify-content-between align-items-center'>
            <label className='form-check form-check-custom form-check-solid mb-0 me-auto'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={donAskAgainDelete}
                onChange={(e) => setDonAskAgainDelete(e.target.checked)}
              />
              <span className='form-check-label'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_DELETE_DONT_ASK_AGAIN',
                })}
              </span>
            </label>
            <div className='d-flex gap-2'>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => {
                  setDeleteSlotConfirm(null)
                  setDonAskAgainDelete(false)
                }}
              >
                {intl.formatMessage({id: 'GENERAL.CANCEL'})}
              </button>
              <button
                type='button'
                className='btn btn-danger'
                onClick={() => {
                  const slot = deleteSlotRef.current || deleteSlotConfirm
                  if (slot) {
                    if (donAskAgainDelete) {
                      localStorage.setItem(LESSON_DELETE_SKIP_CONFIRM_KEY, 'true')
                    }
                    performDeleteSlot(slot)
                    setDonAskAgainDelete(false)
                  }
                }}
              >
                {intl.formatMessage({id: 'GENERAL.DELETE'})}
              </button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Delete row/column confirmation modal */}
        <Modal show={!!deleteRowColConfirm} onHide={() => setDeleteRowColConfirm(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage({id: 'GENERAL.DELETE'})}</Modal.Title>
          </Modal.Header>
          <Modal.Body className='py-4'>
            {deleteRowColConfirm && (
              <p className='mb-0'>
                {deleteRowColConfirm.type === 'row'
                  ? intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_ROW_CONFIRM'},
                      {name: deleteRowColConfirm.name}
                    )
                  : intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.DELETE_COL_CONFIRM'},
                      {name: deleteRowColConfirm.name}
                    )}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => setDeleteRowColConfirm(null)}
            >
              {intl.formatMessage({id: 'GENERAL.CANCEL'})}
            </button>
            <button
              type='button'
              className='btn btn-danger'
              onClick={() => {
                if (deleteRowColConfirm) {
                  if (deleteRowColConfirm.type === 'row') {
                    const targetRowKey = deleteRowColConfirm.rowKey || deleteRowColConfirm.name
                    const targetSubjects = deleteRowColConfirm.rowSubjects || subjectNames
                    deleteRow(targetRowKey, targetSubjects)
                  } else {
                    deleteColumn(deleteRowColConfirm.name, classKeys)
                  }
                  setDeleteRowColConfirm(null)
                }
              }}
            >
              {intl.formatMessage({id: 'GENERAL.DELETE'})}
            </button>
          </Modal.Footer>
        </Modal>
      </>
    )
  }

  const stepperSteps = useMemo(() => {
    const out: Array<{
      id: string
      topTabId: string
      classesFlowStep?: number
      label: string
    }> = []

    // Keep stepper order aligned with `tabs` order, but expand the `classes` tab
    // into its internal flow (Classes -> Sections -> Review).
    for (const tab of tabs) {
      if (tab.id === 'teachers') {
        // Review Teachers is still its own sub-step (URL/tracking), but has no stepper label —
        // the Teachers step stays highlighted while the review screen is open.
        out.push({id: 'teachers', topTabId: 'teachers', label: tab.label})
      } else if (tab.id === 'classes') {
        out.push({
          id: 'classes-classes',
          topTabId: 'classes',
          classesFlowStep: 2,
          label: tab.label || 'Classes',
        })
        out.push({
          id: 'classes-sections',
          topTabId: 'classes',
          classesFlowStep: 1,
          label: 'Sections',
        })
        out.push({
          id: 'classes-review',
          topTabId: 'classes',
          classesFlowStep: 3,
          label: 'Review',
        })
      } else if (tab.id === 'subjects') {
        out.push({id: 'subjects', topTabId: 'subjects', label: tab.label})
      } else if (tab.id === 'lessonPlanning') {
        out.push({
          id: 'lessonPlanning',
          topTabId: 'lessonPlanning',
          label: tab.label,
        })
      } else if (tab.id === 'students') {
        out.push({id: 'students', topTabId: 'students', label: tab.label})
      }
    }

    return out
  }, [tabs, intl])

  const activeStepId = useMemo(() => {
    if (activeTab === 'classes') {
      if (classesFlowStep === 1) return 'classes-sections'
      if (classesFlowStep === 2) return 'classes-classes'
      return 'classes-review'
    }
    return activeTab
  }, [activeTab, classesFlowStep])

  const effectiveStepperSteps = stepperOverride?.steps ?? stepperSteps
  const effectiveActiveStepId = useMemo(() => {
    if (!stepperOverride) return activeStepId

    const ids = new Set(stepperOverride.steps.map((s) => s.id))
    // When Step4Manual is used inside Step 3 (Curriculum flow), the stepper should
    // reflect the live internal state (activeTab/classesFlowStep) rather than relying
    // on persisted wizard state that may lag behind.
    if (activeTab === 'subjects' && ids.has('subjects')) return 'subjects'
    if (activeTab === 'classes') {
      if (classesFlowStep === 1 && ids.has('sections')) return 'sections'
      if (classesFlowStep === 3 && ids.has('review')) return 'review'
      if (ids.has('classes')) return 'classes'
    }
    return stepperOverride.activeStepId ?? activeStepId
  }, [activeStepId, activeTab, classesFlowStep, stepperOverride])
  const activeIdx = effectiveStepperSteps.findIndex((s) => s.id === effectiveActiveStepId)

  return (
    <div
      className='d-flex flex-column align-items-center h-100 step4-manual'
      style={{paddingTop: '0.5rem'}}
    >
      <div
        className={`step4-stepper-wrapper mb-3${stepperOverride ? ' step4-stepper-wrapper--curriculum' : ''}`}
      >
        <HorizontalStepper
          isDark={isDark}
          labels={effectiveStepperSteps.map((s) => s.label)}
          activeIndex={Math.max(0, activeIdx)}
          maxReachedIndex={Math.max(0, activeIdx)}
          completing={Boolean(isGenerating)}
          isStepClickable={(idx) => {
            if (idx > activeIdx) return false
            const step = effectiveStepperSteps[idx]
            if (!step) return false
            if (stepperOverride?.onStepClick) return true
            return Boolean(step.topTabId)
          }}
          onStepClick={(idx) => {
            if (idx > activeIdx) return
            const step = effectiveStepperSteps[idx]
            if (!step) return
            if (stepperOverride?.onStepClick) {
              stepperOverride.onStepClick(step.id)
              // Don't return here: in Step3Curriculum the override only handles
              // "curriculum" and "levels". For "classes/sections/review/subjects",
              // Step4Manual must still perform internal navigation.
            }
            if (!step.topTabId) return
            if (step.topTabId === 'classes' && typeof step.classesFlowStep === 'number') {
              setActiveTab('classes')
              goToClassesFlowStep(step.classesFlowStep)
              return
            }
            if (step.topTabId === 'teachers') {
              goToTeachersPhase('entry')
            }
            setActiveTab(step.topTabId)
            saveProgress(step.topTabId)
          }}
        />
      </div>

      <div
        className={`flex-grow-1 w-100 d-flex flex-column align-items-center step4-content-area ${activeTab === 'teachers' ? 'step4-tab-teachers' : 'step4-tab-others'}`}
      >
        {activeTab === 'teachers' && renderTeachersUI()}
        {activeTab === 'classes' && renderClassesUI()}
        {activeTab === 'subjects' && renderSubjectsUI()}
        {activeTab === 'lessonPlanning' && renderLessonPlanningPlaceholder()}
        {activeTab === 'students' && renderStudentsUI()}
      </div>

      <Modal
        show={!!pendingDeleteAction}
        onHide={() => {
          setPendingDeleteAction(null)
          setDonAskAgainDeleteRow(false)
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'GENERAL.DELETE'})}</Modal.Title>
        </Modal.Header>
        {/* One-line confirmation: the theme's default modal-body padding leaves a tall empty
            band above and below it, so tighten the vertical padding. */}
        <Modal.Body className='py-4'>
          {pendingDeleteAction?.type === 'students-prefill'
            ? intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.PREFILL_REMOVE_CONFIRM'},
                {
                  count: studentsPrefillRows().length,
                  saved: studentsPrefillRows().filter(isSavedStudent).length,
                }
              )
            : pendingDeleteAction?.type === 'category-row' &&
                pendingDeleteAction.category === 'students' &&
                isSavedStudent(((localData.students as any[]) || [])[pendingDeleteAction.index])
              ? intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.DELETE_SAVED_CONFIRM',
                })
              : pendingDeleteAction?.type === 'teacher-rows' &&
                  pendingDeleteAction.indices.length > 1
                ? intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW.BULK_DELETE_CONFIRM'},
                    {count: pendingDeleteAction.indices.length}
                  )
                : intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.DELETE_ROW_CONFIRM'})}
        </Modal.Body>
        <Modal.Footer className='d-flex justify-content-between align-items-center'>
          <label
            className='form-check form-check-custom form-check-solid mb-0 me-auto'
            // Deleting from the school is always confirmed: no "don't ask again" there.
            style={alwaysConfirmDelete(pendingDeleteAction) ? {visibility: 'hidden'} : undefined}
          >
            <input
              className='form-check-input'
              type='checkbox'
              checked={donAskAgainDeleteRow}
              onChange={(e) => setDonAskAgainDeleteRow(e.target.checked)}
            />
            <span className='form-check-label'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_DELETE_DONT_ASK_AGAIN',
              })}
            </span>
          </label>
          <div className='d-flex gap-2'>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => {
                setPendingDeleteAction(null)
                setDonAskAgainDeleteRow(false)
              }}
            >
              {intl.formatMessage({id: 'GENERAL.CANCEL'})}
            </button>
            <button type='button' className='btn btn-danger' onClick={handleConfirmDeleteAction}>
              {intl.formatMessage({id: 'GENERAL.DELETE'})}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      <OnboardingExcelImportModal
        show={showExcelImportModal}
        file={excelImportFile}
        isDark={isDark}
        notice={
          excelImportFromPrefill
            ? intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.PREFILL_FOUND',
              })
            : undefined
        }
        onClose={() => {
          setShowExcelImportModal(false)
          setExcelImportFile(null)
          setExcelImportFromPrefill(false)
        }}
        onImported={handleExcelImported}
      />
    </div>
  )
}

export default Step4Manual
